import React, { useEffect, useState } from "react";
import {
  Check,
  RotateCcw,
  Award,
  Heart,
  Pill,
  Activity,
  Stethoscope,
} from "lucide-react";
import { applyBoost, GAME_BOOST } from "../lib/tamagotchiUtils";

const MedicalMatch = ({ onBack, userData, saveUserData }) => {
  const storageKey = "medicalMatchProgress";
  const [gamePhase, setGamePhase] = useState("theme"); // theme, level, game, playing, complete
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameOrder, setGameOrder] = useState([]);
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [moves, setMoves] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [completedGames, setCompletedGames] = useState({});
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);

  // Theme configurations
  const themes = [
    {
      id: "anatomy",
      name: "Anatomy Match",
      description: "Match organs with their primary functions",
      icon: Heart,
      color: "from-red-100 to-pink-100",
      borderColor: "border-red-300",
      iconColor: "text-red-600",
      coins: 15,
      xp: 30,
    },
    {
      id: "pharmacology",
      name: "Drug Match",
      description: "Match medications with their drug classes",
      icon: Pill,
      color: "from-blue-100 to-cyan-100",
      borderColor: "border-blue-300",
      iconColor: "text-blue-600",
      coins: 20,
      xp: 40,
    },
    {
      id: "pathology",
      name: "Disease Match",
      description: "Match diseases with their key symptoms",
      icon: Activity,
      color: "from-green-100 to-emerald-100",
      borderColor: "border-green-300",
      iconColor: "text-green-600",
      coins: 20,
      xp: 40,
    },
    {
      id: "physiology",
      name: "Physiology Match",
      description: "Match body systems with their key hormones",
      icon: Stethoscope,
      color: "from-purple-100 to-violet-100",
      borderColor: "border-purple-300",
      iconColor: "text-purple-600",
      coins: 25,
      xp: 50,
    },
  ];

  // Level configurations
  const levels = [
    {
      id: 1,
      name: "Level 1",
      pairs: 6,
      coins: 10,
      xp: 20,
      color: "from-green-100 to-emerald-100",
      borderColor: "border-green-300",
    },
    {
      id: 2,
      name: "Level 2",
      pairs: 8,
      coins: 15,
      xp: 30,
      color: "from-blue-100 to-cyan-100",
      borderColor: "border-blue-300",
    },
    {
      id: 3,
      name: "Level 3",
      pairs: 10,
      coins: 20,
      xp: 40,
      color: "from-purple-100 to-violet-100",
      borderColor: "border-purple-300",
    },
    {
      id: 4,
      name: "Level 4",
      pairs: 12,
      coins: 25,
      xp: 50,
      color: "from-orange-100 to-amber-100",
      borderColor: "border-orange-300",
    },
    {
      id: 5,
      name: "Level 5",
      pairs: 14,
      coins: 30,
      xp: 60,
      color: "from-red-100 to-pink-100",
      borderColor: "border-red-300",
    },
  ];

  // Match pairs for each theme - 6 games per level
  // Structure: matchData[theme][level][game] = array of pairs
  const matchData = {
    anatomy: {
      1: {
        // Level 1 - 6 pairs each
        1: [
          { id: 1, term: "Heart", match: "Pumps blood" },
          { id: 2, term: "Lungs", match: "Gas exchange" },
          { id: 3, term: "Liver", match: "Detoxification" },
          { id: 4, term: "Kidneys", match: "Filter waste" },
          { id: 5, term: "Brain", match: "Neural control" },
          { id: 6, term: "Stomach", match: "Digest food" },
        ],
        2: [
          { id: 1, term: "Pancreas", match: "Produces insulin" },
          { id: 2, term: "Spleen", match: "Filters blood" },
          { id: 3, term: "Gallbladder", match: "Stores bile" },
          { id: 4, term: "Esophagus", match: "Food transport" },
          { id: 5, term: "Trachea", match: "Air passage" },
          { id: 6, term: "Larynx", match: "Voice box" },
        ],
        3: [
          { id: 1, term: "Thymus", match: "T-cell maturation" },
          { id: 2, term: "Adrenal gland", match: "Stress hormones" },
          { id: 3, term: "Thyroid", match: "Metabolism control" },
          { id: 4, term: "Pituitary", match: "Master gland" },
          { id: 5, term: "Hypothalamus", match: "Homeostasis" },
          { id: 6, term: "Pineal gland", match: "Sleep regulation" },
        ],
        4: [
          { id: 1, term: "Femur", match: "Thigh bone" },
          { id: 2, term: "Humerus", match: "Upper arm bone" },
          { id: 3, term: "Tibia", match: "Shin bone" },
          { id: 4, term: "Radius", match: "Forearm bone" },
          { id: 5, term: "Vertebrae", match: "Spine bones" },
          { id: 6, term: "Sternum", match: "Breastbone" },
        ],
        5: [
          { id: 1, term: "Biceps", match: "Flex elbow" },
          { id: 2, term: "Triceps", match: "Extend elbow" },
          { id: 3, term: "Quadriceps", match: "Extend knee" },
          { id: 4, term: "Hamstrings", match: "Flex knee" },
          { id: 5, term: "Deltoid", match: "Shoulder movement" },
          { id: 6, term: "Diaphragm", match: "Breathing muscle" },
        ],
        6: [
          { id: 1, term: "Skin", match: "Protection barrier" },
          { id: 2, term: "Blood", match: "Oxygen transport" },
          { id: 3, term: "Bones", match: "Support structure" },
          { id: 4, term: "Muscles", match: "Movement" },
          { id: 5, term: "Bladder", match: "Stores urine" },
          { id: 6, term: "Intestines", match: "Absorb nutrients" },
        ],
        7: [
          { id: 1, term: "Fallopian tube", match: "Egg transport" },
          { id: 2, term: "Seminal vesicle", match: "Fructose production" },
          { id: 3, term: "Prostate gland", match: "PSA secretion" },
          { id: 4, term: "Ovaries", match: "Estrogen & progesterone" },
          { id: 5, term: "Testes", match: "Testosterone" },
          { id: 6, term: "Uterus", match: "Fetal development" },
        ],
        8: [
          { id: 1, term: "CN I", match: "Olfactory (smell)" },
          { id: 2, term: "CN II", match: "Optic (vision)" },
          { id: 3, term: "CN III", match: "Oculomotor (eye movement)" },
          { id: 4, term: "CN VII", match: "Facial (expression)" },
          { id: 5, term: "CN VIII", match: "Vestibulocochlear (hearing)" },
          { id: 6, term: "CN X", match: "Vagus (parasympathetic)" },
        ],
        9: [
          {
            id: 1,
            term: "MCA territory",
            match: "Lateral cortex — face and arm",
          },
          { id: 2, term: "ACA territory", match: "Medial cortex — leg" },
          { id: 3, term: "PCA territory", match: "Occipital lobe — vision" },
          {
            id: 4,
            term: "PICA territory",
            match: "Lateral medulla + inferior cerebellum",
          },
          {
            id: 5,
            term: "AICA territory",
            match: "Lateral inferior cerebellum",
          },
          { id: 6, term: "Basilar artery", match: "Pons" },
        ],
        10: [
          { id: 1, term: "Biceps brachii", match: "Musculocutaneous nerve" },
          { id: 2, term: "Triceps", match: "Radial nerve" },
          { id: 3, term: "Deltoid", match: "Axillary nerve" },
          { id: 4, term: "Diaphragm", match: "Phrenic nerve (C3-C5)" },
          { id: 5, term: "Trapezius", match: "Spinal accessory (CN XI)" },
          { id: 6, term: "Serratus anterior", match: "Long thoracic nerve" },
        ],
        11: [
          { id: 1, term: "Stomach blood supply", match: "Celiac axis" },
          {
            id: 2,
            term: "Small intestine",
            match: "Superior mesenteric artery",
          },
          { id: 3, term: "Left colon", match: "Inferior mesenteric artery" },
          { id: 4, term: "Spleen", match: "Splenic artery" },
          { id: 5, term: "Kidneys", match: "Direct renal arteries (aorta)" },
          { id: 6, term: "Gonads", match: "Gonadal arteries (L2 aorta)" },
        ],
        12: [
          {
            id: 1,
            term: "Shoulder (glenohumeral)",
            match: "Ball and socket joint",
          },
          { id: 2, term: "Knee (tibiofemoral)", match: "Modified hinge joint" },
          { id: 3, term: "Atlas-axis (C1-C2)", match: "Pivot joint" },
          {
            id: 4,
            term: "Pubic symphysis",
            match: "Secondary cartilaginous joint",
          },
          { id: 5, term: "Skull sutures", match: "Fibrous (synarthrosis)" },
          { id: 6, term: "Rib-sternum junction", match: "Synchondrosis" },
        ],
        13: [
          { id: 1, term: "Appendix", match: "Lymphoid tissue (MALT)" },
          { id: 2, term: "Cecum", match: "Start of large intestine" },
          { id: 3, term: "Epiglottis", match: "Prevents aspiration" },
          { id: 4, term: "Uvula", match: "Soft palate extension" },
          { id: 5, term: "Tonsils", match: "Oropharyngeal lymphoid tissue" },
          { id: 6, term: "Adenoids", match: "Nasopharyngeal lymphoid tissue" },
        ],
        14: [
          {
            id: 1,
            term: "Parathyroid glands",
            match: "Calcium regulation (PTH)",
          },
          { id: 2, term: "Zona glomerulosa", match: "Aldosterone secretion" },
          { id: 3, term: "Zona fasciculata", match: "Cortisol secretion" },
          { id: 4, term: "Zona reticularis", match: "Androgen secretion" },
          {
            id: 5,
            term: "Adrenal medulla",
            match: "Epinephrine & norepinephrine",
          },
          {
            id: 6,
            term: "Pineal gland",
            match: "Melatonin (sleep regulation)",
          },
        ],
        15: [
          { id: 1, term: "Epididymis", match: "Sperm maturation" },
          { id: 2, term: "Vas deferens", match: "Sperm transport" },
          { id: 3, term: "Seminal vesicle", match: "Fructose for sperm" },
          { id: 4, term: "Corpus cavernosum", match: "Penile erection" },
          {
            id: 5,
            term: "Endometrium",
            match: "Uterine lining (shed in menses)",
          },
          { id: 6, term: "Corpus luteum", match: "Progesterone production" },
        ],
        16: [
          { id: 1, term: "Masseter", match: "Jaw closure (CN V3)" },
          { id: 2, term: "Temporalis", match: "Jaw closure and retraction" },
          { id: 3, term: "Parotid gland", match: "Serous saliva (CN IX)" },
          {
            id: 4,
            term: "Submandibular gland",
            match: "Mixed saliva (largest output)",
          },
          {
            id: 5,
            term: "Sublingual gland",
            match: "Mucous saliva (smallest gland)",
          },
          {
            id: 6,
            term: "Thyroid cartilage",
            match: "Adam's apple; largest laryngeal cartilage",
          },
        ],
        17: [
          {
            id: 1,
            term: "Cornea",
            match: "Transparent outer eye; refracts light",
          },
          { id: 2, term: "Sclera", match: "White of the eye; protective coat" },
          { id: 3, term: "Iris", match: "Colored part; controls pupil size" },
          { id: 4, term: "Cochlea", match: "Converts sound to nerve signals" },
          {
            id: 5,
            term: "Tympanic membrane",
            match: "Vibrates with sound; transmits to ossicles",
          },
          {
            id: 6,
            term: "Semicircular canals",
            match: "Rotational balance detection",
          },
        ],
        18: [
          {
            id: 1,
            term: "ACL",
            match: "Prevents anterior tibial displacement",
          },
          {
            id: 2,
            term: "PCL",
            match: "Prevents posterior tibial displacement",
          },
          { id: 3, term: "MCL", match: "Medial knee stability" },
          {
            id: 4,
            term: "Achilles tendon",
            match: "Gastrocnemius/soleus to calcaneus",
          },
          {
            id: 5,
            term: "Rotator cuff",
            match: "Four muscles stabilize glenohumeral joint",
          },
          {
            id: 6,
            term: "Plantar fascia",
            match: "Supports longitudinal foot arch",
          },
        ],
      },
      2: {
        // Level 2 - 8 pairs each
        1: [
          { id: 1, term: "Heart", match: "Pumps blood" },
          { id: 2, term: "Lungs", match: "Gas exchange" },
          { id: 3, term: "Liver", match: "Detoxification" },
          { id: 4, term: "Kidneys", match: "Filter waste" },
          { id: 5, term: "Brain", match: "Neural control" },
          { id: 6, term: "Stomach", match: "Digest food" },
          { id: 7, term: "Pancreas", match: "Insulin production" },
          { id: 8, term: "Intestines", match: "Absorb nutrients" },
        ],
        2: [
          { id: 1, term: "Aorta", match: "Largest artery" },
          { id: 2, term: "Vena cava", match: "Largest vein" },
          { id: 3, term: "Pulmonary artery", match: "To lungs" },
          { id: 4, term: "Coronary arteries", match: "Heart supply" },
          { id: 5, term: "Carotid artery", match: "Brain supply" },
          { id: 6, term: "Femoral artery", match: "Leg supply" },
          { id: 7, term: "Renal artery", match: "Kidney supply" },
          { id: 8, term: "Hepatic portal", match: "Liver supply" },
        ],
        3: [
          { id: 1, term: "Cerebrum", match: "Thinking brain" },
          { id: 2, term: "Cerebellum", match: "Balance center" },
          { id: 3, term: "Brainstem", match: "Vital functions" },
          { id: 4, term: "Hippocampus", match: "Memory formation" },
          { id: 5, term: "Amygdala", match: "Emotion center" },
          { id: 6, term: "Thalamus", match: "Sensory relay" },
          { id: 7, term: "Corpus callosum", match: "Brain bridge" },
          { id: 8, term: "Medulla", match: "Autonomic control" },
        ],
        4: [
          { id: 1, term: "Cornea", match: "Eye surface" },
          { id: 2, term: "Retina", match: "Light detection" },
          { id: 3, term: "Lens", match: "Focus light" },
          { id: 4, term: "Iris", match: "Eye color" },
          { id: 5, term: "Pupil", match: "Light entry" },
          { id: 6, term: "Cochlea", match: "Hearing organ" },
          { id: 7, term: "Eardrum", match: "Sound vibration" },
          { id: 8, term: "Olfactory bulb", match: "Smell processing" },
        ],
        5: [
          { id: 1, term: "Right atrium", match: "Receives venous blood" },
          { id: 2, term: "Left atrium", match: "Receives from lungs" },
          { id: 3, term: "Right ventricle", match: "Pumps to lungs" },
          { id: 4, term: "Left ventricle", match: "Pumps to body" },
          { id: 5, term: "Mitral valve", match: "Left AV valve" },
          { id: 6, term: "Tricuspid valve", match: "Right AV valve" },
          { id: 7, term: "Aortic valve", match: "Left outflow" },
          { id: 8, term: "Pulmonary valve", match: "Right outflow" },
        ],
        6: [
          { id: 1, term: "Esophagus", match: "Food transport" },
          { id: 2, term: "Stomach", match: "Acid digestion" },
          { id: 3, term: "Duodenum", match: "First small intestine" },
          { id: 4, term: "Jejunum", match: "Nutrient absorption" },
          { id: 5, term: "Ileum", match: "Bile salt absorption" },
          { id: 6, term: "Cecum", match: "Beginning of colon" },
          { id: 7, term: "Colon", match: "Water absorption" },
          { id: 8, term: "Rectum", match: "Stool storage" },
        ],
        7: [
          { id: 1, term: "Vagus nerve", match: "CN X — parasympathetic" },
          { id: 2, term: "Phrenic nerve", match: "C3, C4, C5" },
          { id: 3, term: "Recurrent laryngeal", match: "Phonation" },
          { id: 4, term: "Sciatic nerve", match: "Largest nerve in body" },
          { id: 5, term: "Brachial plexus", match: "C5 to T1" },
          { id: 6, term: "Thoracic duct", match: "Left lymphatic drainage" },
          { id: 7, term: "Cisterna chyli", match: "Abdominal lymph reservoir" },
          { id: 8, term: "Saphenous vein", match: "Medial thigh & leg" },
        ],
        8: [
          {
            id: 1,
            term: "Dorsal columns",
            match: "Fine touch & proprioception",
          },
          { id: 2, term: "Spinothalamic tract", match: "Pain & temperature" },
          { id: 3, term: "Corticospinal tract", match: "Voluntary movement" },
          { id: 4, term: "C5 dermatome", match: "Deltoid & lateral arm" },
          { id: 5, term: "T4 dermatome", match: "Nipple level" },
          { id: 6, term: "T10 dermatome", match: "Umbilicus level" },
          { id: 7, term: "L4 dermatome", match: "Knee reflex" },
          { id: 8, term: "S1 dermatome", match: "Lateral foot & ankle" },
        ],
        9: [
          {
            id: 1,
            term: "Falciform ligament",
            match: "Liver to anterior wall",
          },
          { id: 2, term: "Inguinal ligament", match: "ASIS to pubic tubercle" },
          { id: 3, term: "Broad ligament", match: "Uterine support" },
          { id: 4, term: "Round ligament", match: "Gubernaculum remnant" },
          {
            id: 5,
            term: "Medial umbilical fold",
            match: "Obliterated umbilical artery",
          },
          { id: 6, term: "Median umbilical fold", match: "Urachus remnant" },
          {
            id: 7,
            term: "Lateral umbilical fold",
            match: "Inferior epigastric artery",
          },
          { id: 8, term: "Coronary ligament", match: "Liver to diaphragm" },
        ],
        10: [
          { id: 1, term: "Serratus anterior", match: "Long thoracic (C5-C7)" },
          { id: 2, term: "Rhomboids", match: "Dorsal scapular nerve" },
          { id: 3, term: "Latissimus dorsi", match: "Thoracodorsal nerve" },
          { id: 4, term: "Deltoid", match: "Axillary nerve (C5-C6)" },
          { id: 5, term: "Biceps brachii", match: "Musculocutaneous nerve" },
          { id: 6, term: "Triceps brachii", match: "Radial nerve" },
          { id: 7, term: "Quadriceps femoris", match: "Femoral nerve" },
          { id: 8, term: "Hamstrings", match: "Sciatic nerve" },
        ],
        11: [
          { id: 1, term: "Type I pneumocytes", match: "Gas exchange surface" },
          {
            id: 2,
            term: "Type II pneumocytes",
            match: "Surfactant production",
          },
          { id: 3, term: "Parietal cells", match: "HCl + intrinsic factor" },
          { id: 4, term: "Chief cells", match: "Pepsinogen secretion" },
          { id: 5, term: "G cells", match: "Gastrin secretion" },
          { id: 6, term: "Kupffer cells", match: "Hepatic macrophages" },
          {
            id: 7,
            term: "Ito cells (hepatic stellate)",
            match: "Vitamin A storage + fibrosis",
          },
          { id: 8, term: "Juxtaglomerular cells", match: "Renin production" },
        ],
        12: [
          {
            id: 1,
            term: "Right kidney (anterior)",
            match: "Lies behind liver",
          },
          {
            id: 2,
            term: "Left kidney (anterior)",
            match: "Lies behind spleen",
          },
          { id: 3, term: "Pancreatic head", match: "Embraced by duodenum" },
          {
            id: 4,
            term: "Common bile duct",
            match: "Passes through pancreatic head",
          },
          {
            id: 5,
            term: "Ureter (pelvic brim)",
            match: "Crosses external iliac artery",
          },
          { id: 6, term: "Appendix base", match: "McBurney's point" },
          { id: 7, term: "Spleen (surface)", match: "9th–11th ribs" },
          { id: 8, term: "Aortic bifurcation", match: "L4 vertebral level" },
        ],
        13: [
          { id: 1, term: "Celiac trunk", match: "Foregut arterial supply" },
          {
            id: 2,
            term: "Superior mesenteric artery",
            match: "Midgut arterial supply",
          },
          {
            id: 3,
            term: "Inferior mesenteric artery",
            match: "Hindgut arterial supply",
          },
          {
            id: 4,
            term: "Renal arteries",
            match: "Kidney supply; arise at L2",
          },
          {
            id: 5,
            term: "Gonadal arteries",
            match: "Testis/ovary supply; arise at L2",
          },
          {
            id: 6,
            term: "Common iliac arteries",
            match: "Aorta bifurcation at L4",
          },
          {
            id: 7,
            term: "Inferior phrenic arteries",
            match: "Diaphragm supply; first aortic branches",
          },
          {
            id: 8,
            term: "Lumbar arteries",
            match: "Posterior abdominal wall supply",
          },
        ],
        14: [
          {
            id: 1,
            term: "Frontal bone",
            match: "Forehead; anterior cranial fossa floor",
          },
          { id: 2, term: "Parietal bone", match: "Top and lateral skull" },
          {
            id: 3,
            term: "Temporal bone",
            match: "Lateral skull; houses inner ear; middle cranial fossa",
          },
          {
            id: 4,
            term: "Occipital bone",
            match: "Posterior skull; contains foramen magnum",
          },
          {
            id: 5,
            term: "Sphenoid bone",
            match: "Central skull base; sella turcica",
          },
          {
            id: 6,
            term: "Ethmoid bone",
            match: "Cribriform plate; nasal cavity roof",
          },
          {
            id: 7,
            term: "Mandible",
            match: "Lower jaw; only movable skull bone",
          },
          {
            id: 8,
            term: "Maxilla",
            match: "Upper jaw; forms hard palate and orbital floor",
          },
        ],
        15: [
          {
            id: 1,
            term: "ACL",
            match: "Prevents anterior tibial displacement",
          },
          {
            id: 2,
            term: "PCL",
            match: "Prevents posterior tibial displacement",
          },
          {
            id: 3,
            term: "MCL",
            match: "Resists valgus stress; attached to medial meniscus",
          },
          {
            id: 4,
            term: "LCL",
            match: "Resists varus stress; not attached to lateral meniscus",
          },
          {
            id: 5,
            term: "Medial meniscus",
            match: "C-shaped; attached to MCL; less mobile",
          },
          {
            id: 6,
            term: "Lateral meniscus",
            match: "O-shaped; more mobile; unhappy triad spared",
          },
          {
            id: 7,
            term: "Popliteus",
            match: "Unlocks knee from full extension",
          },
          {
            id: 8,
            term: "Iliotibial band",
            match: "Lateral thigh fascia to Gerdy's tubercle",
          },
        ],
        16: [
          {
            id: 1,
            term: "Supraspinatus",
            match: "Abduction 0-15°; suprascapular nerve",
          },
          {
            id: 2,
            term: "Infraspinatus",
            match: "External rotation; suprascapular nerve",
          },
          {
            id: 3,
            term: "Teres minor",
            match: "External rotation; axillary nerve",
          },
          {
            id: 4,
            term: "Subscapularis",
            match: "Internal rotation; subscapular nerve",
          },
          {
            id: 5,
            term: "Subacromial bursa",
            match: "Between supraspinatus tendon and acromion",
          },
          {
            id: 6,
            term: "Long head of biceps",
            match: "Intra-articular; origin at supraglenoid tubercle",
          },
          {
            id: 7,
            term: "Glenohumeral ligaments",
            match: "Inferior is strongest; prevents anterior dislocation",
          },
          {
            id: 8,
            term: "Coracoacromial arch",
            match: "Supraspinatus outlet; narrowing causes impingement",
          },
        ],
        17: [
          {
            id: 1,
            term: "Hepatic artery",
            match: "25% liver blood; high O2; from celiac trunk",
          },
          {
            id: 2,
            term: "Portal vein",
            match: "75% liver blood; nutrient-rich; SMV + splenic",
          },
          {
            id: 3,
            term: "Hepatic veins",
            match: "Three veins drain directly into IVC",
          },
          {
            id: 4,
            term: "Couinaud segments",
            match: "8 independent segments with portal pedicles",
          },
          {
            id: 5,
            term: "Ligamentum venosum",
            match: "Ductus venosus remnant; in fissure of liver",
          },
          {
            id: 6,
            term: "Porta hepatis",
            match: "Portal vein + hepatic artery + bile duct entry",
          },
          {
            id: 7,
            term: "Round ligament of liver",
            match: "Ligamentum teres; umbilical vein remnant",
          },
          {
            id: 8,
            term: "Falciform ligament",
            match: "Liver to anterior abdominal wall; contains round ligament",
          },
        ],
        18: [
          {
            id: 1,
            term: "Posterior horn (spinal cord)",
            match: "Sensory neuron cell bodies (afferent)",
          },
          {
            id: 2,
            term: "Anterior horn (spinal cord)",
            match: "Motor neuron cell bodies (efferent)",
          },
          {
            id: 3,
            term: "Lateral horn (T1-L2, S2-S4)",
            match: "Autonomic preganglionic neurons",
          },
          {
            id: 4,
            term: "Gray matter (spinal cord)",
            match: "H-shaped; neuron cell bodies",
          },
          {
            id: 5,
            term: "White matter (spinal cord)",
            match: "Myelinated axon tracts surrounding gray",
          },
          {
            id: 6,
            term: "Central canal",
            match: "CSF channel; remnant of neural tube",
          },
          {
            id: 7,
            term: "Dorsal columns",
            match: "Fine touch, proprioception, vibration; ipsilateral",
          },
          {
            id: 8,
            term: "Lateral corticospinal tract",
            match: "Voluntary motor; decussates at medullary pyramids",
          },
        ],
      },
      3: {
        // Level 3 - 10 pairs each
        1: [
          { id: 1, term: "Heart", match: "Pumps blood" },
          { id: 2, term: "Lungs", match: "Gas exchange" },
          { id: 3, term: "Liver", match: "Detoxification" },
          { id: 4, term: "Kidneys", match: "Filter waste" },
          { id: 5, term: "Brain", match: "Neural control" },
          { id: 6, term: "Stomach", match: "Digest food" },
          { id: 7, term: "Pancreas", match: "Insulin production" },
          { id: 8, term: "Intestines", match: "Absorb nutrients" },
          { id: 9, term: "Spleen", match: "Blood filter" },
          { id: 10, term: "Gallbladder", match: "Store bile" },
        ],
        2: [
          { id: 1, term: "Nephron", match: "Kidney unit" },
          { id: 2, term: "Glomerulus", match: "Blood filtration" },
          { id: 3, term: "Bowman capsule", match: "Filtrate collection" },
          { id: 4, term: "Loop of Henle", match: "Water reabsorption" },
          { id: 5, term: "Collecting duct", match: "Urine concentration" },
          { id: 6, term: "Renal cortex", match: "Outer kidney" },
          { id: 7, term: "Renal medulla", match: "Inner kidney" },
          { id: 8, term: "Renal pelvis", match: "Urine collection" },
          { id: 9, term: "Ureter", match: "To bladder" },
          { id: 10, term: "Urethra", match: "Urine exit" },
        ],
        3: [
          { id: 1, term: "Alveoli", match: "Gas exchange sacs" },
          { id: 2, term: "Bronchi", match: "Main airways" },
          { id: 3, term: "Bronchioles", match: "Small airways" },
          { id: 4, term: "Pleura", match: "Lung membrane" },
          { id: 5, term: "Diaphragm", match: "Breathing muscle" },
          { id: 6, term: "Trachea", match: "Windpipe" },
          { id: 7, term: "Larynx", match: "Voice production" },
          { id: 8, term: "Pharynx", match: "Throat" },
          { id: 9, term: "Epiglottis", match: "Airway protection" },
          { id: 10, term: "Nasal cavity", match: "Air filtering" },
        ],
        4: [
          { id: 1, term: "Duodenum", match: "First small intestine" },
          { id: 2, term: "Jejunum", match: "Middle small intestine" },
          { id: 3, term: "Ileum", match: "Last small intestine" },
          { id: 4, term: "Cecum", match: "Start of colon" },
          { id: 5, term: "Ascending colon", match: "Right side colon" },
          { id: 6, term: "Transverse colon", match: "Cross abdomen" },
          { id: 7, term: "Descending colon", match: "Left side colon" },
          { id: 8, term: "Sigmoid colon", match: "S-shaped colon" },
          { id: 9, term: "Rectum", match: "Stool storage" },
          { id: 10, term: "Appendix", match: "Immune tissue" },
        ],
        5: [
          { id: 1, term: "Frontal lobe", match: "Planning/reasoning" },
          { id: 2, term: "Parietal lobe", match: "Sensory processing" },
          { id: 3, term: "Temporal lobe", match: "Hearing/memory" },
          { id: 4, term: "Occipital lobe", match: "Vision processing" },
          { id: 5, term: "Motor cortex", match: "Movement control" },
          { id: 6, term: "Sensory cortex", match: "Touch processing" },
          { id: 7, term: "Broca area", match: "Speech production" },
          { id: 8, term: "Wernicke area", match: "Speech comprehension" },
          { id: 9, term: "Prefrontal cortex", match: "Decision making" },
          { id: 10, term: "Visual cortex", match: "Image processing" },
        ],
        6: [
          { id: 1, term: "Frontal lobe", match: "Executive function" },
          { id: 2, term: "Parietal lobe", match: "Spatial awareness" },
          { id: 3, term: "Temporal lobe", match: "Auditory processing" },
          { id: 4, term: "Occipital lobe", match: "Visual processing" },
          { id: 5, term: "Cerebellum", match: "Coordination" },
          { id: 6, term: "Brainstem", match: "Vital functions" },
          { id: 7, term: "Thalamus", match: "Sensory relay" },
          { id: 8, term: "Hypothalamus", match: "Homeostasis" },
          { id: 9, term: "Hippocampus", match: "Memory" },
          { id: 10, term: "Amygdala", match: "Emotion" },
        ],
        7: [
          { id: 1, term: "Brodmann area 4", match: "Primary motor cortex" },
          { id: 2, term: "Brodmann area 17", match: "Primary visual cortex" },
          { id: 3, term: "Broca's area", match: "Speech production" },
          { id: 4, term: "Wernicke's area", match: "Speech comprehension" },
          { id: 5, term: "Substantia nigra", match: "Dopamine production" },
          { id: 6, term: "Locus coeruleus", match: "Norepinephrine source" },
          { id: 7, term: "Raphe nuclei", match: "Serotonin source" },
          { id: 8, term: "VTA", match: "Dopamine reward pathway" },
          { id: 9, term: "Basal ganglia", match: "Movement modulation" },
          { id: 10, term: "Anterior cingulate", match: "Error detection" },
        ],
        8: [
          { id: 1, term: "Notochord", match: "Induces neural tube" },
          { id: 2, term: "Neural crest cells", match: "Melanocytes & PNS" },
          { id: 3, term: "Somites", match: "Muscle & vertebrae" },
          { id: 4, term: "Endoderm", match: "GI epithelium & lungs" },
          { id: 5, term: "Ectoderm", match: "Skin & nervous system" },
          { id: 6, term: "Mesoderm", match: "Muscle, bone & blood" },
          { id: 7, term: "Primitive streak", match: "Gastrulation axis" },
          { id: 8, term: "Allantois", match: "Bladder / urachus origin" },
          { id: 9, term: "Yolk sac", match: "Early hematopoiesis" },
          {
            id: 10,
            term: "Lateral plate mesoderm",
            match: "GI tract & limb bones",
          },
        ],
        9: [
          { id: 1, term: "Foramen ovale", match: "CN V3" },
          { id: 2, term: "Foramen rotundum", match: "CN V2" },
          {
            id: 3,
            term: "Superior orbital fissure",
            match: "CN III, IV, VI, V1",
          },
          { id: 4, term: "Jugular foramen", match: "CN IX, X, XI" },
          { id: 5, term: "Foramen magnum", match: "Brainstem exit" },
          { id: 6, term: "Internal acoustic meatus", match: "CN VII & VIII" },
          { id: 7, term: "Hypoglossal canal", match: "CN XII" },
          { id: 8, term: "Cribriform plate", match: "CN I" },
          { id: 9, term: "Optic canal", match: "CN II & ophthalmic artery" },
          { id: 10, term: "Stylomastoid foramen", match: "CN VII exit" },
        ],
        10: [
          {
            id: 1,
            term: "Transpyloric plane (L1)",
            match: "Pylorus + renal hila",
          },
          { id: 2, term: "Aortic bifurcation", match: "L4 level" },
          {
            id: 3,
            term: "Sternal angle (angle of Louis)",
            match: "T4 — carina level",
          },
          {
            id: 4,
            term: "Cricoid cartilage",
            match: "C6 — start of trachea/esophagus",
          },
          { id: 5, term: "McBurney's point", match: "Appendix base location" },
          { id: 6, term: "Murphy's point", match: "Gallbladder fundus" },
          { id: 7, term: "Calot's triangle", match: "Cystic artery location" },
          {
            id: 8,
            term: "Hesselbach's triangle",
            match: "Direct inguinal hernia site",
          },
          {
            id: 9,
            term: "Deep inguinal ring",
            match: "Midpoint of inguinal ligament",
          },
          {
            id: 10,
            term: "Femoral triangle",
            match: "NAVEL — nerve, artery, vein, empty, lymph",
          },
        ],
        11: [
          {
            id: 1,
            term: "MCA territory",
            match: "Lateral cortex — face and arm",
          },
          { id: 2, term: "ACA territory", match: "Medial cortex — leg" },
          { id: 3, term: "PCA territory", match: "Occipital lobe — vision" },
          {
            id: 4,
            term: "PICA territory",
            match: "Lateral medulla + inferior cerebellum",
          },
          {
            id: 5,
            term: "AICA territory",
            match: "Lateral inferior cerebellum",
          },
          { id: 6, term: "Basilar artery", match: "Pons" },
          {
            id: 7,
            term: "Celiac trunk",
            match: "Foregut — esophagus to proximal duodenum",
          },
          {
            id: 8,
            term: "Superior mesenteric artery",
            match: "Midgut — duodenum to splenic flexure",
          },
          {
            id: 9,
            term: "Inferior mesenteric artery",
            match: "Hindgut — splenic flexure to rectum",
          },
          {
            id: 10,
            term: "Watershed zone (colon)",
            match: "Splenic + hepatic flexures",
          },
        ],
        12: [
          {
            id: 1,
            term: "Meckel's diverticulum",
            match: "Vitello-intestinal duct remnant",
          },
          { id: 2, term: "Patent urachus", match: "Allantois remnant" },
          {
            id: 3,
            term: "Horseshoe kidney",
            match: "Fusion failure + non-ascent",
          },
          { id: 4, term: "Hypospadias", match: "Urethral fold fusion failure" },
          {
            id: 5,
            term: "Tracheoesophageal fistula",
            match: "Abnormal TE septation",
          },
          { id: 6, term: "Duodenal atresia", match: "Double bubble sign" },
          {
            id: 7,
            term: "Hirschsprung's disease",
            match: "Absent myenteric plexus",
          },
          { id: 8, term: "Pyloric stenosis", match: "Hypertrophied pylorus" },
          {
            id: 9,
            term: "Omphalocele",
            match: "Failure of gut to return (covered)",
          },
          {
            id: 10,
            term: "Gastroschisis",
            match: "Lateral abdominal wall defect (uncovered)",
          },
        ],
        13: [
          {
            id: 1,
            term: "CN III nucleus (Edinger-Westphal)",
            match: "Parasympathetic pupillary constriction",
          },
          {
            id: 2,
            term: "CN IV nucleus",
            match: "Exits dorsal midbrain; only dorsal CN exit",
          },
          {
            id: 3,
            term: "CN VI nucleus (abducens)",
            match: "Facial colliculus; floor of 4th ventricle",
          },
          {
            id: 4,
            term: "Nucleus ambiguus",
            match: "CN IX, X, XI motor nuclei; branchiomotor",
          },
          {
            id: 5,
            term: "Nucleus tractus solitarius",
            match: "CN VII, IX, X visceral afferents; taste",
          },
          {
            id: 6,
            term: "Mesencephalic nucleus of V",
            match: "Proprioception; only peripheral neuron in CNS",
          },
          {
            id: 7,
            term: "Spinal nucleus of V",
            match: "Pain and temperature from face",
          },
          {
            id: 8,
            term: "Salivatory nucleus",
            match: "CN VII (submandibular/sublingual) + IX (parotid)",
          },
          {
            id: 9,
            term: "Cochlear nucleus",
            match: "Hearing; dorsal + ventral; cochlear nerve input",
          },
          {
            id: 10,
            term: "Vestibular nuclei",
            match: "Balance; project to cerebellum + spinal cord",
          },
        ],
        14: [
          {
            id: 1,
            term: "Musculocutaneous nerve (C5-C7)",
            match: "Biceps brachii, brachialis, coracobrachialis",
          },
          {
            id: 2,
            term: "Median nerve (C6-T1)",
            match: "Anterior forearm + thenar muscles; ape hand if injured",
          },
          {
            id: 3,
            term: "Ulnar nerve (C8-T1)",
            match: "Intrinsic hand + medial flexors; claw hand if injured",
          },
          {
            id: 4,
            term: "Radial nerve (C5-T1)",
            match: "Posterior arm + forearm extensors; wrist drop if injured",
          },
          {
            id: 5,
            term: "Axillary nerve (C5-C6)",
            match: "Deltoid + teres minor; quadrangular space",
          },
          {
            id: 6,
            term: "Thoracodorsal nerve (C6-C8)",
            match: "Latissimus dorsi; axillary dissection at risk",
          },
          {
            id: 7,
            term: "Dorsal scapular nerve (C5)",
            match: "Rhomboids + levator scapulae",
          },
          {
            id: 8,
            term: "Long thoracic nerve (C5-C7)",
            match: "Serratus anterior; winged scapula if injured",
          },
          {
            id: 9,
            term: "Suprascapular nerve (C5-C6)",
            match: "Supraspinatus + infraspinatus",
          },
          {
            id: 10,
            term: "Medial pectoral nerve",
            match: "Pectoralis minor + part of pec major",
          },
        ],
        15: [
          {
            id: 1,
            term: "Left gastric → esophageal varices",
            match: "Portal (left gastric) ↔ systemic (azygos)",
          },
          {
            id: 2,
            term: "Superior rectal → rectal varices",
            match:
              "Portal (superior rectal) ↔ systemic (middle/inferior rectal)",
          },
          {
            id: 3,
            term: "Paraumbilical → caput medusae",
            match: "Portal (paraumbilical) ↔ systemic (epigastric veins)",
          },
          {
            id: 4,
            term: "Retzius veins",
            match: "Retroperitoneal portal ↔ posterior abdominal wall systemic",
          },
          {
            id: 5,
            term: "Splenomegaly",
            match: "Splenic vein congestion from portal hypertension",
          },
          {
            id: 6,
            term: "Ascites (portal HTN)",
            match: "Elevated portal pressure + low albumin → fluid shift",
          },
          {
            id: 7,
            term: "Hepatic encephalopathy",
            match: "Ammonia bypasses liver via porto-systemic shunts",
          },
          {
            id: 8,
            term: "Spontaneous bacterial peritonitis",
            match: "Ascites complication; E. coli + Klebsiella",
          },
          {
            id: 9,
            term: "Hepatorenal syndrome",
            match: "Renal vasoconstriction + low GFR in liver failure",
          },
          {
            id: 10,
            term: "Budd-Chiari syndrome",
            match: "Hepatic vein obstruction → tender hepatomegaly + ascites",
          },
        ],
        16: [
          {
            id: 1,
            term: "Zona glomerulosa",
            match: "Aldosterone; outermost; renin-angiotensin regulated",
          },
          {
            id: 2,
            term: "Zona fasciculata",
            match: "Cortisol; middle (largest zone); ACTH regulated",
          },
          {
            id: 3,
            term: "Zona reticularis",
            match: "Androgens (DHEA/DHEA-S); innermost cortex",
          },
          {
            id: 4,
            term: "Adrenal medulla",
            match: "Chromaffin cells; epinephrine 80% + NE 20%",
          },
          {
            id: 5,
            term: "Right adrenal vein",
            match: "Drains directly into IVC",
          },
          {
            id: 6,
            term: "Left adrenal vein",
            match: "Drains into left renal vein",
          },
          {
            id: 7,
            term: "Superior adrenal arteries",
            match: "From inferior phrenic artery",
          },
          {
            id: 8,
            term: "Middle adrenal arteries",
            match: "From abdominal aorta",
          },
          {
            id: 9,
            term: "Inferior adrenal arteries",
            match: "From renal arteries",
          },
          {
            id: 10,
            term: "Extra-adrenal pheochromocytoma",
            match: "Paraganglioma; 10% rule; organ of Zuckerkandl",
          },
        ],
        17: [
          {
            id: 1,
            term: "Levator ani",
            match: "Puborectalis + pubococcygeus + iliococcygeus",
          },
          {
            id: 2,
            term: "Pelvic diaphragm",
            match: "Levator ani + coccygeus; pelvic floor",
          },
          {
            id: 3,
            term: "Urogenital diaphragm",
            match: "Deep transverse perineal + sphincter urethrae",
          },
          {
            id: 4,
            term: "Ischiorectal fossa",
            match: "Inferior to levator ani; contains pudendal canal",
          },
          {
            id: 5,
            term: "Perineal body",
            match: "Central tendon of perineum; attaches all perineal muscles",
          },
          {
            id: 6,
            term: "Anal triangle",
            match: "Posterior perineum; contains anal canal",
          },
          {
            id: 7,
            term: "Urogenital triangle",
            match: "Anterior perineum; external genitalia + urethra",
          },
          {
            id: 8,
            term: "Superficial perineal space",
            match: "Between Colles' fascia and perineal membrane",
          },
          {
            id: 9,
            term: "Deep perineal space",
            match: "Between perineal membrane and pelvic diaphragm",
          },
          {
            id: 10,
            term: "Pudendal nerve (S2-S4)",
            match: "Passes through Alcock's canal; perineal sensation",
          },
        ],
        18: [
          {
            id: 1,
            term: "Superior mediastinum",
            match: "Aortic arch, SVC, trachea, esophagus, thymus",
          },
          {
            id: 2,
            term: "Anterior mediastinum",
            match: "Thymus remnant, fat, lymphatics",
          },
          {
            id: 3,
            term: "Middle mediastinum",
            match: "Heart, pericardium, great vessel roots, phrenic nerve",
          },
          {
            id: 4,
            term: "Posterior mediastinum",
            match: "Esophagus, descending aorta, azygos, thoracic duct",
          },
          {
            id: 5,
            term: "Right recurrent laryngeal nerve",
            match: "Loops around right subclavian artery",
          },
          {
            id: 6,
            term: "Left recurrent laryngeal nerve",
            match: "Loops around ligamentum arteriosum/aortic arch",
          },
          {
            id: 7,
            term: "Thoracic duct",
            match: "Left-sided; drains to left subclavian-IJV junction",
          },
          {
            id: 8,
            term: "Azygos vein",
            match: "Right paravertebral; drains into SVC",
          },
          {
            id: 9,
            term: "Greater splanchnic nerve (T5-T9)",
            match: "Celiac ganglion; foregut sympathetic",
          },
          {
            id: 10,
            term: "Lesser splanchnic nerve (T10-T11)",
            match: "Superior mesenteric ganglion; midgut sympathetic",
          },
        ],
      },
      4: {
        // Level 4 - 12 pairs each
        1: [
          { id: 1, term: "Heart", match: "Pumps blood" },
          { id: 2, term: "Lungs", match: "Gas exchange" },
          { id: 3, term: "Liver", match: "Detoxification" },
          { id: 4, term: "Kidneys", match: "Filter waste" },
          { id: 5, term: "Brain", match: "Neural control" },
          { id: 6, term: "Stomach", match: "Digest food" },
          { id: 7, term: "Pancreas", match: "Insulin production" },
          { id: 8, term: "Intestines", match: "Absorb nutrients" },
          { id: 9, term: "Spleen", match: "Blood filter" },
          { id: 10, term: "Gallbladder", match: "Store bile" },
          { id: 11, term: "Thymus", match: "T-cell maturation" },
          { id: 12, term: "Adrenal glands", match: "Stress hormones" },
        ],
        2: [
          { id: 1, term: "Scapula", match: "Shoulder blade" },
          { id: 2, term: "Clavicle", match: "Collarbone" },
          { id: 3, term: "Pelvis", match: "Hip bones" },
          { id: 4, term: "Patella", match: "Kneecap" },
          { id: 5, term: "Fibula", match: "Calf bone" },
          { id: 6, term: "Ulna", match: "Inner forearm" },
          { id: 7, term: "Carpals", match: "Wrist bones" },
          { id: 8, term: "Metacarpals", match: "Hand bones" },
          { id: 9, term: "Phalanges", match: "Finger bones" },
          { id: 10, term: "Tarsals", match: "Ankle bones" },
          { id: 11, term: "Metatarsals", match: "Foot bones" },
          { id: 12, term: "Sacrum", match: "Base of spine" },
        ],
        3: [
          { id: 1, term: "Hepatocytes", match: "Liver cells" },
          { id: 2, term: "Kupffer cells", match: "Liver macrophages" },
          { id: 3, term: "Portal triad", match: "Artery/vein/bile duct" },
          { id: 4, term: "Central vein", match: "Lobule drainage" },
          { id: 5, term: "Sinusoids", match: "Liver capillaries" },
          { id: 6, term: "Bile canaliculi", match: "Bile channels" },
          { id: 7, term: "Space of Disse", match: "Between cells" },
          { id: 8, term: "Right lobe", match: "Larger liver lobe" },
          { id: 9, term: "Left lobe", match: "Smaller liver lobe" },
          { id: 10, term: "Caudate lobe", match: "Posterior lobe" },
          { id: 11, term: "Quadrate lobe", match: "Inferior lobe" },
          { id: 12, term: "Falciform ligament", match: "Liver attachment" },
        ],
        4: [
          { id: 1, term: "SA node", match: "Pacemaker" },
          { id: 2, term: "AV node", match: "Delay signal" },
          { id: 3, term: "Bundle of His", match: "Septal conduction" },
          { id: 4, term: "Purkinje fibers", match: "Ventricular activation" },
          { id: 5, term: "Epicardium", match: "Outer heart layer" },
          { id: 6, term: "Myocardium", match: "Heart muscle" },
          { id: 7, term: "Endocardium", match: "Inner heart lining" },
          { id: 8, term: "Pericardium", match: "Heart sac" },
          { id: 9, term: "Septum", match: "Heart divider" },
          { id: 10, term: "Papillary muscles", match: "Valve anchors" },
          { id: 11, term: "Chordae tendineae", match: "Valve strings" },
          { id: 12, term: "Apex", match: "Heart tip" },
        ],
        5: [
          { id: 1, term: "Cervical vertebrae", match: "Neck bones (C1-C7)" },
          { id: 2, term: "Thoracic vertebrae", match: "Chest bones (T1-T12)" },
          { id: 3, term: "Lumbar vertebrae", match: "Lower back (L1-L5)" },
          { id: 4, term: "Atlas (C1)", match: "Supports skull" },
          { id: 5, term: "Axis (C2)", match: "Head rotation" },
          { id: 6, term: "Intervertebral disc", match: "Cushion between" },
          { id: 7, term: "Spinal canal", match: "Cord protection" },
          { id: 8, term: "Spinous process", match: "Back projection" },
          { id: 9, term: "Transverse process", match: "Side projection" },
          { id: 10, term: "Vertebral body", match: "Weight bearing" },
          { id: 11, term: "Foramen", match: "Nerve opening" },
          { id: 12, term: "Facet joints", match: "Spine articulation" },
        ],
        6: [
          { id: 1, term: "Scapula", match: "Shoulder blade" },
          { id: 2, term: "Clavicle", match: "Collarbone" },
          { id: 3, term: "Pelvis", match: "Hip bones" },
          { id: 4, term: "Patella", match: "Kneecap" },
          { id: 5, term: "Fibula", match: "Calf bone" },
          { id: 6, term: "Ulna", match: "Inner forearm" },
          { id: 7, term: "Carpals", match: "Wrist bones" },
          { id: 8, term: "Metacarpals", match: "Hand bones" },
          { id: 9, term: "Phalanges", match: "Finger bones" },
          { id: 10, term: "Tarsals", match: "Ankle bones" },
          { id: 11, term: "Metatarsals", match: "Foot bones" },
          { id: 12, term: "Sacrum", match: "Base of spine" },
        ],
        7: [
          { id: 1, term: "Porta hepatis", match: "Portal triad entry" },
          { id: 2, term: "Foramen of Winslow", match: "Entry to lesser sac" },
          { id: 3, term: "Morison's pouch", match: "Right subhepatic space" },
          { id: 4, term: "Pouch of Douglas", match: "Rectouterine pouch" },
          { id: 5, term: "Femoral triangle", match: "NAVEL contents" },
          { id: 6, term: "Adductor canal", match: "Hunter's canal" },
          { id: 7, term: "Hesselbach's triangle", match: "Direct hernia site" },
          { id: 8, term: "Scarpa's fascia", match: "Deep membranous fascia" },
          { id: 9, term: "Linea alba", match: "Midline aponeurosis" },
          {
            id: 10,
            term: "Ligament of Treitz",
            match: "Duodenojejunal flexure",
          },
          { id: 11, term: "Glisson's capsule", match: "Liver fibrous coat" },
          {
            id: 12,
            term: "Denonvilliers' fascia",
            match: "Rectoprostatic septum",
          },
        ],
        8: [
          {
            id: 1,
            term: "Dorsal root ganglion",
            match: "Sensory neuron cell bodies",
          },
          { id: 2, term: "Anterior horn", match: "LMN (motor) cell bodies" },
          {
            id: 3,
            term: "Nucleus gracilis",
            match: "Lower limb fine touch relay",
          },
          {
            id: 4,
            term: "Nucleus cuneatus",
            match: "Upper limb fine touch relay",
          },
          { id: 5, term: "VPL thalamus", match: "Somatosensory relay nucleus" },
          {
            id: 6,
            term: "Lateral geniculate nucleus",
            match: "Visual thalamic relay",
          },
          {
            id: 7,
            term: "Medial geniculate nucleus",
            match: "Auditory thalamic relay",
          },
          {
            id: 8,
            term: "Substantia nigra pars compacta",
            match: "Dopaminergic nigrostriatal pathway",
          },
          {
            id: 9,
            term: "Subthalamic nucleus",
            match: "Modulates basal ganglia output",
          },
          {
            id: 10,
            term: "Caudate + putamen",
            match: "Striatum — basal ganglia input",
          },
          {
            id: 11,
            term: "Globus pallidus internus (GPi)",
            match: "Main basal ganglia output",
          },
          { id: 12, term: "Red nucleus", match: "Origin of rubrospinal tract" },
        ],
        9: [
          {
            id: 1,
            term: "Superior sagittal sinus",
            match: "Drains to confluens sinuum",
          },
          {
            id: 2,
            term: "Cavernous sinus",
            match: "CN III, IV, V1, V2, VI pass through",
          },
          { id: 3, term: "Portal vein", match: "SMV + splenic vein junction" },
          { id: 4, term: "Hepatic veins", match: "Drain directly to IVC" },
          {
            id: 5,
            term: "Left gonadal vein",
            match: "Drains to left renal vein",
          },
          {
            id: 6,
            term: "Right gonadal vein",
            match: "Drains directly to IVC",
          },
          {
            id: 7,
            term: "Azygos vein",
            match: "Right-sided thoracic venous drainage",
          },
          {
            id: 8,
            term: "Coronary sinus",
            match: "Returns cardiac venous blood to right atrium",
          },
          {
            id: 9,
            term: "Great saphenous vein",
            match: "Medial leg → femoral vein",
          },
          {
            id: 10,
            term: "Cephalic vein",
            match: "Lateral arm → axillary vein",
          },
          {
            id: 11,
            term: "Internal jugular vein",
            match: "Drains brain via sigmoid sinus",
          },
          {
            id: 12,
            term: "Batson's plexus",
            match: "Vertebral venous plexus — cancer spread route",
          },
        ],
        10: [
          {
            id: 1,
            term: "Carpal tunnel",
            match: "9 flexor tendons + median nerve",
          },
          {
            id: 2,
            term: "Cubital fossa",
            match: "Median nerve, brachial artery, biceps tendon",
          },
          {
            id: 3,
            term: "Popliteal fossa",
            match: "Sciatic branches + popliteal vessels",
          },
          {
            id: 4,
            term: "Adductor canal",
            match: "Femoral vessels + saphenous nerve",
          },
          {
            id: 5,
            term: "Femoral sheath",
            match: "Femoral artery, vein + femoral canal",
          },
          {
            id: 6,
            term: "Morison's pouch",
            match: "Right hepatorenal space (most dependent supine)",
          },
          {
            id: 7,
            term: "Pouch of Douglas",
            match: "Rectouterine space (most dependent erect)",
          },
          {
            id: 8,
            term: "Subphrenic space",
            match: "Between diaphragm and liver",
          },
          {
            id: 9,
            term: "Anterior mediastinum",
            match: "Thymus, aorta, great vessels",
          },
          {
            id: 10,
            term: "Posterior mediastinum",
            match: "Esophagus, thoracic aorta, thoracic duct",
          },
          {
            id: 11,
            term: "Inguinal canal (male)",
            match: "Contains spermatic cord",
          },
          {
            id: 12,
            term: "Parotid gland",
            match: "CN VII traverses (facial palsy if compressed)",
          },
        ],
        11: [
          {
            id: 1,
            term: "Type I pneumocytes",
            match: "Gas exchange; squamous; 95% alveolar surface",
          },
          {
            id: 2,
            term: "Type II pneumocytes",
            match: "Surfactant production; regenerate type I",
          },
          {
            id: 3,
            term: "Parietal cells",
            match: "HCl + intrinsic factor; proton pump target",
          },
          {
            id: 4,
            term: "Chief cells",
            match: "Pepsinogen; activated by HCl to pepsin",
          },
          {
            id: 5,
            term: "G cells (antrum)",
            match: "Gastrin; stimulated by protein + H. pylori",
          },
          {
            id: 6,
            term: "Goblet cells",
            match: "Mucus secretion; small intestine + respiratory",
          },
          {
            id: 7,
            term: "Paneth cells",
            match: "Defensins (antimicrobial); base of crypts of Lieberkühn",
          },
          {
            id: 8,
            term: "Brunner's glands",
            match: "Alkaline mucus; submucosa of duodenum only",
          },
          {
            id: 9,
            term: "Meissner's plexus",
            match: "Submucosal plexus; controls GI secretion",
          },
          {
            id: 10,
            term: "Auerbach's plexus",
            match: "Myenteric plexus; controls GI motility",
          },
          {
            id: 11,
            term: "Interstitial cells of Cajal",
            match: "GI pacemaker cells; slow wave generator",
          },
          {
            id: 12,
            term: "Kupffer cells",
            match: "Resident hepatic macrophages; line sinusoids",
          },
        ],
        12: [
          {
            id: 1,
            term: "Radial nerve (mid-shaft humerus)",
            match: "Wrist drop + loss of finger extension",
          },
          {
            id: 2,
            term: "Median nerve (supracondylar fracture)",
            match: "AIN — loss of index/middle DIP flexion",
          },
          {
            id: 3,
            term: "Ulnar nerve (medial epicondyle)",
            match: "Claw hand; ring/little finger intrinsic atrophy",
          },
          {
            id: 4,
            term: "Axillary nerve (humeral neck fracture)",
            match: "Deltoid weakness + lateral shoulder numbness",
          },
          {
            id: 5,
            term: "Common peroneal (fibular neck)",
            match: "Foot drop + loss of dorsiflexion and eversion",
          },
          {
            id: 6,
            term: "Femoral nerve",
            match: "Quadriceps weakness + loss of patellar reflex",
          },
          {
            id: 7,
            term: "Obturator nerve (pelvic surgery)",
            match: "Adductor weakness + medial thigh numbness",
          },
          {
            id: 8,
            term: "Superior gluteal nerve",
            match: "Trendelenburg gait (hip abductor weakness)",
          },
          {
            id: 9,
            term: "Sciatic nerve (posterior hip dislocation)",
            match: "Combined common peroneal + tibial deficits",
          },
          {
            id: 10,
            term: "Long thoracic nerve (mastectomy)",
            match: "Winged scapula (serratus anterior paralysis)",
          },
          {
            id: 11,
            term: "Phrenic nerve (cervical surgery)",
            match: "Ipsilateral hemidiaphragm paralysis",
          },
          {
            id: 12,
            term: "Recurrent laryngeal nerve (thyroidectomy)",
            match: "Hoarse voice + aspiration risk",
          },
        ],
        13: [
          {
            id: 1,
            term: "Superior vena cava",
            match: "Drains head, neck, arms, upper thorax to right atrium",
          },
          {
            id: 2,
            term: "Azygos vein",
            match: "Right paravertebral → drains to SVC",
          },
          {
            id: 3,
            term: "Hemiazygos",
            match: "Left lower paravertebral; crosses to azygos at T8-T9",
          },
          {
            id: 4,
            term: "Portal vein",
            match: "SMV + splenic vein; normal pressure 5-10 mmHg",
          },
          {
            id: 5,
            term: "Great saphenous vein",
            match: "Medial leg → femoral vein at saphenofemoral junction",
          },
          {
            id: 6,
            term: "Short saphenous vein",
            match: "Posterior calf → popliteal vein",
          },
          { id: 7, term: "Basilic vein", match: "Medial arm → brachial vein" },
          {
            id: 8,
            term: "Cephalic vein",
            match: "Lateral arm → axillary vein",
          },
          {
            id: 9,
            term: "Left renal vein",
            match: "Longer; receives left gonadal + adrenal veins",
          },
          {
            id: 10,
            term: "Left gonadal vein",
            match: "Drains into left renal vein (not IVC)",
          },
          {
            id: 11,
            term: "Right gonadal vein",
            match: "Drains directly into IVC",
          },
          {
            id: 12,
            term: "Coronary sinus",
            match: "Returns venous cardiac blood to right atrium",
          },
        ],
        14: [
          {
            id: 1,
            term: "Iliopsoas",
            match: "Hip flexion; L1-L3; femoral nerve",
          },
          {
            id: 2,
            term: "Gluteus maximus",
            match: "Hip extension; inferior gluteal nerve",
          },
          {
            id: 3,
            term: "Gluteus medius",
            match: "Hip abduction; superior gluteal nerve",
          },
          {
            id: 4,
            term: "Sartorius",
            match:
              "Hip flexion + lateral rotation + knee flexion; femoral nerve",
          },
          {
            id: 5,
            term: "Gracilis",
            match: "Thigh adduction + knee flexion; obturator nerve",
          },
          {
            id: 6,
            term: "Gastrocnemius",
            match: "Plantarflexion + knee flexion; tibial nerve (S1-S2)",
          },
          {
            id: 7,
            term: "Soleus",
            match: "Plantarflexion (standing); tibial nerve; S1 ankle reflex",
          },
          {
            id: 8,
            term: "Tibialis anterior",
            match: "Dorsiflexion + inversion; deep peroneal nerve (L4)",
          },
          {
            id: 9,
            term: "Peroneus longus/brevis",
            match: "Eversion + plantarflexion; superficial peroneal nerve",
          },
          {
            id: 10,
            term: "Extensor digitorum longus",
            match: "Toe extension + dorsiflexion; deep peroneal nerve",
          },
          {
            id: 11,
            term: "Flexor digitorum longus",
            match: "Toe flexion; tibial nerve; crosses plantar foot",
          },
          {
            id: 12,
            term: "Peroneus tertius",
            match: "Dorsiflexion + eversion; deep peroneal nerve",
          },
        ],
        15: [
          {
            id: 1,
            term: "SA node",
            match: "Right atrium near SVC; 60-100 bpm; RCA supply (60%)",
          },
          {
            id: 2,
            term: "AV node",
            match: "Interatrial septum; 40-60 bpm; RCA supply (85%)",
          },
          {
            id: 3,
            term: "Bundle of His",
            match: "Penetrates fibrous skeleton; electrical bridge",
          },
          {
            id: 4,
            term: "Left bundle branch",
            match: "Divides into anterior + posterior fascicles",
          },
          {
            id: 5,
            term: "Right bundle branch",
            match: "Single fascicle; runs in moderator band (RV)",
          },
          {
            id: 6,
            term: "Purkinje fibers",
            match: "Rapid subendocardial conduction; 20-40 bpm intrinsic",
          },
          {
            id: 7,
            term: "Cardiac AP phase 0",
            match: "Rapid Na+ influx (fast Na+ channels)",
          },
          {
            id: 8,
            term: "Cardiac AP phase 2",
            match: "Plateau — Ca2+ influx = K+ efflux; unique to heart",
          },
          {
            id: 9,
            term: "Pacemaker AP (phase 4)",
            match: "Slow Na+/Ca2+ influx (If current); sets heart rate",
          },
          {
            id: 10,
            term: "Troponin I/T",
            match: "Rise 4-6h post-MI; peak 24h; normalize 7-10 days",
          },
          {
            id: 11,
            term: "Creatine kinase-MB",
            match:
              "Rise 4-8h post-MI; normalize 48-72h; useful for reinfarction",
          },
          {
            id: 12,
            term: "Myoglobin",
            match: "First marker post-MI (1-4h); low specificity",
          },
        ],
        16: [
          {
            id: 1,
            term: "Parotid lymph nodes",
            match: "Drain scalp, face, ear, parotid",
          },
          {
            id: 2,
            term: "Submandibular nodes",
            match: "Drain lower face, teeth, anterior tongue",
          },
          {
            id: 3,
            term: "Submental nodes",
            match: "Drain lower lip, chin, floor of mouth",
          },
          {
            id: 4,
            term: "Jugulodigastric node",
            match: "Tonsillar node; most enlarged in pharyngitis",
          },
          {
            id: 5,
            term: "Posterior cervical chain",
            match: "Drains posterior scalp and neck",
          },
          {
            id: 6,
            term: "Pretracheal nodes",
            match: "Drain thyroid gland and trachea",
          },
          {
            id: 7,
            term: "Deep cervical nodes (Levels I-V)",
            match: "Final drainage for entire head and neck",
          },
          {
            id: 8,
            term: "Virchow's node (left supraclavicular)",
            match: "GI malignancy sentinel (Troisier's sign)",
          },
          {
            id: 9,
            term: "Right supraclavicular node",
            match: "Lung or esophageal malignancy sentinel",
          },
          {
            id: 10,
            term: "Sentinel node (breast)",
            match: "Axillary Level I most commonly",
          },
          {
            id: 11,
            term: "Right lymphatic duct",
            match: "Right upper body → right subclavian-IJV junction",
          },
          {
            id: 12,
            term: "Thoracic duct",
            match: "Left-sided; drains everything else to left subclavian-IJV",
          },
        ],
      },
      5: {
        // Level 5 - 14 pairs each
        1: [
          { id: 1, term: "Heart", match: "Pumps blood" },
          { id: 2, term: "Lungs", match: "Gas exchange" },
          { id: 3, term: "Liver", match: "Detoxification" },
          { id: 4, term: "Kidneys", match: "Filter waste" },
          { id: 5, term: "Brain", match: "Neural control" },
          { id: 6, term: "Stomach", match: "Digest food" },
          { id: 7, term: "Pancreas", match: "Insulin production" },
          { id: 8, term: "Intestines", match: "Absorb nutrients" },
          { id: 9, term: "Spleen", match: "Blood filter" },
          { id: 10, term: "Gallbladder", match: "Store bile" },
          { id: 11, term: "Thymus", match: "T-cell maturation" },
          { id: 12, term: "Adrenal glands", match: "Stress hormones" },
          { id: 13, term: "Bone marrow", match: "Blood cell production" },
          { id: 14, term: "Lymph nodes", match: "Immune filtering" },
        ],
        2: [
          { id: 1, term: "Cranial nerve I", match: "Olfactory (smell)" },
          { id: 2, term: "Cranial nerve II", match: "Optic (vision)" },
          { id: 3, term: "Cranial nerve III", match: "Oculomotor (eye)" },
          { id: 4, term: "Cranial nerve V", match: "Trigeminal (face)" },
          { id: 5, term: "Cranial nerve VII", match: "Facial (expression)" },
          { id: 6, term: "Cranial nerve VIII", match: "Vestibulocochlear" },
          { id: 7, term: "Cranial nerve X", match: "Vagus (parasympathetic)" },
          { id: 8, term: "Cranial nerve XII", match: "Hypoglossal (tongue)" },
          { id: 9, term: "Brachial plexus", match: "Arm nerves" },
          { id: 10, term: "Sciatic nerve", match: "Largest nerve" },
          { id: 11, term: "Femoral nerve", match: "Thigh sensation" },
          { id: 12, term: "Radial nerve", match: "Arm extension" },
          { id: 13, term: "Median nerve", match: "Hand grip" },
          { id: 14, term: "Ulnar nerve", match: "Little finger" },
        ],
        3: [
          { id: 1, term: "Thoracic duct", match: "Main lymph vessel" },
          { id: 2, term: "Cisterna chyli", match: "Lymph reservoir" },
          { id: 3, term: "Spleen", match: "Lymphoid organ" },
          { id: 4, term: "Tonsils", match: "Throat lymphoid" },
          { id: 5, term: "Adenoids", match: "Nasal lymphoid" },
          { id: 6, term: "Peyer patches", match: "Gut lymphoid" },
          { id: 7, term: "Axillary nodes", match: "Armpit drainage" },
          { id: 8, term: "Inguinal nodes", match: "Groin drainage" },
          { id: 9, term: "Cervical nodes", match: "Neck drainage" },
          { id: 10, term: "Mesenteric nodes", match: "Gut drainage" },
          { id: 11, term: "T cells", match: "Cell immunity" },
          { id: 12, term: "B cells", match: "Antibody production" },
          { id: 13, term: "Macrophages", match: "Phagocytosis" },
          { id: 14, term: "Natural killer", match: "Kill infected cells" },
        ],
        4: [
          { id: 1, term: "Rotator cuff", match: "Shoulder stability" },
          { id: 2, term: "ACL", match: "Knee stability" },
          { id: 3, term: "MCL", match: "Medial knee" },
          { id: 4, term: "Achilles tendon", match: "Calf to heel" },
          { id: 5, term: "Patellar tendon", match: "Knee extension" },
          { id: 6, term: "Plantar fascia", match: "Foot arch" },
          { id: 7, term: "Meniscus", match: "Knee cartilage" },
          { id: 8, term: "Labrum", match: "Joint rim" },
          { id: 9, term: "Synovial membrane", match: "Joint lining" },
          { id: 10, term: "Articular cartilage", match: "Joint surface" },
          { id: 11, term: "Bursa", match: "Friction reduction" },
          { id: 12, term: "Tendon sheath", match: "Tendon protection" },
          { id: 13, term: "Ligament", match: "Bone to bone" },
          { id: 14, term: "Tendon", match: "Muscle to bone" },
        ],
        5: [
          { id: 1, term: "Epidermis", match: "Outer skin layer" },
          { id: 2, term: "Dermis", match: "Middle skin layer" },
          { id: 3, term: "Hypodermis", match: "Fat layer" },
          { id: 4, term: "Hair follicle", match: "Hair root" },
          { id: 5, term: "Sebaceous gland", match: "Oil production" },
          { id: 6, term: "Sweat gland", match: "Temperature control" },
          { id: 7, term: "Melanocytes", match: "Pigment cells" },
          { id: 8, term: "Keratinocytes", match: "Protective cells" },
          { id: 9, term: "Langerhans cells", match: "Skin immunity" },
          { id: 10, term: "Merkel cells", match: "Touch sensation" },
          { id: 11, term: "Arrector pili", match: "Goosebumps" },
          { id: 12, term: "Nail matrix", match: "Nail growth" },
          { id: 13, term: "Stratum corneum", match: "Dead cell layer" },
          { id: 14, term: "Papillary dermis", match: "Fingerprints" },
        ],
        6: [
          { id: 1, term: "Heart", match: "Pumps blood" },
          { id: 2, term: "Lungs", match: "Gas exchange" },
          { id: 3, term: "Liver", match: "Detoxification" },
          { id: 4, term: "Kidneys", match: "Filter waste" },
          { id: 5, term: "Pancreas", match: "Insulin production" },
          { id: 6, term: "Spleen", match: "Blood filter" },
          { id: 7, term: "Gallbladder", match: "Store bile" },
          { id: 8, term: "Stomach", match: "Digest food" },
          { id: 9, term: "Small intestine", match: "Absorb nutrients" },
          { id: 10, term: "Large intestine", match: "Water absorption" },
          { id: 11, term: "Brain", match: "Neural control" },
          { id: 12, term: "Thyroid", match: "Metabolism control" },
          { id: 13, term: "Adrenal glands", match: "Stress hormones" },
          { id: 14, term: "Bone marrow", match: "Blood cell production" },
        ],
        7: [
          { id: 1, term: "LAD artery", match: "Anterior LV wall" },
          { id: 2, term: "Left circumflex", match: "Lateral wall" },
          { id: 3, term: "Right coronary", match: "SA + AV node supply" },
          { id: 4, term: "Posterior descending", match: "Inferior wall" },
          { id: 5, term: "Fossa ovalis", match: "Foramen ovale remnant" },
          {
            id: 6,
            term: "Ligamentum arteriosum",
            match: "Ductus arteriosus remnant",
          },
          {
            id: 7,
            term: "Ligamentum teres hepatis",
            match: "Umbilical vein remnant",
          },
          {
            id: 8,
            term: "Medial umbilical ligament",
            match: "Umbilical artery remnant",
          },
          { id: 9, term: "Urachus", match: "Allantois remnant" },
          {
            id: 10,
            term: "Meckel's diverticulum",
            match: "Vitello-intestinal remnant",
          },
          {
            id: 11,
            term: "Moderator band",
            match: "Right ventricle conduction",
          },
          { id: 12, term: "Bundle of His", match: "AV to bundle branches" },
          {
            id: 13,
            term: "Chordae tendineae",
            match: "Papillary muscle to valve",
          },
          {
            id: 14,
            term: "Annulus fibrosus",
            match: "Electrical insulation ring",
          },
        ],
        8: [
          {
            id: 1,
            term: "Cribriform plate",
            match: "CN I (olfactory nerve fibers)",
          },
          { id: 2, term: "Optic canal", match: "CN II + ophthalmic artery" },
          {
            id: 3,
            term: "Superior orbital fissure",
            match: "CN III, IV, V1, VI + ophthalmic veins",
          },
          { id: 4, term: "Foramen rotundum", match: "CN V2 (maxillary nerve)" },
          {
            id: 5,
            term: "Foramen ovale (skull)",
            match: "CN V3 (mandibular nerve)",
          },
          { id: 6, term: "Foramen spinosum", match: "Middle meningeal artery" },
          {
            id: 7,
            term: "Jugular foramen",
            match: "CN IX, X, XI + internal jugular vein",
          },
          { id: 8, term: "Internal acoustic meatus", match: "CN VII + VIII" },
          {
            id: 9,
            term: "Hypoglossal canal",
            match: "CN XII (hypoglossal nerve)",
          },
          { id: 10, term: "Stylomastoid foramen", match: "CN VII exits skull" },
          {
            id: 11,
            term: "Foramen magnum",
            match: "Medulla + CN XI spinal roots + vertebral arteries",
          },
          { id: 12, term: "Carotid canal", match: "Internal carotid artery" },
          {
            id: 13,
            term: "Foramen lacerum",
            match: "Covered by cartilage; ICA passes over it",
          },
          {
            id: 14,
            term: "Petrotympanic fissure",
            match: "Chorda tympani (CN VII branch) exits",
          },
        ],
        9: [
          {
            id: 1,
            term: "Greater splanchnic (T5-T9)",
            match: "Celiac ganglion → foregut sympathetic",
          },
          {
            id: 2,
            term: "Lesser splanchnic (T10-T11)",
            match: "Superior mesenteric ganglion → midgut",
          },
          {
            id: 3,
            term: "Least splanchnic (T12)",
            match: "Renal ganglion/plexus",
          },
          {
            id: 4,
            term: "Pelvic splanchnic nerves (S2-S4)",
            match: "Parasympathetic to pelvic viscera + hindgut",
          },
          {
            id: 5,
            term: "Vagus nerve (CN X)",
            match: "Parasympathetic to thorax + abdomen to splenic flexure",
          },
          {
            id: 6,
            term: "Hypogastric plexus",
            match: "Pelvic sympathetic; controls ejaculation",
          },
          {
            id: 7,
            term: "Enteric nervous system",
            match: "Meissner's + Auerbach's; autonomous GI control",
          },
          {
            id: 8,
            term: "Horner's syndrome",
            match: "Ptosis + miosis + anhidrosis; sympathetic chain lesion",
          },
          {
            id: 9,
            term: "Argyll Robertson pupil",
            match: "Accommodates but doesn't react to light; syphilis",
          },
          {
            id: 10,
            term: "Adie's tonic pupil",
            match: "Dilated, sluggishly reactive; ciliary ganglion lesion",
          },
          {
            id: 11,
            term: "Celiac plexus",
            match: "Major sympathetic plexus of abdomen; T5-T12",
          },
          {
            id: 12,
            term: "Superior mesenteric plexus",
            match: "Midgut sympathetic control",
          },
          {
            id: 13,
            term: "Sympathetic chain ganglia",
            match: "Paravertebral; T1-L2 preganglionic input",
          },
          {
            id: 14,
            term: "Paravertebral vs prevertebral ganglia",
            match: "Paravertebral = chain; prevertebral = celiac/SMA/IMA",
          },
        ],
        10: [
          {
            id: 1,
            term: "Foregut derivatives",
            match: "Esophagus to proximal duodenum; liver; pancreas; biliary",
          },
          {
            id: 2,
            term: "Midgut derivatives",
            match: "Distal duodenum to splenic flexure of colon",
          },
          {
            id: 3,
            term: "Hindgut derivatives",
            match: "Splenic flexure to upper anal canal (above pectinate line)",
          },
          {
            id: 4,
            term: "Branchial arch 1 (mandibular)",
            match: "Mandible, malleus, incus; CN V3",
          },
          {
            id: 5,
            term: "Branchial arch 2 (hyoid; Reichert's)",
            match: "Stapes, styloid, hyoid lesser cornu; CN VII",
          },
          {
            id: 6,
            term: "Branchial arch 3",
            match: "Hyoid greater cornu; CN IX; stylopharyngeus",
          },
          {
            id: 7,
            term: "Branchial arch 4",
            match: "Laryngeal cartilages; CN X superior laryngeal nerve",
          },
          {
            id: 8,
            term: "Branchial arch 6",
            match:
              "Cricothyroid + intrinsic laryngeal; CN X recurrent laryngeal",
          },
          {
            id: 9,
            term: "Neural crest cells",
            match: "Schwann cells, melanocytes, adrenal medulla, facial bones",
          },
          {
            id: 10,
            term: "Wolffian (mesonephric) duct",
            match:
              "Male reproductive tract: epididymis, vas deferens, seminal vesicle",
          },
          {
            id: 11,
            term: "Müllerian (paramesonephric) duct",
            match: "Female: fallopian tube, uterus, upper vagina",
          },
          {
            id: 12,
            term: "Urogenital sinus",
            match: "Bladder, urethra, prostate (male) / vestibule (female)",
          },
          {
            id: 13,
            term: "Thyroglossal duct",
            match:
              "Thyroid migration path; foramen cecum → neck; duct cyst if persists",
          },
          {
            id: 14,
            term: "Rathke's pouch",
            match: "Anterior pituitary origin (oral ectoderm upward)",
          },
        ],
        11: [
          {
            id: 1,
            term: "Subclavian artery",
            match: "Becomes axillary at 1st rib",
          },
          {
            id: 2,
            term: "Axillary artery",
            match: "Becomes brachial at teres major; 3 parts around pec minor",
          },
          {
            id: 3,
            term: "Brachial artery",
            match: "Divides into radial + ulnar at cubital fossa",
          },
          {
            id: 4,
            term: "Radial artery",
            match: "Lateral forearm; contributes to deep palmar arch",
          },
          {
            id: 5,
            term: "Ulnar artery",
            match:
              "Medial forearm; major contributor to superficial palmar arch",
          },
          {
            id: 6,
            term: "Common iliac arteries",
            match: "L4 aorta bifurcation",
          },
          {
            id: 7,
            term: "External iliac artery",
            match: "Becomes femoral artery at inguinal ligament",
          },
          {
            id: 8,
            term: "Femoral artery",
            match: "Becomes popliteal at adductor hiatus",
          },
          {
            id: 9,
            term: "Popliteal artery",
            match: "Divides into anterior + posterior tibial",
          },
          {
            id: 10,
            term: "Anterior tibial artery",
            match: "Continues as dorsalis pedis on dorsum of foot",
          },
          {
            id: 11,
            term: "Posterior tibial artery",
            match: "Medial ankle; gives fibular/peroneal artery",
          },
          {
            id: 12,
            term: "Profunda femoris (deep femoral)",
            match: "Main blood supply to thigh muscles",
          },
          {
            id: 13,
            term: "Medial femoral circumflex artery",
            match: "Primary supply to femoral head; damaged in neck fracture",
          },
          {
            id: 14,
            term: "Dorsalis pedis",
            match:
              "Continuation of anterior tibial; palpable on dorsum of foot",
          },
        ],
        12: [
          {
            id: 1,
            term: "Right hemidiaphragm",
            match: "Higher than left; liver pushes it up",
          },
          {
            id: 2,
            term: "T8 diaphragm opening",
            match: "IVC + right phrenic nerve",
          },
          {
            id: 3,
            term: "T10 diaphragm opening",
            match: "Esophagus + vagal trunks + left gastric vessels",
          },
          {
            id: 4,
            term: "T12 diaphragm opening",
            match: "Aorta + thoracic duct + azygos vein",
          },
          {
            id: 5,
            term: "Right crus",
            match: "Arises from L1-L3; forms aortic and esophageal hiatuses",
          },
          {
            id: 6,
            term: "Left crus",
            match: "Arises from L1-L2; smaller than right",
          },
          {
            id: 7,
            term: "Central tendon",
            match: "No muscle; fused with pericardium above",
          },
          {
            id: 8,
            term: "Bochdalek hernia",
            match: "Left posterolateral defect; most common CDH; gut in chest",
          },
          {
            id: 9,
            term: "Morgagni hernia",
            match: "Anterior retrosternal defect; right-sided; rare",
          },
          {
            id: 10,
            term: "Sliding hiatal hernia",
            match: "GEJ moves into thorax; most common type; GERD",
          },
          {
            id: 11,
            term: "Paraesophageal hiatal hernia",
            match: "Fundus herniates; GEJ stays; risk of strangulation",
          },
          {
            id: 12,
            term: "Phrenic nerve (C3-C5)",
            match: "Diaphragm motor + pericardium sensory; C4 mainly",
          },
          {
            id: 13,
            term: "Costal part of diaphragm",
            match: "Lower 6 costal cartilages; most of diaphragm mass",
          },
          {
            id: 14,
            term: "Congenital diaphragmatic hernia (left)",
            match: "Bowel + stomach in left thorax; pulmonary hypoplasia",
          },
        ],
        13: [
          {
            id: 1,
            term: "Coronary sinus",
            match: "Receives all cardiac veins; enters right atrium",
          },
          {
            id: 2,
            term: "Great cardiac vein",
            match: "Anterior interventricular groove; drains LV anteriorly",
          },
          {
            id: 3,
            term: "Middle cardiac vein",
            match: "Posterior interventricular groove; drains both ventricles",
          },
          {
            id: 4,
            term: "Small cardiac vein",
            match: "Right atrioventricular groove; drains right heart",
          },
          {
            id: 5,
            term: "Thebesian veins",
            match: "Open directly into cardiac chambers; minor drainage",
          },
          {
            id: 6,
            term: "Crista terminalis",
            match:
              "Ridge in right atrium; border between smooth and trabeculated",
          },
          {
            id: 7,
            term: "Fossa ovalis",
            match: "Interatrial septum; remnant of foramen ovale",
          },
          {
            id: 8,
            term: "Moderator band (right ventricle)",
            match: "Carries right bundle branch across RV",
          },
          {
            id: 9,
            term: "Trabeculae carneae",
            match: "Muscular ridges on inner ventricular walls",
          },
          {
            id: 10,
            term: "Anterior papillary muscle (mitral)",
            match: "Largest; attached to anterior + posterior leaflets",
          },
          {
            id: 11,
            term: "Aortic sinuses of Valsalva",
            match:
              "Three sinuses; right and left give rise to coronary arteries",
          },
          {
            id: 12,
            term: "Sinotubular junction",
            match: "Where aortic root becomes tubular ascending aorta",
          },
          {
            id: 13,
            term: "Transverse pericardial sinus",
            match:
              "Between aorta/PA anteriorly and pulmonary veins/IVC posteriorly",
          },
          {
            id: 14,
            term: "Oblique pericardial sinus",
            match: "Behind left atrium; bounded by pulmonary veins",
          },
        ],
      },
    },
    pharmacology: {
      1: {
        // Level 1 - 6 pairs each
        1: [
          { id: 1, term: "Metformin", match: "Biguanide" },
          { id: 2, term: "Lisinopril", match: "ACE Inhibitor" },
          { id: 3, term: "Atorvastatin", match: "Statin" },
          { id: 4, term: "Omeprazole", match: "PPI" },
          { id: 5, term: "Albuterol", match: "Beta-agonist" },
          { id: 6, term: "Warfarin", match: "Anticoagulant" },
        ],
        2: [
          { id: 1, term: "Aspirin", match: "NSAID" },
          { id: 2, term: "Ibuprofen", match: "COX inhibitor" },
          { id: 3, term: "Acetaminophen", match: "Analgesic" },
          { id: 4, term: "Morphine", match: "Opioid" },
          { id: 5, term: "Codeine", match: "Opioid analgesic" },
          { id: 6, term: "Naproxen", match: "Anti-inflammatory" },
        ],
        3: [
          { id: 1, term: "Amoxicillin", match: "Penicillin" },
          { id: 2, term: "Azithromycin", match: "Macrolide" },
          { id: 3, term: "Ciprofloxacin", match: "Fluoroquinolone" },
          { id: 4, term: "Doxycycline", match: "Tetracycline" },
          { id: 5, term: "Metronidazole", match: "Antiprotozoal" },
          { id: 6, term: "Clindamycin", match: "Lincosamide" },
        ],
        4: [
          { id: 1, term: "Diazepam", match: "Benzodiazepine" },
          { id: 2, term: "Sertraline", match: "SSRI" },
          { id: 3, term: "Fluoxetine", match: "Antidepressant" },
          { id: 4, term: "Quetiapine", match: "Antipsychotic" },
          { id: 5, term: "Lithium", match: "Mood stabilizer" },
          { id: 6, term: "Buspirone", match: "Anxiolytic" },
        ],
        5: [
          { id: 1, term: "Diphenhydramine", match: "Antihistamine" },
          { id: 2, term: "Cetirizine", match: "H1 blocker" },
          { id: 3, term: "Ranitidine", match: "H2 blocker" },
          { id: 4, term: "Montelukast", match: "Leukotriene inhibitor" },
          { id: 5, term: "Prednisone", match: "Corticosteroid" },
          { id: 6, term: "Epinephrine", match: "Anaphylaxis" },
        ],
        6: [
          { id: 1, term: "Penicillin G", match: "Beta-lactam" },
          { id: 2, term: "Azithromycin", match: "Macrolide" },
          { id: 3, term: "Fluoxetine", match: "SSRI" },
          { id: 4, term: "Amlodipine", match: "Ca-channel blocker" },
          { id: 5, term: "Metoprolol", match: "Beta-blocker" },
          { id: 6, term: "Hydrochlorothiazide", match: "Thiazide" },
        ],
        7: [
          { id: 1, term: "Warfarin", match: "Vitamin K antagonist" },
          { id: 2, term: "Heparin", match: "Antithrombin III activator" },
          { id: 3, term: "Digoxin", match: "Na/K ATPase inhibitor" },
          { id: 4, term: "Metformin", match: "Biguanide / AMPK" },
          { id: 5, term: "Aspirin", match: "Irreversible COX inhibitor" },
          { id: 6, term: "Amoxicillin", match: "Aminopenicillin" },
        ],
        8: [
          { id: 1, term: "ACE inhibitor", match: "Dry cough" },
          { id: 2, term: "Statin", match: "Myopathy" },
          { id: 3, term: "Metformin", match: "Lactic acidosis" },
          { id: 4, term: "Amiodarone", match: "Thyroid dysfunction" },
          { id: 5, term: "Lithium", match: "Nephrogenic DI" },
          { id: 6, term: "Clozapine", match: "Agranulocytosis" },
        ],
        9: [
          { id: 1, term: "HTN in pregnancy", match: "Labetalol / methyldopa" },
          { id: 2, term: "Gout acute", match: "NSAIDs / colchicine" },
          { id: 3, term: "H. pylori", match: "Triple therapy" },
          { id: 4, term: "Tuberculosis", match: "RIPE therapy" },
          { id: 5, term: "Anaphylaxis first-line", match: "Epinephrine" },
          { id: 6, term: "Opioid overdose", match: "Naloxone" },
        ],
        10: [
          {
            id: 1,
            term: "Digoxin toxicity risk",
            match: "Monitor K+ and drug levels",
          },
          {
            id: 2,
            term: "Lithium",
            match: "Narrow therapeutic index — monitor levels",
          },
          {
            id: 3,
            term: "Phenytoin",
            match: "Zero-order kinetics — monitor levels",
          },
          { id: 4, term: "Warfarin", match: "Monitor INR" },
          {
            id: 5,
            term: "Aminoglycosides",
            match: "Monitor trough (nephrotoxicity)",
          },
          {
            id: 6,
            term: "Vancomycin",
            match: "Monitor trough (nephrotoxicity)",
          },
        ],
        11: [
          {
            id: 1,
            term: "ACE inhibitors",
            match: "Contraindicated in bilateral RAS",
          },
          {
            id: 2,
            term: "NSAIDs",
            match: "Contraindicated in peptic ulcer disease",
          },
          {
            id: 3,
            term: "Warfarin",
            match: "Contraindicated in pregnancy (teratogenic)",
          },
          {
            id: 4,
            term: "Quinolones",
            match: "Avoid in children (cartilage damage)",
          },
          {
            id: 5,
            term: "Beta-blockers",
            match: "Avoid in cocaine intoxication (unopposed alpha)",
          },
          {
            id: 6,
            term: "Tetracyclines",
            match: "Avoid under age 8 (teeth + bone)",
          },
        ],
        12: [
          { id: 1, term: "Bisphosphonate", match: "Osteoporosis" },
          { id: 2, term: "GLP-1 agonist", match: "Type 2 DM + obesity" },
          {
            id: 3,
            term: "SGLT2 inhibitor",
            match: "Type 2 DM + heart failure",
          },
          {
            id: 4,
            term: "TNF inhibitor",
            match: "Rheumatoid arthritis / Crohn's",
          },
          {
            id: 5,
            term: "Calcineurin inhibitor",
            match: "Transplant rejection prophylaxis",
          },
          {
            id: 6,
            term: "Proton pump inhibitor",
            match: "Peptic ulcer + GERD",
          },
        ],
        13: [
          {
            id: 1,
            term: "Gabapentin",
            match: "Anticonvulsant / neuropathic pain",
          },
          {
            id: 2,
            term: "Phenytoin",
            match: "Antiepileptic / anti-arrhythmic",
          },
          {
            id: 3,
            term: "Levetiracetam",
            match: "Broad-spectrum antiepileptic",
          },
          {
            id: 4,
            term: "Valproate",
            match: "Mood stabilizer / broad antiepileptic",
          },
          {
            id: 5,
            term: "Carbamazepine",
            match: "Trigeminal neuralgia / epilepsy / bipolar",
          },
          {
            id: 6,
            term: "Lamotrigine",
            match: "Epilepsy / bipolar maintenance",
          },
        ],
        14: [
          {
            id: 1,
            term: "Furosemide",
            match: "Loop diuretic (NKCC2 inhibitor)",
          },
          {
            id: 2,
            term: "Hydrochlorothiazide",
            match: "Thiazide diuretic (NCC inhibitor)",
          },
          {
            id: 3,
            term: "Spironolactone",
            match: "Potassium-sparing / aldosterone antagonist",
          },
          {
            id: 4,
            term: "Acetazolamide",
            match: "Carbonic anhydrase inhibitor / altitude sickness",
          },
          {
            id: 5,
            term: "Triamterene",
            match: "Potassium-sparing / ENaC blocker",
          },
          {
            id: 6,
            term: "Mannitol",
            match: "Osmotic diuretic / cerebral edema",
          },
        ],
        15: [
          {
            id: 1,
            term: "Ceftriaxone",
            match: "3rd gen cephalosporin / meningitis / gonorrhea",
          },
          { id: 2, term: "Vancomycin", match: "Glycopeptide / MRSA / C. diff" },
          {
            id: 3,
            term: "Gentamicin",
            match: "Aminoglycoside / gram-negative sepsis",
          },
          { id: 4, term: "Linezolid", match: "Oxazolidinone / MRSA + VRE" },
          {
            id: 5,
            term: "Daptomycin",
            match: "Lipopeptide / gram-positive (not lungs)",
          },
          {
            id: 6,
            term: "Piperacillin-tazobactam",
            match: "Extended-spectrum penicillin + beta-lactamase inhibitor",
          },
        ],
        16: [
          { id: 1, term: "Morphine", match: "Mu-opioid agonist / severe pain" },
          {
            id: 2,
            term: "Naloxone",
            match: "Opioid antagonist / overdose reversal",
          },
          {
            id: 3,
            term: "Buprenorphine",
            match: "Partial mu-opioid agonist / OUD treatment",
          },
          {
            id: 4,
            term: "Methadone",
            match: "Long-acting opioid + NMDA antagonist / OUD",
          },
          {
            id: 5,
            term: "Tramadol",
            match: "Weak opioid + SNRI / mild-moderate pain",
          },
          {
            id: 6,
            term: "Naltrexone",
            match: "Opioid antagonist (oral) / alcohol + opioid dependence",
          },
        ],
        17: [
          {
            id: 1,
            term: "Heparin (UFH)",
            match: "Activates antithrombin III / monitor aPTT",
          },
          {
            id: 2,
            term: "Enoxaparin (LMWH)",
            match: "Anti-Xa activity / no routine monitoring needed",
          },
          {
            id: 3,
            term: "Warfarin",
            match: "Vitamin K antagonist / monitor INR",
          },
          {
            id: 4,
            term: "Apixaban",
            match: "Direct factor Xa inhibitor / oral anticoagulant",
          },
          {
            id: 5,
            term: "Dabigatran",
            match: "Direct thrombin inhibitor / reversal: idarucizumab",
          },
          {
            id: 6,
            term: "Clopidogrel",
            match: "P2Y12 ADP receptor antagonist / antiplatelet",
          },
        ],
        18: [
          {
            id: 1,
            term: "ACE inhibitor (side effect)",
            match: "Dry cough (bradykinin accumulation)",
          },
          {
            id: 2,
            term: "ARB (advantage vs ACEi)",
            match: "No dry cough; blocks AT1 receptor directly",
          },
          {
            id: 3,
            term: "Statin (side effect)",
            match: "Myopathy / rhabdomyolysis (check CK)",
          },
          {
            id: 4,
            term: "Beta-blocker (contraindication)",
            match: "Avoid in acute decompensated asthma / cocaine",
          },
          {
            id: 5,
            term: "Thiazide (side effect)",
            match: "Hypokalemia + hyperuricemia + hyperglycemia",
          },
          {
            id: 6,
            term: "Loop diuretic (side effect)",
            match: "Hypokalemia + ototoxicity (with aminoglycosides)",
          },
        ],
      },
      2: {
        // Level 2 - 8 pairs each
        1: [
          { id: 1, term: "Metformin", match: "Biguanide" },
          { id: 2, term: "Lisinopril", match: "ACE Inhibitor" },
          { id: 3, term: "Atorvastatin", match: "Statin" },
          { id: 4, term: "Omeprazole", match: "PPI" },
          { id: 5, term: "Albuterol", match: "Beta-agonist" },
          { id: 6, term: "Warfarin", match: "Anticoagulant" },
          { id: 7, term: "Amoxicillin", match: "Penicillin" },
          { id: 8, term: "Levothyroxine", match: "Thyroid hormone" },
        ],
        2: [
          { id: 1, term: "Metoprolol", match: "Beta-blocker" },
          { id: 2, term: "Amlodipine", match: "Ca-channel blocker" },
          { id: 3, term: "Losartan", match: "ARB" },
          { id: 4, term: "Hydrochlorothiazide", match: "Thiazide" },
          { id: 5, term: "Furosemide", match: "Loop diuretic" },
          { id: 6, term: "Spironolactone", match: "K-sparing diuretic" },
          { id: 7, term: "Digoxin", match: "Cardiac glycoside" },
          { id: 8, term: "Amiodarone", match: "Antiarrhythmic" },
        ],
        3: [
          { id: 1, term: "Insulin glargine", match: "Long-acting insulin" },
          { id: 2, term: "Insulin lispro", match: "Rapid-acting insulin" },
          { id: 3, term: "Glipizide", match: "Sulfonylurea" },
          { id: 4, term: "Pioglitazone", match: "Thiazolidinedione" },
          { id: 5, term: "Sitagliptin", match: "DPP-4 inhibitor" },
          { id: 6, term: "Empagliflozin", match: "SGLT2 inhibitor" },
          { id: 7, term: "Liraglutide", match: "GLP-1 agonist" },
          { id: 8, term: "Acarbose", match: "Alpha-glucosidase" },
        ],
        4: [
          { id: 1, term: "Gabapentin", match: "Anticonvulsant" },
          { id: 2, term: "Phenytoin", match: "Antiepileptic" },
          { id: 3, term: "Valproic acid", match: "Seizure control" },
          { id: 4, term: "Carbamazepine", match: "Trigeminal neuralgia" },
          { id: 5, term: "Levetiracetam", match: "Epilepsy" },
          { id: 6, term: "Lamotrigine", match: "Bipolar/seizures" },
          { id: 7, term: "Topiramate", match: "Migraine prevention" },
          { id: 8, term: "Pregabalin", match: "Neuropathic pain" },
        ],
        5: [
          { id: 1, term: "Cephalexin", match: "1st gen cephalosporin" },
          { id: 2, term: "Ceftriaxone", match: "3rd gen cephalosporin" },
          { id: 3, term: "Vancomycin", match: "Glycopeptide" },
          { id: 4, term: "Gentamicin", match: "Aminoglycoside" },
          { id: 5, term: "Trimethoprim", match: "Folate inhibitor" },
          { id: 6, term: "Nitrofurantoin", match: "UTI antibiotic" },
          { id: 7, term: "Fluconazole", match: "Antifungal" },
          { id: 8, term: "Acyclovir", match: "Antiviral" },
        ],
        6: [
          { id: 1, term: "Diltiazem", match: "Ca-channel blocker" },
          { id: 2, term: "Propranolol", match: "Beta-blocker" },
          { id: 3, term: "Losartan", match: "ARB" },
          { id: 4, term: "Hydralazine", match: "Vasodilator" },
          { id: 5, term: "Isosorbide", match: "Nitrate" },
          { id: 6, term: "Clopidogrel", match: "Antiplatelet" },
          { id: 7, term: "Apixaban", match: "Factor Xa inhibitor" },
          { id: 8, term: "Heparin", match: "Anticoagulant" },
        ],
        7: [
          { id: 1, term: "Vancomycin", match: "D-Ala-D-Ala binding" },
          { id: 2, term: "Doxycycline", match: "30S — blocks tRNA" },
          { id: 3, term: "Azithromycin", match: "50S — macrolide" },
          { id: 4, term: "Ciprofloxacin", match: "DNA gyrase / topoisomerase" },
          { id: 5, term: "Trimethoprim", match: "Dihydrofolate reductase" },
          { id: 6, term: "Isoniazid", match: "Mycolic acid synthesis" },
          { id: 7, term: "Rifampin", match: "RNA polymerase inhibitor" },
          { id: 8, term: "Metronidazole", match: "Free radical DNA damage" },
        ],
        8: [
          { id: 1, term: "Cisplatin", match: "Nephrotoxicity" },
          { id: 2, term: "Bleomycin", match: "Pulmonary fibrosis" },
          { id: 3, term: "Doxorubicin", match: "Cardiotoxicity" },
          { id: 4, term: "Vincristine", match: "Peripheral neuropathy" },
          { id: 5, term: "Methotrexate", match: "Hepatotoxicity" },
          { id: 6, term: "Cyclophosphamide", match: "Hemorrhagic cystitis" },
          { id: 7, term: "Aminoglycosides", match: "Ototoxicity" },
          { id: 8, term: "Vancomycin infusion", match: "Red man syndrome" },
        ],
        9: [
          { id: 1, term: "Propranolol", match: "Non-selective β-blocker" },
          { id: 2, term: "Atenolol", match: "β1-selective blocker" },
          { id: 3, term: "Prazosin", match: "α1-blocker" },
          { id: 4, term: "Clonidine", match: "α2-agonist" },
          { id: 5, term: "Atropine", match: "Muscarinic antagonist" },
          { id: 6, term: "Pilocarpine", match: "Muscarinic agonist" },
          { id: 7, term: "Physostigmine", match: "AChE inhibitor" },
          { id: 8, term: "Succinylcholine", match: "Nicotinic agonist" },
        ],
        10: [
          { id: 1, term: "MAOIs + SSRIs", match: "Serotonin syndrome" },
          { id: 2, term: "Warfarin + aspirin", match: "Major bleeding risk" },
          {
            id: 3,
            term: "Metformin + IV contrast",
            match: "Lactic acidosis risk",
          },
          {
            id: 4,
            term: "Statins + macrolides",
            match: "Myopathy risk (CYP3A4)",
          },
          { id: 5, term: "Lithium + NSAIDs", match: "Lithium toxicity" },
          { id: 6, term: "Sildenafil + nitrates", match: "Severe hypotension" },
          {
            id: 7,
            term: "Alcohol + metronidazole",
            match: "Disulfiram-like reaction",
          },
          { id: 8, term: "Digoxin + amiodarone", match: "Digoxin toxicity" },
        ],
        11: [
          { id: 1, term: "Clozapine", match: "Weekly CBC — agranulocytosis" },
          { id: 2, term: "Amiodarone", match: "Monitor TFTs + LFTs + CXR" },
          { id: 3, term: "Methotrexate", match: "Folate supplement + LFTs" },
          {
            id: 4,
            term: "Isotretinoin",
            match: "Monthly pregnancy test (teratogenic)",
          },
          { id: 5, term: "Valproate", match: "Monitor ammonia + LFTs" },
          {
            id: 6,
            term: "Tacrolimus",
            match: "Monitor trough levels (nephrotoxicity)",
          },
          {
            id: 7,
            term: "Lithium (long-term)",
            match: "Monitor TSH + creatinine",
          },
          {
            id: 8,
            term: "Carbamazepine",
            match: "Monitor CBC + LFTs (aplastic anemia risk)",
          },
        ],
        12: [
          {
            id: 1,
            term: "CNS penetration (antibiotic)",
            match: "Ceftriaxone, metronidazole, TMP-SMX",
          },
          {
            id: 2,
            term: "Intracellular organisms",
            match: "Azithromycin, doxycycline",
          },
          {
            id: 3,
            term: "Anaerobic coverage",
            match: "Metronidazole, clindamycin",
          },
          {
            id: 4,
            term: "Pseudomonas coverage",
            match: "Piperacillin-tazobactam, ceftazidime",
          },
          {
            id: 5,
            term: "C. difficile treatment",
            match: "Oral vancomycin + fidaxomicin",
          },
          {
            id: 6,
            term: "MRSA (systemic)",
            match: "Vancomycin, linezolid, daptomycin",
          },
          {
            id: 7,
            term: "Fungal (Aspergillus)",
            match: "Voriconazole, amphotericin B",
          },
          {
            id: 8,
            term: "Antiviral (HSV/VZV)",
            match: "Acyclovir (guanosine analog)",
          },
        ],
        13: [
          {
            id: 1,
            term: "Vancomycin MOA",
            match: "Binds D-Ala-D-Ala; blocks transglycosylation",
          },
          {
            id: 2,
            term: "Aminoglycosides MOA",
            match: "Bind 30S; cause mRNA misreading; bactericidal",
          },
          {
            id: 3,
            term: "Tetracyclines MOA",
            match: "Bind 30S; block aminoacyl-tRNA entry to A site",
          },
          {
            id: 4,
            term: "Macrolides MOA",
            match: "Bind 50S; block translocation (23S rRNA)",
          },
          {
            id: 5,
            term: "Fluoroquinolones MOA",
            match:
              "Inhibit DNA gyrase (gram-neg) + topoisomerase IV (gram-pos)",
          },
          {
            id: 6,
            term: "Rifampin MOA",
            match: "Inhibit bacterial RNA polymerase beta subunit",
          },
          {
            id: 7,
            term: "Metronidazole MOA",
            match: "Free radical DNA strand breaks; anaerobes + protozoa",
          },
          {
            id: 8,
            term: "Trimethoprim MOA",
            match: "Inhibit dihydrofolate reductase (DHFR)",
          },
        ],
        14: [
          {
            id: 1,
            term: "Cisplatin toxicity",
            match: "Nephrotoxicity + ototoxicity (prevent with hydration)",
          },
          {
            id: 2,
            term: "Bleomycin toxicity",
            match: "Pulmonary fibrosis (dose-limiting)",
          },
          {
            id: 3,
            term: "Doxorubicin toxicity",
            match: "Cardiotoxicity / dilated cardiomyopathy (dose-limiting)",
          },
          {
            id: 4,
            term: "Vincristine toxicity",
            match: "Peripheral neuropathy (no myelosuppression)",
          },
          {
            id: 5,
            term: "Cyclophosphamide toxicity",
            match: "Hemorrhagic cystitis (prevent with mesna + hydration)",
          },
          {
            id: 6,
            term: "Methotrexate toxicity",
            match: "Mucositis + hepatotoxicity (rescue with leucovorin)",
          },
          {
            id: 7,
            term: "5-Fluorouracil toxicity",
            match: "GI mucositis + myelosuppression",
          },
          {
            id: 8,
            term: "Paclitaxel toxicity",
            match: "Peripheral neuropathy + myelosuppression",
          },
        ],
        15: [
          {
            id: 1,
            term: "Propranolol (non-selective)",
            match: "Blocks beta-1 + beta-2; avoid in asthma",
          },
          {
            id: 2,
            term: "Atenolol / metoprolol",
            match: "Beta-1 selective; safer in reactive airway disease",
          },
          {
            id: 3,
            term: "Carvedilol",
            match: "Non-selective beta + alpha-1 blocker; used in HF",
          },
          {
            id: 4,
            term: "Prazosin / tamsulosin",
            match: "Alpha-1 blocker; BPH + hypertension",
          },
          {
            id: 5,
            term: "Clonidine",
            match: "Alpha-2 agonist; decreases sympathetic outflow",
          },
          {
            id: 6,
            term: "Phenylephrine",
            match: "Pure alpha-1 agonist; causes reflex bradycardia",
          },
          {
            id: 7,
            term: "Dobutamine",
            match: "Beta-1 agonist; increases contractility (stress test)",
          },
          {
            id: 8,
            term: "Epinephrine (high dose)",
            match: "Alpha-1 + beta-1 + beta-2; anaphylaxis first-line",
          },
        ],
        16: [
          {
            id: 1,
            term: "Acetaminophen overdose antidote",
            match: "N-acetylcysteine (replenishes glutathione)",
          },
          {
            id: 2,
            term: "Opioid overdose antidote",
            match: "Naloxone (competitive opioid receptor antagonist)",
          },
          {
            id: 3,
            term: "Benzodiazepine overdose antidote",
            match: "Flumazenil (GABA-A competitive antagonist)",
          },
          {
            id: 4,
            term: "Heparin overdose antidote",
            match: "Protamine sulfate",
          },
          {
            id: 5,
            term: "Warfarin overdose antidote",
            match: "Vitamin K (slow) + fresh frozen plasma (urgent)",
          },
          {
            id: 6,
            term: "Beta-blocker toxicity antidote",
            match: "Glucagon (bypasses receptor; raises cAMP)",
          },
          {
            id: 7,
            term: "Digoxin toxicity antidote",
            match: "Digoxin-specific Fab antibody fragments",
          },
          {
            id: 8,
            term: "Organophosphate antidote",
            match: "Atropine + pralidoxime (2-PAM; before aging)",
          },
        ],
        17: [
          {
            id: 1,
            term: "CYP3A4 inducers",
            match: "Rifampin, phenytoin, carbamazepine, St. John's wort",
          },
          {
            id: 2,
            term: "CYP3A4 inhibitors",
            match:
              "Azole antifungals, macrolides, protease inhibitors, grapefruit",
          },
          {
            id: 3,
            term: "CYP2D6 inhibitors",
            match: "Fluoxetine, paroxetine → increase tricyclic/codeine levels",
          },
          {
            id: 4,
            term: "Warfarin + rifampin",
            match: "Rifampin induces CYP2C9 → decreased warfarin effect",
          },
          {
            id: 5,
            term: "Clopidogrel + omeprazole",
            match: "CYP2C19 inhibition reduces clopidogrel activation",
          },
          {
            id: 6,
            term: "Azathioprine + allopurinol",
            match: "Xanthine oxidase inhibition → azathioprine toxicity",
          },
          {
            id: 7,
            term: "Digoxin + amiodarone",
            match: "Amiodarone inhibits P-gp + CYP → raised digoxin level",
          },
          {
            id: 8,
            term: "Theophylline + ciprofloxacin",
            match: "CYP1A2 inhibition → theophylline toxicity",
          },
        ],
        18: [
          {
            id: 1,
            term: "Tetracyclines in pregnancy",
            match: "Teeth discoloration + bone growth inhibition; avoid",
          },
          {
            id: 2,
            term: "ACE inhibitors (2nd/3rd trimester)",
            match: "Fetal renal defects + oligohydramnios",
          },
          {
            id: 3,
            term: "NSAIDs (3rd trimester)",
            match: "Premature ductus arteriosus closure",
          },
          {
            id: 4,
            term: "Warfarin (1st trimester)",
            match:
              "Warfarin embryopathy; nasal hypoplasia; use heparin instead",
          },
          {
            id: 5,
            term: "Fluoroquinolones in children",
            match: "Cartilage damage; avoid under age 18",
          },
          {
            id: 6,
            term: "Aspirin in children with viral illness",
            match: "Reye's syndrome (hepatic failure + encephalopathy)",
          },
          {
            id: 7,
            term: "Chloramphenicol in neonates",
            match: "Gray baby syndrome (no UDP-glucuronosyltransferase)",
          },
          {
            id: 8,
            term: "Valproate in pregnancy",
            match: "Neural tube defects; supplement folic acid",
          },
        ],
      },
      3: {
        // Level 3 - 10 pairs each
        1: [
          { id: 1, term: "Metformin", match: "Biguanide" },
          { id: 2, term: "Lisinopril", match: "ACE Inhibitor" },
          { id: 3, term: "Atorvastatin", match: "Statin" },
          { id: 4, term: "Omeprazole", match: "PPI" },
          { id: 5, term: "Albuterol", match: "Beta-agonist" },
          { id: 6, term: "Warfarin", match: "Anticoagulant" },
          { id: 7, term: "Amoxicillin", match: "Penicillin" },
          { id: 8, term: "Levothyroxine", match: "Thyroid hormone" },
          { id: 9, term: "Prednisone", match: "Corticosteroid" },
          { id: 10, term: "Gabapentin", match: "Anticonvulsant" },
        ],
        2: [
          { id: 1, term: "Heparin", match: "Parenteral anticoagulant" },
          { id: 2, term: "Enoxaparin", match: "LMWH" },
          { id: 3, term: "Rivaroxaban", match: "Factor Xa inhibitor" },
          { id: 4, term: "Dabigatran", match: "Direct thrombin" },
          { id: 5, term: "Clopidogrel", match: "P2Y12 inhibitor" },
          { id: 6, term: "Ticagrelor", match: "Antiplatelet" },
          { id: 7, term: "Alteplase", match: "Thrombolytic" },
          { id: 8, term: "Tranexamic acid", match: "Antifibrinolytic" },
          { id: 9, term: "Vitamin K", match: "Warfarin reversal" },
          { id: 10, term: "Protamine", match: "Heparin reversal" },
        ],
        3: [
          { id: 1, term: "Ondansetron", match: "5-HT3 antagonist" },
          { id: 2, term: "Metoclopramide", match: "Prokinetic" },
          { id: 3, term: "Promethazine", match: "Antiemetic" },
          { id: 4, term: "Sucralfate", match: "Mucosal protectant" },
          { id: 5, term: "Misoprostol", match: "Prostaglandin analog" },
          { id: 6, term: "Bismuth", match: "H. pylori treatment" },
          { id: 7, term: "Lactulose", match: "Osmotic laxative" },
          { id: 8, term: "Loperamide", match: "Antidiarrheal" },
          { id: 9, term: "Docusate", match: "Stool softener" },
          { id: 10, term: "Polyethylene glycol", match: "Bowel prep" },
        ],
        4: [
          { id: 1, term: "Propofol", match: "IV anesthetic" },
          { id: 2, term: "Ketamine", match: "Dissociative" },
          { id: 3, term: "Midazolam", match: "Pre-anesthetic" },
          { id: 4, term: "Fentanyl", match: "Potent opioid" },
          { id: 5, term: "Succinylcholine", match: "Depolarizing blocker" },
          { id: 6, term: "Rocuronium", match: "Non-depolarizing" },
          { id: 7, term: "Neostigmine", match: "Reversal agent" },
          { id: 8, term: "Lidocaine", match: "Local anesthetic" },
          { id: 9, term: "Bupivacaine", match: "Long-acting local" },
          { id: 10, term: "Sevoflurane", match: "Inhaled anesthetic" },
        ],
        5: [
          { id: 1, term: "Methotrexate", match: "DMARD" },
          { id: 2, term: "Adalimumab", match: "TNF inhibitor" },
          { id: 3, term: "Infliximab", match: "Anti-TNF" },
          { id: 4, term: "Rituximab", match: "Anti-CD20" },
          { id: 5, term: "Etanercept", match: "TNF receptor" },
          { id: 6, term: "Hydroxychloroquine", match: "Antimalarial/RA" },
          { id: 7, term: "Sulfasalazine", match: "IBD/RA drug" },
          { id: 8, term: "Leflunomide", match: "Pyrimidine inhibitor" },
          { id: 9, term: "Cyclosporine", match: "Calcineurin inhibitor" },
          { id: 10, term: "Azathioprine", match: "Purine analog" },
        ],
        6: [
          { id: 1, term: "Fluoxetine", match: "SSRI" },
          { id: 2, term: "Venlafaxine", match: "SNRI" },
          { id: 3, term: "Bupropion", match: "NDRI" },
          { id: 4, term: "Trazodone", match: "SARI" },
          { id: 5, term: "Haloperidol", match: "Typical antipsychotic" },
          { id: 6, term: "Olanzapine", match: "Atypical antipsychotic" },
          { id: 7, term: "Lorazepam", match: "Benzodiazepine" },
          { id: 8, term: "Buspirone", match: "Anxiolytic" },
          { id: 9, term: "Lithium", match: "Mood stabilizer" },
          { id: 10, term: "Valproate", match: "Mood stabilizer" },
        ],
        7: [
          { id: 1, term: "Propranolol", match: "Non-selective β-blocker" },
          { id: 2, term: "Metoprolol", match: "β1-selective blocker" },
          { id: 3, term: "Labetalol", match: "α + β blocker" },
          { id: 4, term: "Verapamil", match: "Non-DHP CCB" },
          { id: 5, term: "Amlodipine", match: "DHP calcium blocker" },
          { id: 6, term: "Hydralazine", match: "Direct arteriolar dilator" },
          { id: 7, term: "Nitrates", match: "Venodilator via NO" },
          { id: 8, term: "Spironolactone", match: "Aldosterone antagonist" },
          { id: 9, term: "Furosemide", match: "Loop diuretic — NKCC2" },
          { id: 10, term: "Hydrochlorothiazide", match: "NCC inhibitor" },
        ],
        8: [
          { id: 1, term: "MRSA", match: "Vancomycin" },
          { id: 2, term: "VRE", match: "Linezolid / daptomycin" },
          { id: 3, term: "ESBL producers", match: "Carbapenems" },
          {
            id: 4,
            term: "Pseudomonas aeruginosa",
            match: "Piperacillin-tazobactam",
          },
          {
            id: 5,
            term: "Atypical organisms",
            match: "Azithromycin / doxycycline",
          },
          { id: 6, term: "Anaerobes", match: "Metronidazole" },
          { id: 7, term: "PCP pneumonia", match: "TMP-SMX" },
          { id: 8, term: "CMV retinitis", match: "Ganciclovir" },
          {
            id: 9,
            term: "Toxoplasmosis",
            match: "Pyrimethamine + sulfadiazine",
          },
          { id: 10, term: "Fungal meningitis", match: "Amphotericin B" },
        ],
        9: [
          {
            id: 1,
            term: "First-pass metabolism",
            match: "Reduces oral bioavailability",
          },
          { id: 2, term: "Half-life", match: "Time for 50% drug elimination" },
          {
            id: 3,
            term: "Volume of distribution",
            match: "Dose / plasma concentration",
          },
          { id: 4, term: "Clearance", match: "Rate of drug elimination" },
          {
            id: 5,
            term: "Zero-order kinetics",
            match: "Constant amount removed",
          },
          {
            id: 6,
            term: "First-order kinetics",
            match: "Constant fraction removed",
          },
          { id: 7, term: "CYP450 inducers", match: "Rifampin & carbamazepine" },
          {
            id: 8,
            term: "CYP450 inhibitors",
            match: "Fluconazole & cimetidine",
          },
          { id: 9, term: "Protein binding", match: "Warfarin binds albumin" },
          { id: 10, term: "Loading dose", match: "Vd × target concentration" },
        ],
        10: [
          {
            id: 1,
            term: "MRSA resistance mechanism",
            match: "Modified PBP2a (mecA gene)",
          },
          {
            id: 2,
            term: "ESBL resistance",
            match: "Extended-spectrum beta-lactamase",
          },
          {
            id: 3,
            term: "Vancomycin resistance (VRE)",
            match: "D-Ala-D-Lac substitution",
          },
          {
            id: 4,
            term: "Aminoglycoside resistance",
            match: "Modifying enzymes (acetyltransferase)",
          },
          {
            id: 5,
            term: "Macrolide resistance",
            match: "23S rRNA methylation (erm gene)",
          },
          {
            id: 6,
            term: "Fluoroquinolone resistance",
            match: "DNA gyrase mutation",
          },
          { id: 7, term: "Isoniazid resistance", match: "katG gene mutation" },
          { id: 8, term: "Rifampin resistance", match: "rpoB gene mutation" },
          {
            id: 9,
            term: "Chloramphenicol resistance",
            match: "Acetyltransferase enzyme",
          },
          {
            id: 10,
            term: "Efflux pumps",
            match: "Multi-drug resistance mechanism",
          },
        ],
        11: [
          {
            id: 1,
            term: "Acetazolamide",
            match: "Carbonic anhydrase inhibitor",
          },
          { id: 2, term: "Allopurinol", match: "Xanthine oxidase inhibitor" },
          {
            id: 3,
            term: "Colchicine",
            match: "Microtubule polymerization inhibitor",
          },
          {
            id: 4,
            term: "Aspirin (low-dose)",
            match: "Irreversible COX-1 — inhibits TXA2",
          },
          {
            id: 5,
            term: "Chloroquine",
            match: "Heme polymerization inhibitor",
          },
          {
            id: 6,
            term: "Ivermectin",
            match: "GABA-gated Cl⁻ channel agonist",
          },
          {
            id: 7,
            term: "Albendazole",
            match: "Tubulin polymerization inhibitor",
          },
          {
            id: 8,
            term: "Dantrolene",
            match: "RyR1 (ryanodine receptor) blocker",
          },
          {
            id: 9,
            term: "Dapsone",
            match: "Dihydropteroate synthase inhibitor",
          },
          {
            id: 10,
            term: "Amlodipine",
            match: "L-type calcium channel blocker",
          },
        ],
        12: [
          {
            id: 1,
            term: "Varenicline",
            match: "Partial α4β2 nicotinic agonist (smoking cessation)",
          },
          { id: 2, term: "Buprenorphine", match: "Partial μ-opioid agonist" },
          { id: 3, term: "Naltrexone", match: "μ-opioid antagonist (oral)" },
          { id: 4, term: "Aripiprazole", match: "Partial D2 + 5HT1A agonist" },
          {
            id: 5,
            term: "Clozapine receptor profile",
            match: "D4 + 5HT2A high affinity",
          },
          {
            id: 6,
            term: "Ondansetron",
            match: "5-HT3 antagonist (antiemetic)",
          },
          { id: 7, term: "Sumatriptan", match: "5-HT1B/1D agonist (triptan)" },
          {
            id: 8,
            term: "Metoclopramide",
            match: "D2 antagonist (prokinetic)",
          },
          { id: 9, term: "Cabergoline", match: "D2 agonist (prolactinoma)" },
          {
            id: 10,
            term: "Buspirone mechanism",
            match: "5-HT1A partial agonist (anxiolytic)",
          },
        ],
        13: [
          {
            id: 1,
            term: "Cyclosporine",
            match: "Calcineurin inhibitor; inhibits IL-2; nephrotoxic",
          },
          {
            id: 2,
            term: "Tacrolimus (FK506)",
            match:
              "Calcineurin inhibitor via FKBP12; more potent than cyclosporine",
          },
          {
            id: 3,
            term: "Sirolimus (rapamycin)",
            match:
              "mTOR inhibitor; not nephrotoxic; inhibits T cell proliferation",
          },
          {
            id: 4,
            term: "Mycophenolate mofetil",
            match:
              "IMPDH inhibitor; blocks purine synthesis; lymphocyte-specific",
          },
          {
            id: 5,
            term: "Azathioprine",
            match:
              "Purine analog; metabolized by xanthine oxidase; hepatotoxic",
          },
          {
            id: 6,
            term: "Basiliximab / daclizumab",
            match: "Anti-CD25 (IL-2Rα); induction therapy",
          },
          {
            id: 7,
            term: "Rituximab",
            match: "Anti-CD20; B-cell depletion; RA + lymphoma",
          },
          {
            id: 8,
            term: "TNF inhibitors (infliximab, adalimumab)",
            match: "Anti-TNFα; RA + Crohn's + ankylosing spondylitis",
          },
          {
            id: 9,
            term: "Eculizumab",
            match:
              "Anti-C5 complement; paroxysmal nocturnal hemoglobinuria + aHUS",
          },
          {
            id: 10,
            term: "Belimumab",
            match: "Anti-BLyS; reduces B-cell survival; SLE treatment",
          },
        ],
        14: [
          {
            id: 1,
            term: "Imatinib (Gleevec)",
            match: "BCR-ABL tyrosine kinase inhibitor; CML first-line",
          },
          {
            id: 2,
            term: "Trastuzumab (Herceptin)",
            match: "Anti-HER2 monoclonal Ab; HER2+ breast cancer",
          },
          {
            id: 3,
            term: "Cetuximab / panitumumab",
            match: "Anti-EGFR; colorectal cancer (KRAS wild-type only)",
          },
          {
            id: 4,
            term: "Bevacizumab (Avastin)",
            match: "Anti-VEGF; inhibits angiogenesis; multiple cancers",
          },
          {
            id: 5,
            term: "Pembrolizumab / nivolumab",
            match: "Anti-PD-1 checkpoint inhibitor; multiple solid tumors",
          },
          {
            id: 6,
            term: "Ipilimumab",
            match: "Anti-CTLA-4; enhances T-cell activation; melanoma",
          },
          {
            id: 7,
            term: "Bortezomib",
            match: "Proteasome inhibitor; multiple myeloma",
          },
          { id: 8, term: "Venetoclax", match: "BCL-2 inhibitor; CLL + AML" },
          {
            id: 9,
            term: "Ibrutinib",
            match: "BTK inhibitor; CLL + mantle cell lymphoma",
          },
          {
            id: 10,
            term: "BRAF inhibitors (vemurafenib)",
            match: "BRAF V600E mutation; melanoma + papillary thyroid",
          },
        ],
        15: [
          {
            id: 1,
            term: "Acyclovir MOA",
            match:
              "Viral thymidine kinase phosphorylates; inhibits DNA polymerase",
          },
          {
            id: 2,
            term: "Ganciclovir MOA",
            match: "UL97 kinase phosphorylates; CMV DNA polymerase inhibitor",
          },
          {
            id: 3,
            term: "Oseltamivir MOA",
            match: "Neuraminidase inhibitor; prevents influenza viral release",
          },
          {
            id: 4,
            term: "Tenofovir (TDF/TAF)",
            match: "Nucleotide reverse transcriptase inhibitor; HIV + HBV",
          },
          {
            id: 5,
            term: "Efavirenz",
            match: "NNRTI; CNS side effects + teratogenic (FDA category D)",
          },
          {
            id: 6,
            term: "Raltegravir / dolutegravir",
            match: "Integrase strand transfer inhibitors; HIV",
          },
          {
            id: 7,
            term: "Sofosbuvir",
            match: "NS5B polymerase inhibitor; pan-genotypic HCV treatment",
          },
          {
            id: 8,
            term: "Foscarnet",
            match:
              "Directly inhibits viral DNA polymerase; no phosphorylation required",
          },
          {
            id: 9,
            term: "Maraviroc",
            match: "CCR5 antagonist; tropism test required; HIV",
          },
          {
            id: 10,
            term: "Amphotericin B MOA",
            match: "Binds ergosterol; disrupts fungal membrane",
          },
        ],
        16: [
          {
            id: 1,
            term: "Digoxin MOA",
            match: "Na/K-ATPase inhibitor → ↑ intracellular Ca2+",
          },
          {
            id: 2,
            term: "Amiodarone toxicities",
            match:
              "Pulmonary fibrosis, thyroid dysfunction, corneal deposits, hepatotoxicity",
          },
          {
            id: 3,
            term: "Adenosine MOA",
            match: "A1 receptor; slows AV node; very short-acting",
          },
          {
            id: 4,
            term: "Nitrates MOA",
            match: "Donate NO → ↑ cGMP → smooth muscle relaxation; preload ↓",
          },
          {
            id: 5,
            term: "Hydralazine",
            match:
              "Arteriolar vasodilator; causes reflex tachycardia; drug-induced lupus",
          },
          {
            id: 6,
            term: "Ivabradine",
            match:
              "If (funny current) inhibitor; reduces heart rate; no inotropy",
          },
          {
            id: 7,
            term: "Sacubitril/valsartan (ARNI)",
            match: "Neprilysin inhibitor + ARB; HFrEF treatment",
          },
          {
            id: 8,
            term: "Eplerenone",
            match: "Selective aldosterone antagonist; MI + heart failure",
          },
          {
            id: 9,
            term: "Milrinone",
            match:
              "PDE-3 inhibitor; ↑ cAMP → inotropy + vasodilation; acute HF",
          },
          {
            id: 10,
            term: "Nesiritide",
            match:
              "Recombinant BNP; natriuretic + vasodilator; acute decompensated HF",
          },
        ],
        17: [
          {
            id: 1,
            term: "First-order kinetics",
            match: "Constant fraction eliminated per unit time; most drugs",
          },
          {
            id: 2,
            term: "Zero-order kinetics",
            match:
              "Constant amount eliminated; phenytoin, ethanol, aspirin (high dose)",
          },
          {
            id: 3,
            term: "Volume of distribution (Vd)",
            match: "High Vd = lipid-soluble, tissue-bound drugs",
          },
          {
            id: 4,
            term: "Loading dose formula",
            match: "LD = Vd × target Cp / bioavailability",
          },
          {
            id: 5,
            term: "Maintenance dose formula",
            match: "MD = Cl × target Cp / bioavailability",
          },
          { id: 6, term: "Half-life formula", match: "t½ = 0.693 × Vd / Cl" },
          {
            id: 7,
            term: "Steady state reached",
            match: "~5 half-lives; depends on t½ only, not dose",
          },
          {
            id: 8,
            term: "Bioavailability",
            match: "Fraction reaching systemic circulation; IV = 100%",
          },
          {
            id: 9,
            term: "First-pass metabolism",
            match:
              "High extraction = low oral bioavailability (morphine, propranolol)",
          },
          {
            id: 10,
            term: "Therapeutic index",
            match: "TD50 / ED50; narrow index = frequent monitoring needed",
          },
        ],
        18: [
          {
            id: 1,
            term: "Community-acquired pneumonia (outpatient)",
            match: "Azithromycin or doxycycline (first-line)",
          },
          {
            id: 2,
            term: "CAP (inpatient, non-ICU)",
            match: "Beta-lactam + macrolide or respiratory fluoroquinolone",
          },
          {
            id: 3,
            term: "MRSA severe infection",
            match: "Vancomycin (first-line) or linezolid/daptomycin",
          },
          {
            id: 4,
            term: "Status epilepticus first-line",
            match: "IV lorazepam → fosphenytoin if seizure continues",
          },
          {
            id: 5,
            term: "Pulmonary TB (standard)",
            match: "RIPE × 2 months → RI × 4 months",
          },
          {
            id: 6,
            term: "Hypertensive emergency in pregnancy",
            match: "IV labetalol or hydralazine",
          },
          {
            id: 7,
            term: "Anaphylaxis first-line",
            match: "Epinephrine IM (anterolateral thigh)",
          },
          {
            id: 8,
            term: "C. difficile (mild-moderate)",
            match: "Oral vancomycin or fidaxomicin (NOT metronidazole)",
          },
          {
            id: 9,
            term: "CML first-line",
            match: "Imatinib (BCR-ABL tyrosine kinase inhibitor)",
          },
          {
            id: 10,
            term: "Alcohol use disorder (withdrawal prevention)",
            match: "Benzodiazepines (chlordiazepoxide or lorazepam)",
          },
        ],
      },
      4: {
        // Level 4 - 12 pairs each
        1: [
          { id: 1, term: "Metformin", match: "Biguanide" },
          { id: 2, term: "Lisinopril", match: "ACE Inhibitor" },
          { id: 3, term: "Atorvastatin", match: "Statin" },
          { id: 4, term: "Omeprazole", match: "PPI" },
          { id: 5, term: "Albuterol", match: "Beta-agonist" },
          { id: 6, term: "Warfarin", match: "Anticoagulant" },
          { id: 7, term: "Amoxicillin", match: "Penicillin" },
          { id: 8, term: "Levothyroxine", match: "Thyroid hormone" },
          { id: 9, term: "Prednisone", match: "Corticosteroid" },
          { id: 10, term: "Gabapentin", match: "Anticonvulsant" },
          { id: 11, term: "Losartan", match: "ARB" },
          { id: 12, term: "Amlodipine", match: "Ca-channel blocker" },
        ],
        2: [
          { id: 1, term: "Tamoxifen", match: "SERM" },
          { id: 2, term: "Letrozole", match: "Aromatase inhibitor" },
          { id: 3, term: "Trastuzumab", match: "HER2 antibody" },
          { id: 4, term: "Imatinib", match: "Tyrosine kinase" },
          { id: 5, term: "Pembrolizumab", match: "PD-1 inhibitor" },
          { id: 6, term: "Cisplatin", match: "Platinum agent" },
          { id: 7, term: "Doxorubicin", match: "Anthracycline" },
          { id: 8, term: "Paclitaxel", match: "Taxane" },
          { id: 9, term: "Vincristine", match: "Vinca alkaloid" },
          { id: 10, term: "Cyclophosphamide", match: "Alkylating agent" },
          { id: 11, term: "5-Fluorouracil", match: "Antimetabolite" },
          { id: 12, term: "Bleomycin", match: "Antitumor antibiotic" },
        ],
        3: [
          { id: 1, term: "Fluticasone", match: "Inhaled steroid" },
          { id: 2, term: "Salmeterol", match: "LABA" },
          { id: 3, term: "Tiotropium", match: "LAMA" },
          { id: 4, term: "Ipratropium", match: "SAMA" },
          { id: 5, term: "Theophylline", match: "Methylxanthine" },
          { id: 6, term: "Roflumilast", match: "PDE4 inhibitor" },
          { id: 7, term: "Omalizumab", match: "Anti-IgE" },
          { id: 8, term: "Mepolizumab", match: "Anti-IL5" },
          { id: 9, term: "Cromolyn", match: "Mast cell stabilizer" },
          { id: 10, term: "Zafirlukast", match: "Leukotriene antagonist" },
          { id: 11, term: "Guaifenesin", match: "Expectorant" },
          { id: 12, term: "Dextromethorphan", match: "Antitussive" },
        ],
        4: [
          { id: 1, term: "Levodopa", match: "Dopamine precursor" },
          { id: 2, term: "Carbidopa", match: "DOPA decarboxylase" },
          { id: 3, term: "Pramipexole", match: "Dopamine agonist" },
          { id: 4, term: "Selegiline", match: "MAO-B inhibitor" },
          { id: 5, term: "Entacapone", match: "COMT inhibitor" },
          { id: 6, term: "Amantadine", match: "NMDA antagonist" },
          { id: 7, term: "Benztropine", match: "Anticholinergic" },
          { id: 8, term: "Donepezil", match: "Cholinesterase" },
          { id: 9, term: "Memantine", match: "NMDA blocker" },
          { id: 10, term: "Rivastigmine", match: "AChE inhibitor" },
          { id: 11, term: "Riluzole", match: "ALS drug" },
          { id: 12, term: "Tetrabenazine", match: "VMAT2 inhibitor" },
        ],
        5: [
          { id: 1, term: "Sildenafil", match: "PDE5 inhibitor" },
          { id: 2, term: "Tamsulosin", match: "Alpha-1 blocker" },
          { id: 3, term: "Finasteride", match: "5-alpha reductase" },
          { id: 4, term: "Oxybutynin", match: "Antimuscarinic" },
          { id: 5, term: "Desmopressin", match: "ADH analog" },
          { id: 6, term: "Tadalafil", match: "Long-acting PDE5" },
          { id: 7, term: "Dutasteride", match: "5AR inhibitor" },
          { id: 8, term: "Solifenacin", match: "OAB drug" },
          { id: 9, term: "Mirabegron", match: "Beta-3 agonist" },
          { id: 10, term: "Tolterodine", match: "Bladder relaxant" },
          { id: 11, term: "Alfuzosin", match: "BPH treatment" },
          { id: 12, term: "Phenazopyridine", match: "Urinary analgesic" },
        ],
        6: [
          { id: 1, term: "Heparin", match: "Anticoagulant" },
          { id: 2, term: "Warfarin", match: "Vitamin K antagonist" },
          { id: 3, term: "Dabigatran", match: "Direct thrombin" },
          { id: 4, term: "Rivaroxaban", match: "Factor Xa inhibitor" },
          { id: 5, term: "Clopidogrel", match: "P2Y12 inhibitor" },
          { id: 6, term: "Aspirin", match: "COX inhibitor" },
          { id: 7, term: "Alteplase", match: "Thrombolytic" },
          { id: 8, term: "Protamine", match: "Heparin reversal" },
          { id: 9, term: "Vitamin K", match: "Warfarin reversal" },
          { id: 10, term: "Tranexamic acid", match: "Antifibrinolytic" },
          { id: 11, term: "Dipyridamole", match: "Antiplatelet" },
          { id: 12, term: "Ticagrelor", match: "Antiplatelet" },
        ],
        7: [
          { id: 1, term: "Naloxone", match: "Opioid antagonist" },
          { id: 2, term: "Flumazenil", match: "Benzodiazepine antagonist" },
          { id: 3, term: "Acetylcysteine", match: "Paracetamol antidote" },
          { id: 4, term: "Atropine", match: "Muscarinic antagonist" },
          { id: 5, term: "Pralidoxime", match: "Organophosphate antidote" },
          { id: 6, term: "Physostigmine", match: "Anticholinergic antidote" },
          { id: 7, term: "Ketamine", match: "NMDA dissociative" },
          { id: 8, term: "Propofol", match: "GABA-A potentiator" },
          { id: 9, term: "Succinylcholine", match: "Depolarizing NMJ blocker" },
          { id: 10, term: "Rocuronium", match: "Non-depolarizing NMJ" },
          { id: 11, term: "Neostigmine", match: "AChE inhibitor — reversal" },
          { id: 12, term: "Dantrolene", match: "Malignant hyperthermia Rx" },
        ],
        8: [
          { id: 1, term: "Imatinib", match: "BCR-ABL inhibitor (CML)" },
          {
            id: 2,
            term: "Trastuzumab",
            match: "HER2 monoclonal Ab (breast Ca)",
          },
          { id: 3, term: "Rituximab", match: "Anti-CD20 (B cell lymphoma)" },
          {
            id: 4,
            term: "Cetuximab",
            match: "Anti-EGFR (colorectal + head/neck)",
          },
          {
            id: 5,
            term: "Bevacizumab",
            match: "Anti-VEGF (angiogenesis inhibitor)",
          },
          {
            id: 6,
            term: "Ipilimumab",
            match: "Anti-CTLA-4 checkpoint inhibitor",
          },
          {
            id: 7,
            term: "Pembrolizumab",
            match: "Anti-PD-1 checkpoint inhibitor",
          },
          {
            id: 8,
            term: "Atezolizumab",
            match: "Anti-PD-L1 checkpoint inhibitor",
          },
          {
            id: 9,
            term: "Bortezomib",
            match: "26S proteasome inhibitor (myeloma)",
          },
          { id: 10, term: "Olaparib", match: "PARP inhibitor (BRCA+ tumors)" },
          {
            id: 11,
            term: "Palbociclib",
            match: "CDK4/6 inhibitor (HR+ breast Ca)",
          },
          {
            id: 12,
            term: "Vemurafenib",
            match: "BRAF V600E inhibitor (melanoma)",
          },
        ],
        9: [
          {
            id: 1,
            term: "Cisplatin toxicity",
            match: "Nephrotoxicity + ototoxicity",
          },
          { id: 2, term: "Bleomycin toxicity", match: "Pulmonary fibrosis" },
          {
            id: 3,
            term: "Vincristine toxicity",
            match: "Peripheral neuropathy",
          },
          {
            id: 4,
            term: "Doxorubicin toxicity",
            match: "Dilated cardiomyopathy",
          },
          {
            id: 5,
            term: "Cyclophosphamide toxicity",
            match: "Hemorrhagic cystitis (→ mesna)",
          },
          {
            id: 6,
            term: "Busulfan toxicity",
            match: "Pulmonary fibrosis + hyperpigmentation",
          },
          {
            id: 7,
            term: "Methotrexate toxicity",
            match: "Mucositis + hepatotoxicity",
          },
          {
            id: 8,
            term: "5-Fluorouracil toxicity",
            match: "Myelosuppression + mucositis",
          },
          {
            id: 9,
            term: "Paclitaxel toxicity",
            match: "Peripheral neuropathy + alopecia",
          },
          {
            id: 10,
            term: "Ifosfamide toxicity",
            match: "Hemorrhagic cystitis + encephalopathy",
          },
          {
            id: 11,
            term: "Asparaginase toxicity",
            match: "Pancreatitis + coagulopathy",
          },
          {
            id: 12,
            term: "Tamoxifen toxicity",
            match: "Endometrial cancer + DVT risk",
          },
        ],
        10: [
          { id: 1, term: "MRSA", match: "Vancomycin, linezolid, daptomycin" },
          { id: 2, term: "VRE", match: "Linezolid, daptomycin" },
          {
            id: 3,
            term: "CRE (carbapenem-resistant)",
            match: "Colistin, tigecycline",
          },
          {
            id: 4,
            term: "Pseudomonas aeruginosa",
            match: "Pip-tazo, ceftazidime, cefepime, ciprofloxacin",
          },
          {
            id: 5,
            term: "C. difficile",
            match: "Oral vancomycin + fidaxomicin",
          },
          {
            id: 6,
            term: "Candida (systemic)",
            match: "Echinocandins (caspofungin)",
          },
          {
            id: 7,
            term: "Aspergillus",
            match: "Voriconazole + amphotericin B",
          },
          { id: 8, term: "CMV", match: "Ganciclovir / valganciclovir" },
          {
            id: 9,
            term: "Toxoplasma gondii",
            match: "Pyrimethamine + sulfadiazine",
          },
          { id: 10, term: "PCP (P. jirovecii)", match: "TMP-SMX (first-line)" },
          {
            id: 11,
            term: "Malaria (P. falciparum)",
            match: "Artemisinin-based combination",
          },
          {
            id: 12,
            term: "Cryptococcal meningitis",
            match: "Amphotericin B + flucytosine",
          },
        ],
        11: [
          {
            id: 1,
            term: "Haloperidol",
            match: "D2 antagonist; typical antipsychotic; high EPS risk",
          },
          {
            id: 2,
            term: "Clozapine",
            match: "D4 + 5-HT2A antagonist; agranulocytosis; weekly CBC",
          },
          {
            id: 3,
            term: "Risperidone",
            match: "D2 + 5-HT2A antagonist; highest hyperprolactinemia risk",
          },
          {
            id: 4,
            term: "Aripiprazole",
            match:
              "D2 partial agonist; weight-neutral; metabolically favorable",
          },
          {
            id: 5,
            term: "Quetiapine",
            match: "D2 + 5-HT2A + H1 antagonist; sedation + weight gain",
          },
          {
            id: 6,
            term: "Olanzapine",
            match: "D2 + 5-HT2A + muscarinic; metabolic syndrome",
          },
          {
            id: 7,
            term: "Fluoxetine (SSRI)",
            match:
              "Long half-life; inhibits CYP2D6; fewest discontinuation effects",
          },
          {
            id: 8,
            term: "Paroxetine (SSRI)",
            match:
              "Most anticholinergic SSRI; CYP2D6 inhibitor; worst discontinuation",
          },
          {
            id: 9,
            term: "Venlafaxine (SNRI)",
            match:
              "NE + 5-HT reuptake inhibitor; anxiety + depression + neuropathic pain",
          },
          {
            id: 10,
            term: "Bupropion",
            match:
              "NE + DA reuptake inhibitor; lowers seizure threshold; smoking cessation",
          },
          {
            id: 11,
            term: "Mirtazapine",
            match:
              "Alpha-2 antagonist; weight gain + sedation; no sexual side effects",
          },
          {
            id: 12,
            term: "MAO inhibitors (phenelzine)",
            match:
              "Inhibit MAO-A/B; tyramine hypertensive crisis; serotonin syndrome risk",
          },
        ],
        12: [
          {
            id: 1,
            term: "BRCA1/BRCA2 mutation drugs",
            match: "PARP inhibitors (olaparib, rucaparib)",
          },
          {
            id: 2,
            term: "HER2+ breast cancer",
            match: "Trastuzumab + pertuzumab + chemotherapy",
          },
          {
            id: 3,
            term: "ER/PR+ breast cancer",
            match:
              "Tamoxifen (premenopausal) or aromatase inhibitor (postmenopausal)",
          },
          {
            id: 4,
            term: "NSCLC with EGFR mutation",
            match: "Erlotinib, gefitinib, osimertinib (3rd gen)",
          },
          {
            id: 5,
            term: "NSCLC with ALK rearrangement",
            match: "Crizotinib, alectinib, brigatinib",
          },
          {
            id: 6,
            term: "Melanoma with BRAF V600E",
            match: "Vemurafenib + cobimetinib (BRAF + MEK inhibitor)",
          },
          {
            id: 7,
            term: "Colorectal cancer (KRAS wild-type)",
            match: "Cetuximab or panitumumab (anti-EGFR)",
          },
          {
            id: 8,
            term: "CML (imatinib-resistant)",
            match: "Dasatinib, nilotinib, or bosutinib (2nd gen)",
          },
          {
            id: 9,
            term: "Hodgkin lymphoma",
            match: "ABVD (adriamycin, bleomycin, vinblastine, dacarbazine)",
          },
          {
            id: 10,
            term: "DLBCL (diffuse large B-cell lymphoma)",
            match:
              "R-CHOP (rituximab + cyclophosphamide + doxorubicin + vincristine + prednisone)",
          },
          {
            id: 11,
            term: "Multiple myeloma first-line",
            match: "Bortezomib + lenalidomide + dexamethasone (VRd)",
          },
          {
            id: 12,
            term: "AML (acute promyelocytic)",
            match: "ATRA (all-trans retinoic acid) + arsenic trioxide",
          },
        ],
      },
      5: {
        // Level 5 - 14 pairs each
        1: [
          { id: 1, term: "Metformin", match: "Biguanide" },
          { id: 2, term: "Lisinopril", match: "ACE Inhibitor" },
          { id: 3, term: "Atorvastatin", match: "Statin" },
          { id: 4, term: "Omeprazole", match: "PPI" },
          { id: 5, term: "Albuterol", match: "Beta-agonist" },
          { id: 6, term: "Warfarin", match: "Anticoagulant" },
          { id: 7, term: "Amoxicillin", match: "Penicillin" },
          { id: 8, term: "Levothyroxine", match: "Thyroid hormone" },
          { id: 9, term: "Prednisone", match: "Corticosteroid" },
          { id: 10, term: "Gabapentin", match: "Anticonvulsant" },
          { id: 11, term: "Losartan", match: "ARB" },
          { id: 12, term: "Amlodipine", match: "Ca-channel blocker" },
          { id: 13, term: "Metoprolol", match: "Beta-blocker" },
          { id: 14, term: "Furosemide", match: "Loop diuretic" },
        ],
        2: [
          { id: 1, term: "Estradiol", match: "Estrogen" },
          { id: 2, term: "Progesterone", match: "Progestin" },
          { id: 3, term: "Clomiphene", match: "Ovulation inducer" },
          { id: 4, term: "Oxytocin", match: "Uterine contraction" },
          { id: 5, term: "Terbutaline", match: "Tocolytic" },
          { id: 6, term: "Misoprostol", match: "Cervical ripening" },
          { id: 7, term: "Magnesium sulfate", match: "Preeclampsia" },
          { id: 8, term: "Leuprolide", match: "GnRH agonist" },
          { id: 9, term: "Danazol", match: "Endometriosis" },
          { id: 10, term: "Mifepristone", match: "Progesterone blocker" },
          { id: 11, term: "Medroxyprogesterone", match: "Contraceptive" },
          { id: 12, term: "Dinoprostone", match: "PGE2 analog" },
          { id: 13, term: "Carboprost", match: "PGF2 analog" },
          { id: 14, term: "Methylergonovine", match: "Postpartum bleeding" },
        ],
        3: [
          { id: 1, term: "Enalapril", match: "ACE inhibitor" },
          { id: 2, term: "Valsartan", match: "ARB" },
          { id: 3, term: "Diltiazem", match: "Non-DHP CCB" },
          { id: 4, term: "Nifedipine", match: "DHP CCB" },
          { id: 5, term: "Hydralazine", match: "Vasodilator" },
          { id: 6, term: "Nitroprusside", match: "Hypertensive crisis" },
          { id: 7, term: "Clonidine", match: "Central alpha-2" },
          { id: 8, term: "Prazosin", match: "Alpha-1 blocker" },
          { id: 9, term: "Carvedilol", match: "Alpha/beta blocker" },
          { id: 10, term: "Labetalol", match: "Mixed blocker" },
          { id: 11, term: "Minoxidil", match: "K-channel opener" },
          { id: 12, term: "Fenoldopam", match: "D1 agonist" },
          { id: 13, term: "Eplerenone", match: "MR antagonist" },
          { id: 14, term: "Aliskiren", match: "Renin inhibitor" },
        ],
        4: [
          { id: 1, term: "Haloperidol", match: "Typical antipsychotic" },
          { id: 2, term: "Risperidone", match: "Atypical antipsychotic" },
          { id: 3, term: "Olanzapine", match: "D2/5HT2 blocker" },
          { id: 4, term: "Clozapine", match: "Treatment-resistant" },
          { id: 5, term: "Aripiprazole", match: "Partial D2 agonist" },
          { id: 6, term: "Ziprasidone", match: "QT prolongation" },
          { id: 7, term: "Venlafaxine", match: "SNRI" },
          { id: 8, term: "Bupropion", match: "NDRI" },
          { id: 9, term: "Mirtazapine", match: "Alpha-2 antagonist" },
          { id: 10, term: "Trazodone", match: "Sleep aid" },
          { id: 11, term: "Amitriptyline", match: "TCA" },
          { id: 12, term: "Phenelzine", match: "MAOI" },
          { id: 13, term: "Escitalopram", match: "SSRI" },
          { id: 14, term: "Duloxetine", match: "Pain/depression" },
        ],
        5: [
          { id: 1, term: "Naloxone", match: "Opioid antagonist" },
          { id: 2, term: "Flumazenil", match: "Benzo antagonist" },
          { id: 3, term: "N-acetylcysteine", match: "Acetaminophen OD" },
          { id: 4, term: "Atropine", match: "Cholinergic OD" },
          { id: 5, term: "Pralidoxime", match: "Organophosphate" },
          { id: 6, term: "Fomepizole", match: "Methanol/ethylene" },
          { id: 7, term: "Deferoxamine", match: "Iron chelator" },
          { id: 8, term: "Dimercaprol", match: "Heavy metal" },
          { id: 9, term: "EDTA", match: "Lead poisoning" },
          { id: 10, term: "Glucagon", match: "Beta-blocker OD" },
          { id: 11, term: "Digibind", match: "Digoxin toxicity" },
          { id: 12, term: "Methylene blue", match: "Methemoglobinemia" },
          { id: 13, term: "Physostigmine", match: "Anticholinergic OD" },
          { id: 14, term: "Hydroxocobalamin", match: "Cyanide" },
        ],
        6: [
          { id: 1, term: "Insulin glargine", match: "Long-acting insulin" },
          { id: 2, term: "Insulin lispro", match: "Rapid-acting insulin" },
          { id: 3, term: "Metformin", match: "Biguanide" },
          { id: 4, term: "Glipizide", match: "Sulfonylurea" },
          { id: 5, term: "Pioglitazone", match: "Thiazolidinedione" },
          { id: 6, term: "Sitagliptin", match: "DPP-4 inhibitor" },
          { id: 7, term: "Empagliflozin", match: "SGLT2 inhibitor" },
          { id: 8, term: "Liraglutide", match: "GLP-1 agonist" },
          { id: 9, term: "Acarbose", match: "Alpha-glucosidase" },
          { id: 10, term: "Levothyroxine", match: "Thyroid hormone" },
          { id: 11, term: "Methimazole", match: "Thionamide" },
          { id: 12, term: "Propylthiouracil", match: "Thionamide" },
          { id: 13, term: "Desmopressin", match: "ADH analog" },
          { id: 14, term: "Prednisone", match: "Corticosteroid" },
        ],
        7: [
          { id: 1, term: "Imatinib", match: "BCR-ABL inhibitor" },
          { id: 2, term: "Trastuzumab", match: "HER2 monoclonal Ab" },
          { id: 3, term: "Rituximab", match: "Anti-CD20" },
          { id: 4, term: "Cetuximab", match: "Anti-EGFR" },
          { id: 5, term: "Bevacizumab", match: "Anti-VEGF" },
          { id: 6, term: "Ipilimumab", match: "Anti-CTLA-4" },
          { id: 7, term: "Pembrolizumab", match: "Anti-PD-1" },
          { id: 8, term: "Atezolizumab", match: "Anti-PD-L1" },
          { id: 9, term: "Bortezomib", match: "Proteasome inhibitor" },
          { id: 10, term: "Venetoclax", match: "BCL-2 inhibitor" },
          { id: 11, term: "Ibrutinib", match: "BTK inhibitor" },
          { id: 12, term: "Lenalidomide", match: "Immunomodulatory IMiD" },
          {
            id: 13,
            term: "Azacitidine",
            match: "DNA methyltransferase inhibitor",
          },
          { id: 14, term: "Crizotinib", match: "ALK inhibitor" },
        ],
        8: [
          {
            id: 1,
            term: "Metformin MOA",
            match: "Activates AMPK → inhibits hepatic gluconeogenesis",
          },
          {
            id: 2,
            term: "Sulfonylurea MOA",
            match:
              "Closes K-ATP channels → depolarizes beta cell → insulin release",
          },
          {
            id: 3,
            term: "GLP-1 agonist MOA",
            match:
              "Incretin; glucose-dependent insulin release; delays gastric emptying",
          },
          {
            id: 4,
            term: "DPP-4 inhibitor MOA",
            match:
              "Prevents GLP-1 degradation; modest glucose lowering; weight neutral",
          },
          {
            id: 5,
            term: "SGLT2 inhibitor MOA",
            match:
              "Blocks glucose reabsorption in PCT; glucosuria; weight + BP + cardioprotective",
          },
          {
            id: 6,
            term: "Pioglitazone (TZD) MOA",
            match:
              "PPAR-gamma agonist; improves peripheral insulin sensitivity",
          },
          {
            id: 7,
            term: "Insulin glargine",
            match: "Long-acting (24h); no peak; once daily basal insulin",
          },
          {
            id: 8,
            term: "Insulin lispro/aspart",
            match: "Rapid-acting; onset 15 min; give with meals",
          },
          {
            id: 9,
            term: "Levothyroxine (T4)",
            match: "Synthetic T4; converted peripherally to T3; once daily",
          },
          {
            id: 10,
            term: "Methimazole / PTU",
            match:
              "Thionamides; inhibit thyroid peroxidase; PTU also blocks T4→T3",
          },
          {
            id: 11,
            term: "Radioactive iodine (I-131)",
            match:
              "Destroys thyroid follicles; hyperthyroidism + thyroid cancer",
          },
          {
            id: 12,
            term: "Desmopressin (DDAVP)",
            match:
              "ADH analog; V2 receptor; central DI + nocturnal enuresis + vWD type 1",
          },
          {
            id: 13,
            term: "Fludrocortisone",
            match:
              "Synthetic mineralocorticoid; Addison's + congenital adrenal hyperplasia",
          },
          {
            id: 14,
            term: "Bisphosphonates (alendronate)",
            match:
              "Inhibit osteoclasts; osteoporosis; risk of osteonecrosis of jaw",
          },
        ],
        9: [
          {
            id: 1,
            term: "Aspirin MOA",
            match: "Irreversibly inhibits COX-1 + COX-2 (acetylates serine)",
          },
          {
            id: 2,
            term: "Ibuprofen MOA",
            match: "Reversible COX-1 + COX-2 inhibition",
          },
          {
            id: 3,
            term: "Celecoxib MOA",
            match:
              "Selective COX-2 inhibitor; less GI toxicity; increases thrombotic risk",
          },
          {
            id: 4,
            term: "Allopurinol",
            match:
              "Xanthine oxidase inhibitor; chronic gout; caution with azathioprine",
          },
          {
            id: 5,
            term: "Colchicine MOA",
            match:
              "Inhibits microtubule polymerization; acute gout; also pericarditis",
          },
          {
            id: 6,
            term: "Probenecid",
            match:
              "Inhibits URAT1 urate reabsorption; uricosuric; acute gout only after attack resolves",
          },
          {
            id: 7,
            term: "Methotrexate (low dose RA)",
            match: "Adenosine release + AICAR accumulation; antiproliferative",
          },
          {
            id: 8,
            term: "Hydroxychloroquine",
            match: "Lysosomal pH alteration + TLR inhibition; SLE + RA",
          },
          {
            id: 9,
            term: "Tocilizumab",
            match:
              "Anti-IL-6 receptor; RA + giant cell arteritis + cytokine storm",
          },
          {
            id: 10,
            term: "Anakinra",
            match: "IL-1 receptor antagonist; RA + systemic JIA + FMF",
          },
          {
            id: 11,
            term: "Abatacept",
            match: "CTLA4-Ig; blocks CD80/CD86-CD28 costimulation; RA",
          },
          {
            id: 12,
            term: "Secukinumab / ixekizumab",
            match: "Anti-IL-17; psoriasis + ankylosing spondylitis + PsA",
          },
          {
            id: 13,
            term: "Ustekinumab",
            match: "Anti-IL-12/23 (anti-p40 subunit); psoriasis + Crohn's + UC",
          },
          {
            id: 14,
            term: "Guselkumab / risankizumab",
            match: "Anti-IL-23 (anti-p19 subunit); psoriasis + PsA",
          },
        ],
        10: [
          {
            id: 1,
            term: "Carbon monoxide poisoning",
            match: "100% O2 (high-flow or hyperbaric O2)",
          },
          {
            id: 2,
            term: "Cyanide poisoning",
            match: "Hydroxocobalamin or amyl nitrite + sodium thiosulfate",
          },
          {
            id: 3,
            term: "Methanol / ethylene glycol",
            match: "Fomepizole (ADH inhibitor); dialysis if severe",
          },
          { id: 4, term: "Iron overdose", match: "Deferoxamine chelation" },
          {
            id: 5,
            term: "Lead poisoning (children)",
            match: "Succimer (DMSA) for blood level > 45 mcg/dL",
          },
          {
            id: 6,
            term: "Lead poisoning (severe/adult)",
            match: "EDTA + dimercaprol (BAL)",
          },
          {
            id: 7,
            term: "Arsenic / mercury poisoning",
            match: "Dimercaprol (BAL) or DMSA",
          },
          {
            id: 8,
            term: "Calcium channel blocker toxicity",
            match: "Calcium gluconate + high-dose insulin therapy",
          },
          {
            id: 9,
            term: "Dabigatran reversal",
            match: "Idarucizumab (specific antibody fragment)",
          },
          {
            id: 10,
            term: "Rivaroxaban/apixaban reversal",
            match: "Andexanet alfa (factor Xa decoy)",
          },
          {
            id: 11,
            term: "Serotonin syndrome treatment",
            match: "Cyproheptadine (5-HT2A antagonist) + supportive care",
          },
          {
            id: 12,
            term: "Neuroleptic malignant syndrome treatment",
            match: "Dantrolene + bromocriptine + supportive cooling",
          },
          {
            id: 13,
            term: "Malignant hyperthermia treatment",
            match: "Dantrolene (RyR1 blocker) + cooling",
          },
          {
            id: 14,
            term: "Anticholinergic toxicity treatment",
            match: "Physostigmine (reversible AChE inhibitor)",
          },
        ],
      },
    },
    pathology: {
      1: {
        // Level 1 - 6 pairs each
        1: [
          { id: 1, term: "Diabetes", match: "Hyperglycemia" },
          { id: 2, term: "Hypertension", match: "High BP" },
          { id: 3, term: "Asthma", match: "Wheezing" },
          { id: 4, term: "Anemia", match: "Fatigue & pallor" },
          { id: 5, term: "Pneumonia", match: "Cough & fever" },
          { id: 6, term: "GERD", match: "Heartburn" },
        ],
        2: [
          { id: 1, term: "Heart failure", match: "Edema" },
          { id: 2, term: "Angina", match: "Chest pain" },
          { id: 3, term: "Arrhythmia", match: "Irregular heartbeat" },
          { id: 4, term: "DVT", match: "Leg swelling" },
          { id: 5, term: "Atherosclerosis", match: "Plaque buildup" },
          { id: 6, term: "Endocarditis", match: "Valve infection" },
        ],
        3: [
          { id: 1, term: "Stroke", match: "Sudden weakness" },
          { id: 2, term: "Epilepsy", match: "Seizures" },
          { id: 3, term: "Parkinson's", match: "Tremor" },
          { id: 4, term: "Alzheimer's", match: "Memory loss" },
          { id: 5, term: "Multiple sclerosis", match: "Demyelination" },
          { id: 6, term: "Meningitis", match: "Stiff neck" },
        ],
        4: [
          { id: 1, term: "Cirrhosis", match: "Liver scarring" },
          { id: 2, term: "Hepatitis", match: "Liver inflammation" },
          { id: 3, term: "Pancreatitis", match: "Abdominal pain" },
          { id: 4, term: "Cholecystitis", match: "GB inflammation" },
          { id: 5, term: "Appendicitis", match: "RLQ pain" },
          { id: 6, term: "Crohn's", match: "Skip lesions" },
        ],
        5: [
          { id: 1, term: "Osteoporosis", match: "Brittle bones" },
          { id: 2, term: "Osteoarthritis", match: "Joint wear" },
          { id: 3, term: "Rheumatoid arthritis", match: "Joint inflammation" },
          { id: 4, term: "Gout", match: "Crystal deposit" },
          { id: 5, term: "Fibromyalgia", match: "Widespread pain" },
          { id: 6, term: "Lupus", match: "Butterfly rash" },
        ],
        6: [
          { id: 1, term: "COPD", match: "Chronic cough" },
          { id: 2, term: "UTI", match: "Dysuria" },
          { id: 3, term: "Appendicitis", match: "RLQ pain" },
          { id: 4, term: "Kidney stones", match: "Flank pain" },
          { id: 5, term: "Hypothyroidism", match: "Cold intolerance" },
          { id: 6, term: "Hyperthyroidism", match: "Heat intolerance" },
        ],
        7: [
          { id: 1, term: "Turner syndrome", match: "Webbed neck" },
          { id: 2, term: "Marfan syndrome", match: "Arachnodactyly" },
          { id: 3, term: "Hemochromatosis", match: "Bronze diabetes" },
          { id: 4, term: "Wilson's disease", match: "Kayser-Fleischer rings" },
          { id: 5, term: "Cystic fibrosis", match: "Thick mucus" },
          { id: 6, term: "Down syndrome", match: "Trisomy 21" },
        ],
        8: [
          { id: 1, term: "Elevated troponin", match: "Myocardial infarction" },
          { id: 2, term: "Low MCV", match: "Iron deficiency anemia" },
          { id: 3, term: "High TSH", match: "Hypothyroidism" },
          { id: 4, term: "Elevated PSA", match: "Prostate pathology" },
          { id: 5, term: "Elevated D-dimer", match: "Thrombosis / DVT" },
          { id: 6, term: "Low sodium", match: "Hyponatremia" },
        ],
        9: [
          { id: 1, term: "Neonate jaundice", match: "Physiological icterus" },
          { id: 2, term: "Toddler blue spell", match: "Tetralogy of Fallot" },
          { id: 3, term: "Young woman butterfly rash", match: "Lupus (SLE)" },
          {
            id: 4,
            term: "Elderly confused + tremor",
            match: "Parkinson's disease",
          },
          {
            id: 5,
            term: "Middle-age woman dry eyes",
            match: "Sjögren's syndrome",
          },
          {
            id: 6,
            term: "Teen tall + lens dislocation",
            match: "Marfan syndrome",
          },
        ],
        10: [
          {
            id: 1,
            term: "BRCA1/2 mutation",
            match: "Hereditary breast/ovarian cancer",
          },
          {
            id: 2,
            term: "APC mutation",
            match: "Familial adenomatous polyposis",
          },
          { id: 3, term: "RET mutation", match: "MEN2A / MEN2B" },
          { id: 4, term: "VHL mutation", match: "Von Hippel-Lindau syndrome" },
          { id: 5, term: "TP53 mutation", match: "Li-Fraumeni syndrome" },
          {
            id: 6,
            term: "BCR-ABL (t9;22)",
            match: "CML — Philadelphia chromosome",
          },
        ],
        11: [
          { id: 1, term: "S. aureus", match: "Osteomyelitis + endocarditis" },
          {
            id: 2,
            term: "S. pneumoniae",
            match: "Lobar pneumonia + meningitis",
          },
          { id: 3, term: "H. pylori", match: "Peptic ulcer + gastric cancer" },
          {
            id: 4,
            term: "E. coli O157:H7",
            match: "HUS (hemolytic uremic syndrome)",
          },
          { id: 5, term: "Treponema pallidum", match: "Syphilis" },
          {
            id: 6,
            term: "M. tuberculosis",
            match: "Pulmonary TB + Ghon focus",
          },
        ],
        12: [
          { id: 1, term: "Double bubble sign", match: "Duodenal atresia" },
          { id: 2, term: "Bird's beak sign", match: "Achalasia / volvulus" },
          { id: 3, term: "Kerley B lines", match: "Pulmonary edema" },
          {
            id: 4,
            term: "Sunburst periosteal reaction",
            match: "Osteosarcoma",
          },
          {
            id: 5,
            term: "Onion skin periosteal reaction",
            match: "Ewing sarcoma",
          },
          { id: 6, term: "String sign", match: "Crohn's / pyloric stenosis" },
        ],
        13: [
          {
            id: 1,
            term: "Myocardial infarction",
            match: "Chest pain + ST elevation",
          },
          {
            id: 2,
            term: "Pulmonary embolism",
            match: "Sudden dyspnea + pleuritic pain",
          },
          {
            id: 3,
            term: "Appendicitis",
            match: "RLQ pain + rebound tenderness",
          },
          { id: 4, term: "Cholecystitis", match: "RUQ pain + Murphy's sign" },
          {
            id: 5,
            term: "Pancreatitis",
            match: "Epigastric pain radiating to back",
          },
          {
            id: 6,
            term: "Ruptured ectopic pregnancy",
            match: "Sudden lower abdominal pain + syncope",
          },
        ],
        14: [
          {
            id: 1,
            term: "Acute rheumatic fever",
            match: "Strep throat complication; Jones criteria",
          },
          {
            id: 2,
            term: "Infective endocarditis",
            match: "Fever + new murmur + emboli",
          },
          {
            id: 3,
            term: "Pericarditis",
            match: "Pleuritic chest pain + friction rub + saddle ST",
          },
          {
            id: 4,
            term: "Aortic dissection",
            match: "Tearing chest to back pain + unequal BP in arms",
          },
          {
            id: 5,
            term: "Cardiac tamponade",
            match: "Beck's triad: JVD + muffled heart + hypotension",
          },
          {
            id: 6,
            term: "Heart failure (systolic)",
            match: "Low EF + fatigue + edema + dyspnea",
          },
        ],
        15: [
          {
            id: 1,
            term: "Lobar pneumonia",
            match: "S. pneumoniae; consolidation on CXR",
          },
          {
            id: 2,
            term: "Atypical pneumonia",
            match: "Mycoplasma / Chlamydophila; interstitial pattern",
          },
          {
            id: 3,
            term: "ARDS",
            match:
              "Diffuse bilateral infiltrates + refractory hypoxemia + normal PCWP",
          },
          {
            id: 4,
            term: "Pneumothorax",
            match: "Unilateral absent breath sounds + hyperresonance",
          },
          {
            id: 5,
            term: "Pleural effusion (exudate)",
            match: "Light's criteria: protein > 0.5, LDH > 0.6",
          },
          {
            id: 6,
            term: "Sarcoidosis",
            match: "Bilateral hilar lymphadenopathy + noncaseating granulomas",
          },
        ],
        16: [
          {
            id: 1,
            term: "Nephrotic syndrome",
            match: "Proteinuria > 3.5 g/day + edema + hypoalbuminemia",
          },
          {
            id: 2,
            term: "Nephritic syndrome",
            match: "Hematuria + RBC casts + HTN + mild proteinuria",
          },
          {
            id: 3,
            term: "Acute tubular necrosis",
            match: "Muddy brown casts + oliguria + rising creatinine",
          },
          {
            id: 4,
            term: "Renal cell carcinoma",
            match: "Hematuria + flank pain + palpable mass (classic triad)",
          },
          {
            id: 5,
            term: "Bladder cancer (TCC)",
            match: "Painless hematuria; aniline dyes + smoking risk",
          },
          {
            id: 6,
            term: "Wilms tumor (nephroblastoma)",
            match: "Abdominal mass in children; WT1 gene",
          },
        ],
        17: [
          {
            id: 1,
            term: "Peptic ulcer disease",
            match: "Epigastric pain relieved or worsened by eating; H. pylori",
          },
          {
            id: 2,
            term: "Crohn's disease",
            match: "Skip lesions + transmural + can affect any GI segment",
          },
          {
            id: 3,
            term: "Ulcerative colitis",
            match: "Continuous mucosal lesions from rectum; bloody diarrhea",
          },
          {
            id: 4,
            term: "Celiac disease",
            match: "Gluten sensitivity; villous atrophy; malabsorption",
          },
          {
            id: 5,
            term: "Liver cirrhosis",
            match: "Fibrosis + regenerative nodules; portal HTN",
          },
          {
            id: 6,
            term: "Hepatitis B (chronic)",
            match: "HBsAg + HBeAg; high infectivity; HCC risk",
          },
        ],
        18: [
          {
            id: 1,
            term: "Gout",
            match: "Urate crystals; needle-shaped, negatively birefringent",
          },
          {
            id: 2,
            term: "Pseudogout",
            match: "Calcium pyrophosphate crystals; positively birefringent",
          },
          {
            id: 3,
            term: "Osteoarthritis",
            match:
              "Cartilage wear; Heberden's nodes; DIP + weight-bearing joints",
          },
          {
            id: 4,
            term: "Rheumatoid arthritis",
            match: "Symmetric joint inflammation; RF + anti-CCP; PIP + MCP",
          },
          {
            id: 5,
            term: "Ankylosing spondylitis",
            match: "HLA-B27; bamboo spine; sacroiliitis",
          },
          {
            id: 6,
            term: "Systemic lupus erythematosus",
            match: "Malar rash + anti-dsDNA + anti-Sm antibodies",
          },
        ],
      },
      2: {
        // Level 2 - 8 pairs each
        1: [
          { id: 1, term: "Diabetes", match: "Hyperglycemia" },
          { id: 2, term: "Hypertension", match: "High BP" },
          { id: 3, term: "Asthma", match: "Wheezing" },
          { id: 4, term: "Anemia", match: "Fatigue & pallor" },
          { id: 5, term: "Pneumonia", match: "Cough & fever" },
          { id: 6, term: "GERD", match: "Heartburn" },
          { id: 7, term: "Migraine", match: "Severe headache" },
          { id: 8, term: "Arthritis", match: "Joint pain" },
        ],
        2: [
          { id: 1, term: "Myocardial infarction", match: "Heart attack" },
          { id: 2, term: "Cardiomyopathy", match: "Weak heart muscle" },
          { id: 3, term: "Pericarditis", match: "Chest pain worse lying" },
          { id: 4, term: "Aortic stenosis", match: "Syncope on exertion" },
          { id: 5, term: "Mitral regurgitation", match: "Systolic murmur" },
          { id: 6, term: "Atrial fibrillation", match: "Irregular pulse" },
          { id: 7, term: "Hypertrophic CM", match: "Sudden death risk" },
          { id: 8, term: "Pulmonary embolism", match: "Dyspnea & pleuritic" },
        ],
        3: [
          { id: 1, term: "COPD", match: "Barrel chest" },
          { id: 2, term: "Tuberculosis", match: "Night sweats" },
          { id: 3, term: "Lung cancer", match: "Weight loss" },
          { id: 4, term: "Pleural effusion", match: "Dullness to percussion" },
          { id: 5, term: "Pneumothorax", match: "Absent breath sounds" },
          { id: 6, term: "Pulmonary fibrosis", match: "Dry cough" },
          { id: 7, term: "Sarcoidosis", match: "Hilar adenopathy" },
          { id: 8, term: "Bronchiectasis", match: "Chronic productive cough" },
        ],
        4: [
          { id: 1, term: "Acute kidney injury", match: "Rising creatinine" },
          { id: 2, term: "Chronic kidney disease", match: "GFR decline" },
          { id: 3, term: "Nephrotic syndrome", match: "Proteinuria" },
          { id: 4, term: "Nephritic syndrome", match: "Hematuria" },
          { id: 5, term: "Pyelonephritis", match: "Flank pain & fever" },
          { id: 6, term: "Kidney stones", match: "Colicky pain" },
          { id: 7, term: "Glomerulonephritis", match: "RBC casts" },
          { id: 8, term: "Polycystic kidney", match: "Bilateral cysts" },
        ],
        5: [
          { id: 1, term: "Hypothyroidism", match: "Weight gain & fatigue" },
          { id: 2, term: "Hyperthyroidism", match: "Weight loss & tremor" },
          { id: 3, term: "Addison's disease", match: "Hyperpigmentation" },
          { id: 4, term: "Cushing's syndrome", match: "Moon facies" },
          { id: 5, term: "Acromegaly", match: "Enlarged hands" },
          { id: 6, term: "Diabetes insipidus", match: "Polyuria" },
          { id: 7, term: "SIADH", match: "Hyponatremia" },
          { id: 8, term: "Pheochromocytoma", match: "Episodic hypertension" },
        ],
        6: [
          { id: 1, term: "COPD", match: "Barrel chest" },
          { id: 2, term: "Asthma", match: "Wheezing" },
          { id: 3, term: "Pneumonia", match: "Fever & cough" },
          { id: 4, term: "Pulmonary embolism", match: "Pleuritic pain" },
          { id: 5, term: "Heart failure", match: "Edema" },
          { id: 6, term: "Cirrhosis", match: "Liver scarring" },
          { id: 7, term: "Pancreatitis", match: "Epigastric pain" },
          { id: 8, term: "Peptic ulcer", match: "Burning pain" },
        ],
        7: [
          { id: 1, term: "Granulomatosis (GPA)", match: "C-ANCA positive" },
          { id: 2, term: "Microscopic polyangiitis", match: "P-ANCA positive" },
          { id: 3, term: "Polyarteritis nodosa", match: "No lung involvement" },
          { id: 4, term: "Buerger's disease", match: "Young male smoker" },
          {
            id: 5,
            term: "Raynaud's phenomenon",
            match: "Triphasic color change",
          },
          { id: 6, term: "Giant cell arteritis", match: "ESR > 50" },
          { id: 7, term: "Takayasu arteritis", match: "Young Asian woman" },
          { id: 8, term: "Kawasaki disease", match: "Fever > 5 days child" },
        ],
        8: [
          { id: 1, term: "CEA", match: "Colon cancer marker" },
          { id: 2, term: "AFP", match: "Hepatocellular / germ cell" },
          { id: 3, term: "PSA", match: "Prostate cancer marker" },
          { id: 4, term: "CA-125", match: "Ovarian cancer marker" },
          { id: 5, term: "CA 19-9", match: "Pancreatic cancer marker" },
          { id: 6, term: "Beta-hCG", match: "Choriocarcinoma marker" },
          { id: 7, term: "S-100", match: "Melanoma / schwannoma" },
          { id: 8, term: "Calcitonin", match: "Medullary thyroid cancer" },
        ],
        9: [
          { id: 1, term: "ANA", match: "SLE screen" },
          { id: 2, term: "Anti-dsDNA", match: "SLE specific" },
          { id: 3, term: "Anti-Smith", match: "SLE specific" },
          { id: 4, term: "Rheumatoid factor", match: "RA screen" },
          { id: 5, term: "Anti-CCP", match: "RA specific" },
          { id: 6, term: "Anti-centromere Ab", match: "Limited scleroderma" },
          { id: 7, term: "Anti-Scl-70", match: "Diffuse scleroderma" },
          { id: 8, term: "Anti-Jo-1", match: "Polymyositis" },
        ],
        10: [
          {
            id: 1,
            term: "SIADH",
            match: "Small cell lung cancer (paraneoplastic)",
          },
          {
            id: 2,
            term: "Lambert-Eaton syndrome",
            match: "SCLC — voltage-gated Ca2+ Ab",
          },
          { id: 3, term: "Ectopic ACTH", match: "SCLC → Cushing's syndrome" },
          {
            id: 4,
            term: "PTHrP hypercalcemia",
            match: "Squamous cell lung / RCC",
          },
          {
            id: 5,
            term: "Acanthosis nigricans",
            match: "GI cancer / insulin resistance",
          },
          {
            id: 6,
            term: "Trousseau sign (migratory)",
            match: "Pancreatic cancer",
          },
          {
            id: 7,
            term: "Eaton-Lambert (improves with use)",
            match: "SCLC — distinguishes from MG",
          },
          {
            id: 8,
            term: "Dermatomyositis",
            match: "Associated with underlying malignancy",
          },
        ],
        11: [
          { id: 1, term: "Hyponatremia + euvolemia", match: "SIADH" },
          {
            id: 2,
            term: "Hypernatremia + polyuria",
            match: "Diabetes insipidus",
          },
          {
            id: 3,
            term: "Hypokalemia + HTN + alkalosis",
            match: "Primary hyperaldosteronism",
          },
          {
            id: 4,
            term: "Hyperkalemia + acidosis + hyperpigmentation",
            match: "Addison's disease",
          },
          {
            id: 5,
            term: "Hypercalcemia + bone pain + polyuria",
            match: "Primary hyperparathyroidism",
          },
          {
            id: 6,
            term: "Hypocalcemia + tetany + Chvostek",
            match: "Hypoparathyroidism",
          },
          {
            id: 7,
            term: "Hyperphosphatemia + low Ca2+",
            match: "CKD / renal osteodystrophy",
          },
          {
            id: 8,
            term: "Hypomagnesemia + refractory hypoCa",
            match: "Diuretic use / malnutrition",
          },
        ],
        12: [
          {
            id: 1,
            term: "Janeway lesions",
            match: "Infective endocarditis (painless)",
          },
          {
            id: 2,
            term: "Osler's nodes",
            match: "Infective endocarditis (painful)",
          },
          {
            id: 3,
            term: "Virchow's node",
            match: "GI malignancy (left supraclavicular)",
          },
          {
            id: 4,
            term: "Sister Mary Joseph nodule",
            match: "Peritoneal metastasis (umbilical)",
          },
          {
            id: 5,
            term: "Cullen's sign",
            match: "Periumbilical bruising — pancreatitis",
          },
          {
            id: 6,
            term: "Grey Turner's sign",
            match: "Flank bruising — pancreatitis",
          },
          { id: 7, term: "Caput medusae", match: "Portal hypertension" },
          {
            id: 8,
            term: "Courvoisier's sign",
            match: "Painless jaundice — pancreatic cancer",
          },
        ],
        13: [
          {
            id: 1,
            term: "Myocardial infarction (0-4h histo)",
            match: "No change visible; earliest: wavy fibers",
          },
          {
            id: 2,
            term: "MI (4-12h)",
            match: "Coagulative necrosis begins; contraction bands",
          },
          {
            id: 3,
            term: "MI (1-3 days)",
            match: "Neutrophil infiltration; hypereosinophilic dead fibers",
          },
          {
            id: 4,
            term: "MI (3-14 days)",
            match: "Macrophages + granulation tissue; reperfusion hemorrhage",
          },
          {
            id: 5,
            term: "MI (2 weeks+)",
            match: "Fibrosis / scar formation; collagen deposition",
          },
          {
            id: 6,
            term: "Wet gangrene",
            match: "Liquefactive + coagulative necrosis; bacteria present",
          },
          {
            id: 7,
            term: "Dry gangrene",
            match: "Coagulative necrosis; no bacteria; ischemic limb",
          },
          {
            id: 8,
            term: "Caseous necrosis",
            match: "Cheese-like; TB granuloma center",
          },
        ],
        14: [
          {
            id: 1,
            term: "Type I hypersensitivity",
            match: "IgE + mast cells; anaphylaxis, atopy, asthma",
          },
          {
            id: 2,
            term: "Type II hypersensitivity",
            match:
              "IgG/IgM vs cell surface antigens; hemolytic anemia, Goodpasture's",
          },
          {
            id: 3,
            term: "Type III hypersensitivity",
            match: "Immune complex deposition; SLE, serum sickness, PSGN",
          },
          {
            id: 4,
            term: "Type IV hypersensitivity",
            match:
              "T-cell mediated; contact dermatitis, TB, transplant rejection",
          },
          {
            id: 5,
            term: "Autoimmune hemolytic anemia (warm)",
            match: "IgG; direct Coombs+; extravascular hemolysis (spleen)",
          },
          {
            id: 6,
            term: "Cold agglutinin disease",
            match: "IgM; Mycoplasma or EBV; intravascular hemolysis",
          },
          {
            id: 7,
            term: "Graves' disease",
            match: "TSI antibody stimulates TSH receptor; hyperthyroidism",
          },
          {
            id: 8,
            term: "Hashimoto's thyroiditis",
            match:
              "Anti-TPO + anti-thyroglobulin; hypothyroidism; Hurthle cells",
          },
        ],
        15: [
          {
            id: 1,
            term: "Fatty liver (steatosis)",
            match: "Lipid vacuoles in hepatocytes; alcohol or metabolic",
          },
          {
            id: 2,
            term: "Alcoholic hepatitis",
            match:
              "Mallory-Denk bodies + PMN infiltrate + ballooning degeneration",
          },
          {
            id: 3,
            term: "NASH (nonalcoholic steatohepatitis)",
            match:
              "Metabolic syndrome; identical histology to alcoholic hepatitis",
          },
          {
            id: 4,
            term: "Primary biliary cholangitis",
            match: "Anti-mitochondrial Ab; bile duct granulomas; pruritus",
          },
          {
            id: 5,
            term: "Primary sclerosing cholangitis",
            match:
              "Onion skin fibrosis; associated with UC; 'beads on string' MRCP",
          },
          {
            id: 6,
            term: "Hepatocellular carcinoma",
            match: "HBV/HCV/cirrhosis/aflatoxin; ↑AFP; trabecular pattern",
          },
          {
            id: 7,
            term: "Wilson's disease",
            match:
              "ATP7B mutation; copper accumulation; Kayser-Fleischer rings",
          },
          {
            id: 8,
            term: "Hemochromatosis (hereditary)",
            match: "HFE mutation (C282Y); iron overload; Bronze diabetes",
          },
        ],
        16: [
          {
            id: 1,
            term: "Minimal change disease",
            match:
              "Most common nephrotic in children; lipid-filled podocytes; responds to steroids",
          },
          {
            id: 2,
            term: "Focal segmental glomerulosclerosis",
            match: "Nephrotic; HIV, heroin, obesity; podocyte injury",
          },
          {
            id: 3,
            term: "Membranous nephropathy",
            match:
              "Nephrotic; adult; PLA2R antibody; hepatitis B; 'spike and dome'",
          },
          {
            id: 4,
            term: "IgA nephropathy (Berger's)",
            match:
              "Most common nephritis worldwide; mesangial IgA; hematuria after URI",
          },
          {
            id: 5,
            term: "Post-streptococcal GN",
            match: "Nephritic; 'starry sky'; subepithelial humps; low C3",
          },
          {
            id: 6,
            term: "Rapidly progressive GN (Type I)",
            match: "Anti-GBM (Goodpasture's); linear IgG; crescents",
          },
          {
            id: 7,
            term: "Rapidly progressive GN (Type II)",
            match: "Immune complex; SLE; granular IgG; crescents",
          },
          {
            id: 8,
            term: "Rapidly progressive GN (Type III)",
            match: "Pauci-immune; ANCA+; GPA or MPA; crescents",
          },
        ],
        17: [
          {
            id: 1,
            term: "p53 (TP53) mutation",
            match: "Most common mutation in all human cancers; Li-Fraumeni",
          },
          {
            id: 2,
            term: "KRAS mutation",
            match:
              "GTPase; constitutively active; pancreatic + colorectal + lung",
          },
          {
            id: 3,
            term: "BCR-ABL (t(9;22))",
            match: "Philadelphia chromosome; CML; imatinib target",
          },
          {
            id: 4,
            term: "t(14;18) BCL-2",
            match: "Follicular lymphoma; anti-apoptotic; venetoclax target",
          },
          {
            id: 5,
            term: "t(8;14) c-MYC",
            match: "Burkitt lymphoma; starry sky pattern; EBV",
          },
          {
            id: 6,
            term: "t(15;17) PML-RARA",
            match: "AML-M3 (APL); ATRA responsive",
          },
          {
            id: 7,
            term: "APC mutation",
            match: "WNT pathway; colorectal cancer; FAP",
          },
          {
            id: 8,
            term: "PTEN mutation",
            match: "PI3K pathway; endometrial + breast + prostate cancer",
          },
        ],
        18: [
          {
            id: 1,
            term: "Osteosarcoma",
            match:
              "Most common primary bone malignancy; sunburst + Codman's triangle; distal femur",
          },
          {
            id: 2,
            term: "Ewing sarcoma",
            match:
              "Diaphysis; t(11;22) EWS-FLI1; onion-skin periosteal reaction",
          },
          {
            id: 3,
            term: "Chondrosarcoma",
            match:
              "Cartilage tumor; central medullary; older adults; pelvis/femur",
          },
          {
            id: 4,
            term: "Giant cell tumor of bone",
            match:
              "Epiphysis; soap-bubble appearance; F > M; benign but locally aggressive",
          },
          {
            id: 5,
            term: "Osteoid osteoma",
            match: "Nidus with halo; nocturnal pain relieved by ASA; < 2 cm",
          },
          {
            id: 6,
            term: "Fibrous dysplasia",
            match:
              "Bone replaced by fibrous tissue; shepherd's crook deformity; ground glass",
          },
          {
            id: 7,
            term: "Paget's disease of bone",
            match:
              "Mosaic pattern; high ALP + normal Ca; hat too small; osteosarcoma risk",
          },
          {
            id: 8,
            term: "Multiple myeloma (bone)",
            match:
              "CRAB criteria; punched-out lytic lesions; plasma cell dyscrasia",
          },
        ],
      },
      3: {
        // Level 3 - 10 pairs each
        1: [
          { id: 1, term: "Diabetes", match: "Hyperglycemia" },
          { id: 2, term: "Hypertension", match: "High BP" },
          { id: 3, term: "Asthma", match: "Wheezing" },
          { id: 4, term: "Anemia", match: "Fatigue & pallor" },
          { id: 5, term: "Pneumonia", match: "Cough & fever" },
          { id: 6, term: "GERD", match: "Heartburn" },
          { id: 7, term: "Migraine", match: "Severe headache" },
          { id: 8, term: "Arthritis", match: "Joint pain" },
          { id: 9, term: "Heart failure", match: "Edema & dyspnea" },
          { id: 10, term: "Cirrhosis", match: "Liver scarring" },
        ],
        2: [
          { id: 1, term: "Iron deficiency", match: "Microcytic anemia" },
          { id: 2, term: "B12 deficiency", match: "Megaloblastic" },
          { id: 3, term: "Sickle cell", match: "Vaso-occlusive crisis" },
          { id: 4, term: "Thalassemia", match: "Target cells" },
          { id: 5, term: "Hemophilia", match: "Joint bleeds" },
          { id: 6, term: "von Willebrand", match: "Mucosal bleeding" },
          { id: 7, term: "ITP", match: "Thrombocytopenia" },
          { id: 8, term: "TTP", match: "Pentad of symptoms" },
          { id: 9, term: "DIC", match: "Consumptive coagulopathy" },
          { id: 10, term: "Leukemia", match: "Blast cells" },
        ],
        3: [
          { id: 1, term: "Celiac disease", match: "Gluten sensitivity" },
          { id: 2, term: "Ulcerative colitis", match: "Bloody diarrhea" },
          { id: 3, term: "Diverticulitis", match: "LLQ pain" },
          { id: 4, term: "Peptic ulcer", match: "Epigastric pain" },
          { id: 5, term: "Gastritis", match: "H. pylori" },
          { id: 6, term: "Achalasia", match: "Dysphagia" },
          {
            id: 7,
            term: "Intestinal obstruction",
            match: "Absent bowel sounds",
          },
          { id: 8, term: "Irritable bowel", match: "Altered bowel habits" },
          { id: 9, term: "Hemorrhoids", match: "Rectal bleeding" },
          { id: 10, term: "Gallstones", match: "RUQ pain after meals" },
        ],
        4: [
          { id: 1, term: "Psoriasis", match: "Silvery scales" },
          { id: 2, term: "Eczema", match: "Pruritic rash" },
          { id: 3, term: "Acne", match: "Comedones" },
          { id: 4, term: "Rosacea", match: "Facial flushing" },
          { id: 5, term: "Vitiligo", match: "Depigmented patches" },
          { id: 6, term: "Melanoma", match: "Asymmetric mole" },
          { id: 7, term: "Basal cell", match: "Pearly nodule" },
          { id: 8, term: "Squamous cell", match: "Scaly plaque" },
          { id: 9, term: "Cellulitis", match: "Red, warm skin" },
          { id: 10, term: "Herpes zoster", match: "Dermatomal rash" },
        ],
        5: [
          { id: 1, term: "Schizophrenia", match: "Hallucinations" },
          { id: 2, term: "Bipolar disorder", match: "Mood swings" },
          { id: 3, term: "Major depression", match: "Anhedonia" },
          { id: 4, term: "Generalized anxiety", match: "Excessive worry" },
          { id: 5, term: "Panic disorder", match: "Sudden fear" },
          { id: 6, term: "OCD", match: "Intrusive thoughts" },
          { id: 7, term: "PTSD", match: "Flashbacks" },
          { id: 8, term: "Anorexia nervosa", match: "Body dysmorphia" },
          { id: 9, term: "Bulimia", match: "Binge-purge" },
          { id: 10, term: "ADHD", match: "Inattention" },
        ],
        6: [
          { id: 1, term: "Iron deficiency", match: "Microcytic anemia" },
          { id: 2, term: "B12 deficiency", match: "Megaloblastic" },
          { id: 3, term: "Sickle cell", match: "Vaso-occlusive crisis" },
          { id: 4, term: "ITP", match: "Low platelets" },
          { id: 5, term: "DIC", match: "Consumptive coagulopathy" },
          { id: 6, term: "Leukemia", match: "Blast cells" },
          { id: 7, term: "Hemophilia", match: "Joint bleeds" },
          { id: 8, term: "von Willebrand", match: "Mucosal bleeding" },
          { id: 9, term: "Thalassemia", match: "Target cells" },
          { id: 10, term: "TTP", match: "Pentad of symptoms" },
        ],
        7: [
          {
            id: 1,
            term: "Anti-GBM disease",
            match: "Linear immunofluorescence",
          },
          { id: 2, term: "IgA nephropathy", match: "Mesangial IgA deposits" },
          { id: 3, term: "Focal segmental GS", match: "Podocyte loss" },
          {
            id: 4,
            term: "Minimal change disease",
            match: "Effaced foot processes",
          },
          { id: 5, term: "Membranous nephropathy", match: "Spike and dome" },
          { id: 6, term: "MPGN", match: "Tram-track pattern" },
          { id: 7, term: "Post-strep GN", match: "Granular IF pattern" },
          {
            id: 8,
            term: "Rapidly progressive GN",
            match: "Crescents on biopsy",
          },
          { id: 9, term: "Alport syndrome", match: "X-linked hematuria" },
          { id: 10, term: "Renal amyloidosis", match: "Congo red positive" },
        ],
        8: [
          { id: 1, term: "Down syndrome", match: "Trisomy 21" },
          { id: 2, term: "Edwards syndrome", match: "Trisomy 18" },
          { id: 3, term: "Patau syndrome", match: "Trisomy 13" },
          { id: 4, term: "Turner syndrome", match: "45,X" },
          { id: 5, term: "Klinefelter syndrome", match: "47,XXY" },
          { id: 6, term: "Fragile X", match: "CGG repeat / FMR1" },
          { id: 7, term: "Prader-Willi", match: "Paternal 15q deletion" },
          { id: 8, term: "Angelman syndrome", match: "Maternal 15q deletion" },
          { id: 9, term: "Cri-du-chat", match: "5p deletion" },
          { id: 10, term: "DiGeorge syndrome", match: "22q11 deletion" },
        ],
        9: [
          { id: 1, term: "Reed-Sternberg cells", match: "Hodgkin lymphoma" },
          { id: 2, term: "Owl-eye inclusions", match: "CMV infection" },
          { id: 3, term: "Negri bodies", match: "Rabies" },
          {
            id: 4,
            term: "Psammoma bodies",
            match: "Papillary thyroid / meningioma",
          },
          { id: 5, term: "Mallory bodies", match: "Alcoholic hepatitis" },
          { id: 6, term: "Lewy bodies", match: "Parkinson's disease" },
          { id: 7, term: "Amyloid plaques", match: "Alzheimer's disease" },
          { id: 8, term: "Aschoff bodies", match: "Rheumatic fever" },
          { id: 9, term: "Schaumann bodies", match: "Sarcoidosis" },
          {
            id: 10,
            term: "Cowdry A inclusions",
            match: "Herpes simplex virus",
          },
        ],
        10: [
          {
            id: 1,
            term: "JAK2 V617F",
            match: "Polycythemia vera / essential thrombocythemia",
          },
          {
            id: 2,
            term: "BCR-ABL t(9;22)",
            match: "CML — Philadelphia chromosome",
          },
          { id: 3, term: "KRAS mutation", match: "Pancreatic + colon cancer" },
          { id: 4, term: "EGFR mutation", match: "NSCLC adenocarcinoma" },
          {
            id: 5,
            term: "BRAF V600E",
            match: "Melanoma + papillary thyroid cancer",
          },
          {
            id: 6,
            term: "HER2/neu amplification",
            match: "Aggressive breast cancer",
          },
          { id: 7, term: "FLT3 mutation", match: "AML (poor prognosis)" },
          { id: 8, term: "t(14;18) BCL-2", match: "Follicular lymphoma" },
          {
            id: 9,
            term: "RET mutation",
            match: "Medullary thyroid cancer / MEN2",
          },
          { id: 10, term: "c-MYC t(8;14)", match: "Burkitt lymphoma" },
        ],
        11: [
          {
            id: 1,
            term: "ETEC",
            match: "Heat-labile toxin → traveler's diarrhea",
          },
          { id: 2, term: "EHEC O157:H7", match: "Shiga toxin → HUS" },
          {
            id: 3,
            term: "Vibrio cholerae",
            match: "Cholera toxin → rice-water stool",
          },
          {
            id: 4,
            term: "Legionella pneumophila",
            match: "Inhibits phagosome fusion → intracellular",
          },
          {
            id: 5,
            term: "C. difficile",
            match: "Toxin A (enterotoxin) + toxin B (cytotoxin)",
          },
          {
            id: 6,
            term: "Strep pyogenes (GAS)",
            match: "M protein → rheumatic fever (molecular mimicry)",
          },
          {
            id: 7,
            term: "N. gonorrhoeae",
            match: "IgA protease + pili → urethritis / PID",
          },
          {
            id: 8,
            term: "S. aureus virulence",
            match: "Protein A + coagulase",
          },
          {
            id: 9,
            term: "H. influenzae type b",
            match: "Polysaccharide capsule → meningitis / epiglottitis",
          },
          {
            id: 10,
            term: "M. tuberculosis",
            match: "Cord factor (TDM) → granuloma formation",
          },
        ],
        12: [
          {
            id: 1,
            term: "Bruton's XLA",
            match: "No B cells → extracellular bacteria",
          },
          {
            id: 2,
            term: "DiGeorge syndrome",
            match: "No T cells → fungal + viral infections",
          },
          { id: 3, term: "SCID", match: "No T or B cells → all pathogens" },
          {
            id: 4,
            term: "Chronic granulomatous disease",
            match: "Catalase-positive organisms (S. aureus, Aspergillus)",
          },
          {
            id: 5,
            term: "C3 deficiency",
            match: "Encapsulated bacteria (no opsonization)",
          },
          {
            id: 6,
            term: "C5-C9 deficiency",
            match: "Neisseria species (no MAC)",
          },
          {
            id: 7,
            term: "Job syndrome (HIES)",
            match: "S. aureus + eczema + high IgE (Th17 defect)",
          },
          {
            id: 8,
            term: "Wiskott-Aldrich syndrome",
            match: "Thrombocytopenia + eczema + recurrent infections",
          },
          {
            id: 9,
            term: "Chediak-Higashi",
            match: "Defective phagolysosome fusion",
          },
          {
            id: 10,
            term: "MHC II deficiency",
            match: "Bare lymphocyte syndrome — CD4+ deficiency",
          },
        ],
        13: [
          {
            id: 1,
            term: "Squamous cell carcinoma (skin)",
            match: "Sun-exposed; keratin pearls; risk of metastasis",
          },
          {
            id: 2,
            term: "Basal cell carcinoma",
            match:
              "Most common skin cancer; pearly nodule; palisading nuclei; rarely metastasizes",
          },
          {
            id: 3,
            term: "Melanoma",
            match:
              "ABCDE; irregular border + color; S100/HMB45/Melan-A positive",
          },
          {
            id: 4,
            term: "Actinic keratosis",
            match: "Premalignant; solar-induced; can progress to SCC",
          },
          {
            id: 5,
            term: "Keratoacanthoma",
            match:
              "Rapidly growing crater-filled lesion; resembles SCC; self-resolves",
          },
          {
            id: 6,
            term: "Lichen planus",
            match:
              "Pruritic purple polygonal papules; Wickham's striae; anti-HCV association",
          },
          {
            id: 7,
            term: "Pemphigus vulgaris",
            match:
              "Anti-desmoglein 3; intraepidermal blistering; Nikolsky sign positive",
          },
          {
            id: 8,
            term: "Bullous pemphigoid",
            match:
              "Anti-BP180/230; subepidermal blistering; tense bullae; elderly",
          },
          {
            id: 9,
            term: "Dermatitis herpetiformis",
            match: "IgA in dermal papillae; celiac disease; intensely pruritic",
          },
          {
            id: 10,
            term: "Psoriasis (histology)",
            match:
              "Acanthosis + parakeratosis + Munro microabscesses + dilated capillaries",
          },
        ],
        14: [
          {
            id: 1,
            term: "Amyloid (Congo red stain)",
            match: "Apple-green birefringence under polarized light",
          },
          {
            id: 2,
            term: "Psammoma bodies",
            match:
              "Papillary thyroid Ca + meningioma + serous ovarian tumor + mesothelioma",
          },
          {
            id: 3,
            term: "Auer rods",
            match: "Myeloperoxidase+ cytoplasmic inclusions; AML",
          },
          {
            id: 4,
            term: "Negri bodies",
            match: "Intracytoplasmic; Purkinje + hippocampal neurons; rabies",
          },
          {
            id: 5,
            term: "Lewy bodies",
            match: "Alpha-synuclein inclusions; Parkinson's and DLB",
          },
          {
            id: 6,
            term: "Neurofibrillary tangles",
            match: "Hyperphosphorylated tau; Alzheimer's + CTE + Pick's",
          },
          {
            id: 7,
            term: "Mallory-Denk bodies",
            match: "Cytokeratin aggregates in hepatocytes; alcoholic hepatitis",
          },
          {
            id: 8,
            term: "Heinz bodies",
            match:
              "Oxidized Hgb precipitates; G6PD deficiency; crystal violet stain",
          },
          {
            id: 9,
            term: "Reed-Sternberg cells",
            match: "Bilobed 'owl eye' giant cells; Hodgkin lymphoma",
          },
          {
            id: 10,
            term: "Owl-eye inclusions (CMV)",
            match: "Large basophilic intranuclear inclusion; immunocompromised",
          },
        ],
        15: [
          {
            id: 1,
            term: "Hashimoto's thyroiditis",
            match:
              "Lymphocytic infiltrate + Hurthle cell change; hypothyroidism",
          },
          {
            id: 2,
            term: "De Quervain (subacute) thyroiditis",
            match:
              "Granulomatous; post-viral; tender; transient hyper then hypo",
          },
          {
            id: 3,
            term: "Papillary thyroid carcinoma",
            match:
              "Most common thyroid cancer; RET/BRAF; Orphan Annie nuclei + psammoma bodies",
          },
          {
            id: 4,
            term: "Follicular thyroid carcinoma",
            match:
              "Capsular/vascular invasion; hematogenous spread; RAS mutation",
          },
          {
            id: 5,
            term: "Medullary thyroid carcinoma",
            match:
              "Parafollicular C cells; calcitonin; RET mutation; amyloid stroma",
          },
          {
            id: 6,
            term: "Anaplastic thyroid carcinoma",
            match: "Undifferentiated; elderly; rapidly fatal; p53 mutation",
          },
          {
            id: 7,
            term: "Type 1 DM (pathology)",
            match: "Autoimmune beta cell destruction; insulitis; HLA-DR3/DR4",
          },
          {
            id: 8,
            term: "Type 2 DM (pathology)",
            match:
              "Islet amyloid (amylin); beta cell exhaustion; insulin resistance",
          },
          {
            id: 9,
            term: "Conn's syndrome",
            match: "Aldosterone-secreting adrenal adenoma; hypokalemia + HTN",
          },
          {
            id: 10,
            term: "Cushing's disease (pituitary)",
            match: "ACTH-secreting pituitary adenoma; central obesity + striae",
          },
        ],
        16: [
          {
            id: 1,
            term: "Diffuse large B-cell lymphoma",
            match: "Most common NHL; aggressive; R-CHOP",
          },
          {
            id: 2,
            term: "Follicular lymphoma",
            match: "t(14;18) BCL-2; indolent; germinal center B cells",
          },
          {
            id: 3,
            term: "Burkitt lymphoma",
            match: "t(8;14) MYC; starry sky; EBV; jaw mass in Africa",
          },
          {
            id: 4,
            term: "Mantle cell lymphoma",
            match: "t(11;14) cyclin D1; CD5+ CD23-; poor prognosis",
          },
          {
            id: 5,
            term: "Hodgkin lymphoma (nodular sclerosis)",
            match:
              "Most common HL subtype; RS cells + fibrous bands; young women",
          },
          {
            id: 6,
            term: "Hodgkin lymphoma (mixed cellularity)",
            match: "EBV+; RS cells + mixed inflammatory; HIV association",
          },
          {
            id: 7,
            term: "CLL (B-cell)",
            match:
              "CD5+ CD23+ B cells; smudge cells; hypogammaglobulinemia; elderly",
          },
          {
            id: 8,
            term: "AML (acute myeloid leukemia)",
            match: "Auer rods; MPO+; t(15;17) in APL subtype",
          },
          {
            id: 9,
            term: "ALL (B-cell)",
            match: "TdT+ CD10+; peak age 3-5; Down syndrome risk",
          },
          {
            id: 10,
            term: "CML (chronic myeloid leukemia)",
            match: "BCR-ABL; Philadelphia chromosome; low LAP score",
          },
        ],
        17: [
          {
            id: 1,
            term: "Cerebral infarct (ischemic)",
            match:
              "Coagulative → liquefactive necrosis; macrophage infiltrate; 1-2 weeks",
          },
          {
            id: 2,
            term: "Epidural hematoma",
            match:
              "Arterial bleed (middle meningeal); lens-shaped on CT; lucid interval",
          },
          {
            id: 3,
            term: "Subdural hematoma",
            match: "Bridging vein tear; crescent-shaped; elderly + alcoholics",
          },
          {
            id: 4,
            term: "Subarachnoid hemorrhage",
            match:
              "Berry aneurysm rupture; sudden 'thunderclap' headache; xanthochromia",
          },
          {
            id: 5,
            term: "Hypertensive intracerebral hemorrhage",
            match:
              "Charcot-Bouchard aneurysm; basal ganglia/thalamus most common",
          },
          {
            id: 6,
            term: "Glioblastoma multiforme (GBM)",
            match:
              "Pseudopalisading necrosis; VEGF; butterfly pattern across corpus callosum",
          },
          {
            id: 7,
            term: "Meningioma",
            match:
              "Dural attachment; psammoma bodies; WHO grade I; women > men",
          },
          {
            id: 8,
            term: "Schwannoma",
            match:
              "S100+; Verocay bodies; CN VIII most common; NF2 association",
          },
          {
            id: 9,
            term: "Medulloblastoma",
            match: "Cerebellum; Homer-Wright rosettes; PNET; children",
          },
          {
            id: 10,
            term: "Craniopharyngioma",
            match:
              "Rathke's pouch remnant; calcified; machine oil fluid; bitemporal hemianopia",
          },
        ],
        18: [
          {
            id: 1,
            term: "DVT pathophysiology",
            match:
              "Virchow's triad: stasis + hypercoagulability + endothelial injury",
          },
          {
            id: 2,
            term: "Fat embolism",
            match:
              "Long bone fracture → lipid + marrow in bloodstream; petechiae + confusion",
          },
          {
            id: 3,
            term: "Amniotic fluid embolism",
            match: "Sudden delivery collapse; DIC; uncommon but catastrophic",
          },
          {
            id: 4,
            term: "Septic embolism",
            match:
              "Infective endocarditis emboli; lungs (right-sided) or brain (left-sided)",
          },
          {
            id: 5,
            term: "Paradoxical embolism",
            match:
              "DVT → patent foramen ovale → systemic; stroke in young person",
          },
          {
            id: 6,
            term: "Air embolism",
            match:
              "IV line; surgery; nitrogen in divers; gas in heart on imaging",
          },
          {
            id: 7,
            term: "Saddle embolus",
            match: "Large PE at bifurcation of pulmonary artery; sudden death",
          },
          {
            id: 8,
            term: "DIC pathophysiology",
            match:
              "Systemic clotting cascade activation; consumptive coagulopathy; ↑PT/PTT/D-dimer, ↓fibrinogen",
          },
          {
            id: 9,
            term: "Hypercoagulable states (inherited)",
            match:
              "Factor V Leiden most common; protein C/S deficiency; prothrombin G20210A",
          },
          {
            id: 10,
            term: "Antiphospholipid syndrome",
            match:
              "Lupus anticoagulant + anti-cardiolipin; paradoxically causes thrombosis",
          },
        ],
      },
      4: {
        // Level 4 - 12 pairs each
        1: [
          { id: 1, term: "Diabetes", match: "Hyperglycemia" },
          { id: 2, term: "Hypertension", match: "High BP" },
          { id: 3, term: "Asthma", match: "Wheezing" },
          { id: 4, term: "Anemia", match: "Fatigue & pallor" },
          { id: 5, term: "Pneumonia", match: "Cough & fever" },
          { id: 6, term: "GERD", match: "Heartburn" },
          { id: 7, term: "Migraine", match: "Severe headache" },
          { id: 8, term: "Arthritis", match: "Joint pain" },
          { id: 9, term: "Heart failure", match: "Edema & dyspnea" },
          { id: 10, term: "Cirrhosis", match: "Liver scarring" },
          { id: 11, term: "Stroke", match: "Sudden weakness" },
          { id: 12, term: "Epilepsy", match: "Seizures" },
        ],
        2: [
          { id: 1, term: "Breast cancer", match: "Palpable mass" },
          { id: 2, term: "Prostate cancer", match: "Elevated PSA" },
          { id: 3, term: "Colon cancer", match: "Change in bowel" },
          { id: 4, term: "Lung cancer", match: "Persistent cough" },
          { id: 5, term: "Pancreatic cancer", match: "Painless jaundice" },
          { id: 6, term: "Ovarian cancer", match: "Bloating" },
          { id: 7, term: "Cervical cancer", match: "Post-coital bleeding" },
          { id: 8, term: "Lymphoma", match: "Painless adenopathy" },
          { id: 9, term: "Multiple myeloma", match: "Bone pain" },
          { id: 10, term: "Renal cell", match: "Hematuria & flank" },
          { id: 11, term: "Bladder cancer", match: "Painless hematuria" },
          { id: 12, term: "Thyroid cancer", match: "Neck nodule" },
        ],
        3: [
          { id: 1, term: "Sepsis", match: "SIRS + infection" },
          { id: 2, term: "Septic shock", match: "Refractory hypotension" },
          { id: 3, term: "ARDS", match: "Bilateral infiltrates" },
          { id: 4, term: "Anaphylaxis", match: "Airway compromise" },
          { id: 5, term: "DKA", match: "Fruity breath" },
          { id: 6, term: "HHS", match: "Severe hyperglycemia" },
          { id: 7, term: "Thyroid storm", match: "High fever & tachy" },
          { id: 8, term: "Myxedema coma", match: "Hypothermia" },
          { id: 9, term: "Adrenal crisis", match: "Hypotension" },
          { id: 10, term: "Status epilepticus", match: "Prolonged seizure" },
          { id: 11, term: "Malignant hyperthermia", match: "Muscle rigidity" },
          { id: 12, term: "NMS", match: "Lead pipe rigidity" },
        ],
        4: [
          { id: 1, term: "HIV/AIDS", match: "CD4 decline" },
          { id: 2, term: "Syphilis", match: "Chancre" },
          { id: 3, term: "Gonorrhea", match: "Purulent discharge" },
          { id: 4, term: "Chlamydia", match: "Sterile pyuria" },
          { id: 5, term: "Herpes simplex", match: "Vesicular lesions" },
          { id: 6, term: "HPV", match: "Genital warts" },
          { id: 7, term: "Hepatitis B", match: "HBsAg positive" },
          { id: 8, term: "Hepatitis C", match: "HCV antibody" },
          { id: 9, term: "Mononucleosis", match: "Heterophile antibody" },
          { id: 10, term: "Influenza", match: "Myalgias & fever" },
          { id: 11, term: "COVID-19", match: "Anosmia" },
          { id: 12, term: "Malaria", match: "Cyclic fevers" },
        ],
        5: [
          { id: 1, term: "Preeclampsia", match: "Hypertension + proteinuria" },
          { id: 2, term: "Eclampsia", match: "Seizures in pregnancy" },
          { id: 3, term: "HELLP syndrome", match: "Hemolysis + liver" },
          { id: 4, term: "Placenta previa", match: "Painless bleeding" },
          { id: 5, term: "Placental abruption", match: "Painful bleeding" },
          { id: 6, term: "Ectopic pregnancy", match: "Adnexal mass" },
          { id: 7, term: "Gestational diabetes", match: "Glucose intolerance" },
          { id: 8, term: "Hyperemesis", match: "Severe vomiting" },
          { id: 9, term: "Chorioamnionitis", match: "Fever in labor" },
          { id: 10, term: "Postpartum hemorrhage", match: "Uterine atony" },
          { id: 11, term: "Mastitis", match: "Breast pain & fever" },
          { id: 12, term: "Endometriosis", match: "Dysmenorrhea" },
        ],
        6: [
          { id: 1, term: "Peptic ulcer", match: "Epigastric pain" },
          { id: 2, term: "Gastritis", match: "H. pylori" },
          { id: 3, term: "Cholecystitis", match: "RUQ pain" },
          { id: 4, term: "Pancreatitis", match: "Back-radiating pain" },
          { id: 5, term: "Cirrhosis", match: "Liver scarring" },
          { id: 6, term: "Hepatitis", match: "Liver inflammation" },
          { id: 7, term: "Appendicitis", match: "RLQ pain" },
          { id: 8, term: "Diverticulitis", match: "LLQ pain" },
          { id: 9, term: "Ulcerative colitis", match: "Bloody diarrhea" },
          { id: 10, term: "Crohn's disease", match: "Skip lesions" },
          { id: 11, term: "Celiac disease", match: "Gluten sensitivity" },
          { id: 12, term: "Gallstones", match: "Postprandial RUQ pain" },
        ],
        7: [
          { id: 1, term: "Li-Fraumeni", match: "TP53 germline mutation" },
          { id: 2, term: "BRCA1/2", match: "Hereditary breast/ovarian" },
          { id: 3, term: "Lynch syndrome", match: "MLH1/MSH2 mutation" },
          {
            id: 4,
            term: "MEN1",
            match: "3 Ps: pituitary, parathyroid, pancreas",
          },
          { id: 5, term: "MEN2A", match: "RET + medullary thyroid" },
          { id: 6, term: "VHL syndrome", match: "Hemangioblastoma" },
          { id: 7, term: "NF1", match: "Café-au-lait + Lisch nodules" },
          { id: 8, term: "FAP", match: "APC mutation" },
          {
            id: 9,
            term: "Xeroderma pigmentosum",
            match: "UV sensitivity / NER defect",
          },
          { id: 10, term: "Ataxia-telangiectasia", match: "ATM gene defect" },
          { id: 11, term: "Fanconi anemia", match: "DNA cross-link repair" },
          {
            id: 12,
            term: "Turcot syndrome",
            match: "Colorectal + brain tumors",
          },
        ],
        8: [
          {
            id: 1,
            term: "Nephrotic syndrome",
            match: "Proteinuria >3.5 g/day + edema",
          },
          {
            id: 2,
            term: "Nephritic syndrome",
            match: "Hematuria + RBC casts + HTN",
          },
          {
            id: 3,
            term: "HELLP syndrome",
            match: "Hemolysis + elevated liver enzymes + low platelets",
          },
          {
            id: 4,
            term: "Waterhouse-Friderichsen",
            match: "Meningococcal adrenal hemorrhage",
          },
          {
            id: 5,
            term: "Sheehan syndrome",
            match: "Postpartum pituitary necrosis",
          },
          {
            id: 6,
            term: "Budd-Chiari syndrome",
            match: "Hepatic vein thrombosis",
          },
          {
            id: 7,
            term: "Reye syndrome",
            match: "Aspirin + viral illness in children",
          },
          {
            id: 8,
            term: "Conn syndrome",
            match: "Primary hyperaldosteronism (low renin)",
          },
          {
            id: 9,
            term: "Brown-Séquard syndrome",
            match: "Ipsilateral motor + contralateral pain loss",
          },
          {
            id: 10,
            term: "Horner syndrome",
            match: "Ptosis + miosis + anhidrosis (ipsilateral)",
          },
          {
            id: 11,
            term: "Cauda equina syndrome",
            match: "Saddle anesthesia + bladder/bowel dysfunction",
          },
          {
            id: 12,
            term: "Anterior cord syndrome",
            match: "Loss of motor + pain/temp, preserved proprioception",
          },
        ],
        9: [
          {
            id: 1,
            term: "Whipple's disease",
            match: "Tropheryma whipplei — PAS+ foamy macrophages",
          },
          {
            id: 2,
            term: "Goodpasture syndrome",
            match: "Anti-GBM Ab — type II hypersensitivity",
          },
          {
            id: 3,
            term: "Waldenström's macroglobulinemia",
            match: "IgM paraprotein + hyperviscosity",
          },
          {
            id: 4,
            term: "PNH",
            match: "Absent GPI-anchored proteins (CD55/CD59)",
          },
          {
            id: 5,
            term: "TTP",
            match: "ADAMTS13 deficiency → pentad of symptoms",
          },
          { id: 6, term: "HUS", match: "Shiga toxin → thrombocytopenia + AKI" },
          {
            id: 7,
            term: "Antiphospholipid syndrome",
            match: "Lupus anticoagulant + recurrent clots",
          },
          {
            id: 8,
            term: "Behçet's disease",
            match: "Oral ulcers + genital ulcers + uveitis",
          },
          {
            id: 9,
            term: "IgG4-related disease",
            match: "Multiorgan fibrosis + elevated IgG4",
          },
          {
            id: 10,
            term: "Cryoglobulinemia",
            match: "Hepatitis C association + palpable purpura",
          },
          {
            id: 11,
            term: "Rosai-Dorfman disease",
            match: "Sinus histiocytosis + massive lymphadenopathy",
          },
          {
            id: 12,
            term: "Erdheim-Chester disease",
            match: "Non-Langerhans histiocytosis + bone pain",
          },
        ],
        10: [
          {
            id: 1,
            term: "Polycythemia vera",
            match: "JAK2 + low EPO + splenomegaly",
          },
          {
            id: 2,
            term: "Essential thrombocythemia",
            match: "JAK2/CALR mutation",
          },
          {
            id: 3,
            term: "Primary myelofibrosis",
            match: "Teardrop cells + leukoerythroblastic picture",
          },
          {
            id: 4,
            term: "CML",
            match: "BCR-ABL + Philadelphia chromosome + low LAP score",
          },
          {
            id: 5,
            term: "CLL",
            match: "CD5+ B cells + smudge cells + hypogammaglobulinemia",
          },
          { id: 6, term: "AML", match: "Auer rods + MPO positive" },
          { id: 7, term: "B-ALL", match: "TdT+ CD10+ CALLA — peak age 3-5" },
          {
            id: 8,
            term: "Burkitt lymphoma",
            match: "t(8;14) c-MYC + starry sky pattern",
          },
          {
            id: 9,
            term: "Multiple myeloma",
            match: "M-spike + Bence Jones protein + CRAB criteria",
          },
          {
            id: 10,
            term: "Waldenström macroglobulinemia",
            match: "IgM + Rouleaux + hyperviscosity",
          },
          {
            id: 11,
            term: "Hairy cell leukemia",
            match: "TRAP positive + 'fried egg' morphology",
          },
          {
            id: 12,
            term: "Myelodysplastic syndrome",
            match: "Ringed sideroblasts + dysplasia + cytopenias",
          },
        ],
        11: [
          {
            id: 1,
            term: "BRCA1 mutation",
            match: "Hereditary breast + ovarian + fallopian tube cancer",
          },
          {
            id: 2,
            term: "BRCA2 mutation",
            match: "Breast + ovarian + pancreatic + prostate cancer",
          },
          {
            id: 3,
            term: "APC mutation",
            match: "Familial adenomatous polyposis; WNT pathway; colorectal",
          },
          {
            id: 4,
            term: "MLH1/MSH2/MSH6/PMS2 (Lynch)",
            match:
              "Hereditary nonpolyposis colorectal cancer; microsatellite instability",
          },
          {
            id: 5,
            term: "VHL mutation",
            match:
              "Clear cell renal carcinoma + hemangioblastoma + pheochromocytoma",
          },
          {
            id: 6,
            term: "RET mutation",
            match: "MEN2A (medullary thyroid Ca + pheo + parathyroid); MEN2B",
          },
          {
            id: 7,
            term: "MEN1 mutation",
            match: "Parathyroid + pituitary + pancreatic islet tumors (3 P's)",
          },
          {
            id: 8,
            term: "NF1 (neurofibromin)",
            match:
              "Neurofibromatosis type 1; café-au-lait + neurofibromas + Lisch nodules",
          },
          {
            id: 9,
            term: "NF2 (merlin)",
            match:
              "Neurofibromatosis type 2; bilateral acoustic neuromas + meningiomas",
          },
          {
            id: 10,
            term: "TP53 mutation (Li-Fraumeni)",
            match: "Multiple early-onset cancers; 'guardian of the genome'",
          },
          {
            id: 11,
            term: "PTEN mutation (Cowden)",
            match: "Hamartomas + breast + endometrial + thyroid cancer risk",
          },
          {
            id: 12,
            term: "HFE mutation (C282Y)",
            match: "Hereditary hemochromatosis; iron overload; bronze diabetes",
          },
        ],
        12: [
          {
            id: 1,
            term: "CEA",
            match:
              "Colorectal + gastric + pancreatic + lung + breast (monitoring)",
          },
          {
            id: 2,
            term: "AFP",
            match:
              "Hepatocellular carcinoma + yolk sac tumor (non-seminomatous GCT)",
          },
          {
            id: 3,
            term: "Beta-hCG",
            match: "Choriocarcinoma + gestational trophoblastic disease",
          },
          {
            id: 4,
            term: "CA 125",
            match: "Epithelial ovarian cancer (monitoring; not screening)",
          },
          {
            id: 5,
            term: "CA 19-9",
            match: "Pancreatic adenocarcinoma (monitoring)",
          },
          {
            id: 6,
            term: "PSA",
            match: "Prostate cancer screening + monitoring",
          },
          {
            id: 7,
            term: "Calcitonin",
            match: "Medullary thyroid carcinoma marker",
          },
          {
            id: 8,
            term: "Chromogranin A",
            match: "Carcinoid + all neuroendocrine tumors",
          },
          {
            id: 9,
            term: "PLAP (placental alkaline phosphatase)",
            match: "Seminoma marker",
          },
          {
            id: 10,
            term: "S-100 protein",
            match:
              "Melanoma + schwannoma + granular cell tumor + Langerhans cells",
          },
          {
            id: 11,
            term: "CD45 (LCA)",
            match: "Lymphomas; distinguishes from carcinomas on IHC",
          },
          {
            id: 12,
            term: "Vimentin",
            match: "Mesenchymal tumors: sarcomas, mesothelioma, RCC",
          },
        ],
      },
      5: {
        // Level 5 - 14 pairs each
        1: [
          { id: 1, term: "Diabetes", match: "Hyperglycemia" },
          { id: 2, term: "Hypertension", match: "High BP" },
          { id: 3, term: "Asthma", match: "Wheezing" },
          { id: 4, term: "Anemia", match: "Fatigue & pallor" },
          { id: 5, term: "Pneumonia", match: "Cough & fever" },
          { id: 6, term: "GERD", match: "Heartburn" },
          { id: 7, term: "Migraine", match: "Severe headache" },
          { id: 8, term: "Arthritis", match: "Joint pain" },
          { id: 9, term: "Heart failure", match: "Edema & dyspnea" },
          { id: 10, term: "Cirrhosis", match: "Liver scarring" },
          { id: 11, term: "Stroke", match: "Sudden weakness" },
          { id: 12, term: "Epilepsy", match: "Seizures" },
          { id: 13, term: "Lupus", match: "Butterfly rash" },
          { id: 14, term: "Crohn's disease", match: "Skip lesions" },
        ],
        2: [
          { id: 1, term: "Guillain-Barré", match: "Ascending paralysis" },
          { id: 2, term: "Myasthenia gravis", match: "Fatigable weakness" },
          { id: 3, term: "ALS", match: "UMN + LMN signs" },
          { id: 4, term: "Huntington's", match: "Chorea" },
          { id: 5, term: "Bell's palsy", match: "Facial droop" },
          { id: 6, term: "Trigeminal neuralgia", match: "Lancinating pain" },
          { id: 7, term: "Carpal tunnel", match: "Median nerve" },
          { id: 8, term: "Peripheral neuropathy", match: "Stocking-glove" },
          { id: 9, term: "Normal pressure hydro", match: "Wet, wobbly, wacky" },
          { id: 10, term: "Subdural hematoma", match: "Crescent shape" },
          { id: 11, term: "Epidural hematoma", match: "Lens shape" },
          { id: 12, term: "Subarachnoid", match: "Thunderclap headache" },
          { id: 13, term: "Brain abscess", match: "Ring-enhancing" },
          { id: 14, term: "Encephalitis", match: "Altered consciousness" },
        ],
        3: [
          { id: 1, term: "Aortic dissection", match: "Tearing chest pain" },
          { id: 2, term: "Aortic aneurysm", match: "Pulsatile mass" },
          { id: 3, term: "PAD", match: "Claudication" },
          { id: 4, term: "Raynaud's", match: "Color changes fingers" },
          { id: 5, term: "Varicose veins", match: "Dilated leg veins" },
          { id: 6, term: "Chronic venous", match: "Leg ulcers" },
          { id: 7, term: "Vasculitis", match: "Palpable purpura" },
          { id: 8, term: "Giant cell arteritis", match: "Temporal headache" },
          { id: 9, term: "Takayasu arteritis", match: "Pulseless disease" },
          { id: 10, term: "Buerger's disease", match: "Smoking + ischemia" },
          { id: 11, term: "Polyarteritis nodosa", match: "Hepatitis B assoc" },
          { id: 12, term: "Kawasaki disease", match: "Mucocutaneous" },
          { id: 13, term: "Granulomatosis", match: "C-ANCA positive" },
          { id: 14, term: "Henoch-Schönlein", match: "IgA nephropathy" },
        ],
        4: [
          { id: 1, term: "Paget's disease", match: "Elevated ALP" },
          { id: 2, term: "Osteomyelitis", match: "Bone infection" },
          { id: 3, term: "Osteosarcoma", match: "Codman triangle" },
          { id: 4, term: "Ewing sarcoma", match: "Onion skin" },
          { id: 5, term: "Multiple myeloma", match: "Punched-out lesions" },
          { id: 6, term: "Ankylosing spondylitis", match: "Bamboo spine" },
          { id: 7, term: "Psoriatic arthritis", match: "Sausage digits" },
          { id: 8, term: "Reactive arthritis", match: "Can't see, pee, climb" },
          { id: 9, term: "Polymyalgia rheumatica", match: "Morning stiffness" },
          { id: 10, term: "Dermatomyositis", match: "Heliotrope rash" },
          { id: 11, term: "Polymyositis", match: "Proximal weakness" },
          { id: 12, term: "Scleroderma", match: "Skin thickening" },
          { id: 13, term: "Sjögren's syndrome", match: "Dry eyes/mouth" },
          { id: 14, term: "Mixed connective", match: "Anti-U1 RNP" },
        ],
        5: [
          { id: 1, term: "Type 1 DM", match: "Autoimmune beta cell" },
          { id: 2, term: "Type 2 DM", match: "Insulin resistance" },
          { id: 3, term: "Graves' disease", match: "TSI positive" },
          { id: 4, term: "Hashimoto's", match: "Anti-TPO" },
          { id: 5, term: "Primary hyperaldosteronism", match: "Conn syndrome" },
          { id: 6, term: "Primary hyperparathyroid", match: "Elevated PTH/Ca" },
          { id: 7, term: "Secondary hyperparathyroid", match: "CKD related" },
          { id: 8, term: "MEN1", match: "3 Ps" },
          { id: 9, term: "MEN2A", match: "Medullary thyroid" },
          { id: 10, term: "Carcinoid syndrome", match: "Flushing & diarrhea" },
          { id: 11, term: "Zollinger-Ellison", match: "Gastrinoma" },
          { id: 12, term: "Insulinoma", match: "Hypoglycemia" },
          { id: 13, term: "Prolactinoma", match: "Galactorrhea" },
          { id: 14, term: "Acromegaly", match: "Excess GH" },
        ],
        6: [
          { id: 1, term: "Myocardial infarction", match: "ST elevation" },
          {
            id: 2,
            term: "Atrial fibrillation",
            match: "Irregularly irregular",
          },
          { id: 3, term: "Heart failure", match: "S3 + edema" },
          { id: 4, term: "Aortic stenosis", match: "Harsh systolic murmur" },
          { id: 5, term: "Pulmonary embolism", match: "Pleuritic chest pain" },
          { id: 6, term: "COPD", match: "Chronic cough" },
          { id: 7, term: "Asthma", match: "Wheezing" },
          { id: 8, term: "Pneumonia", match: "Lobar consolidation" },
          { id: 9, term: "Cirrhosis", match: "Ascites" },
          { id: 10, term: "Pancreatitis", match: "Elevated lipase" },
          { id: 11, term: "Appendicitis", match: "RLQ pain" },
          { id: 12, term: "Kidney stones", match: "Colicky flank pain" },
          { id: 13, term: "Pyelonephritis", match: "Fever + CVA tenderness" },
          { id: 14, term: "Sepsis", match: "Hypotension + lactate" },
        ],
        7: [
          { id: 1, term: "SIADH", match: "SCLC paraneoplastic" },
          { id: 2, term: "Lambert-Eaton", match: "Voltage-gated Ca2+ Ab" },
          { id: 3, term: "Trousseau sign", match: "Pancreatic cancer" },
          { id: 4, term: "Sweet syndrome", match: "AML association" },
          {
            id: 5,
            term: "Necrolytic migratory erythema",
            match: "Glucagonoma",
          },
          { id: 6, term: "Acanthosis nigricans", match: "Insulin resistance" },
          { id: 7, term: "Pyoderma gangrenosum", match: "IBD association" },
          { id: 8, term: "Erythema nodosum", match: "Sarcoidosis / IBD" },
          { id: 9, term: "Gottron's papules", match: "Dermatomyositis" },
          { id: 10, term: "Leser-Trélat sign", match: "GI malignancy" },
          {
            id: 11,
            term: "Bazex syndrome",
            match: "Acral keratosis / oropharyngeal Ca",
          },
          { id: 12, term: "Eruptive xanthomas", match: "Hypertriglyceridemia" },
          {
            id: 13,
            term: "Primary sclerosing cholangitis",
            match: "Ulcerative colitis",
          },
          { id: 14, term: "Dermatitis herpetiformis", match: "Celiac disease" },
        ],
        8: [
          {
            id: 1,
            term: "Anti-dsDNA antibody",
            match: "SLE; correlates with disease activity",
          },
          {
            id: 2,
            term: "Anti-Sm antibody",
            match: "SLE (most specific); snRNP proteins",
          },
          {
            id: 3,
            term: "Anti-histone antibody",
            match: "Drug-induced lupus (hydralazine, procainamide, isoniazid)",
          },
          {
            id: 4,
            term: "Anti-SSA (Ro)",
            match: "Sjögren's; neonatal lupus with congenital heart block",
          },
          {
            id: 5,
            term: "Anti-SSB (La)",
            match: "Sjögren's syndrome; less specific than anti-Ro",
          },
          {
            id: 6,
            term: "Anti-Scl-70 (anti-topo I)",
            match: "Diffuse systemic sclerosis (dcSSc)",
          },
          {
            id: 7,
            term: "Anti-centromere",
            match: "Limited systemic sclerosis / CREST syndrome",
          },
          {
            id: 8,
            term: "Anti-Jo-1",
            match: "Polymyositis/dermatomyositis (antisynthetase syndrome)",
          },
          {
            id: 9,
            term: "C-ANCA (PR3-ANCA)",
            match: "Granulomatosis with polyangiitis (Wegener's)",
          },
          {
            id: 10,
            term: "P-ANCA (MPO-ANCA)",
            match:
              "Microscopic polyangiitis + eosinophilic GPA (Churg-Strauss)",
          },
          {
            id: 11,
            term: "Anti-GBM antibody",
            match: "Goodpasture syndrome; linear IgG on immunofluorescence",
          },
          {
            id: 12,
            term: "Anti-endomysial / anti-tTG",
            match: "Celiac disease; IgA class",
          },
          {
            id: 13,
            term: "Anti-mitochondrial (AMA)",
            match: "Primary biliary cholangitis (PBC); M2 antigen",
          },
          {
            id: 14,
            term: "Anti-acetylcholine receptor",
            match: "Myasthenia gravis; neuromuscular junction",
          },
        ],
        9: [
          {
            id: 1,
            term: "SIADH → SCLC",
            match: "Ectopic ADH production (most common cause)",
          },
          {
            id: 2,
            term: "Cushing's syndrome → SCLC",
            match: "Ectopic ACTH production",
          },
          {
            id: 3,
            term: "Hypercalcemia → SCC lung / RCC",
            match: "PTHrP (PTH-related protein) secretion",
          },
          {
            id: 4,
            term: "Polycythemia → RCC / HCC",
            match: "Ectopic erythropoietin production",
          },
          {
            id: 5,
            term: "Lambert-Eaton syndrome → SCLC",
            match:
              "Anti-VGCC antibodies; proximal weakness improves with repeat",
          },
          {
            id: 6,
            term: "Cerebellar degeneration → ovarian/breast Ca",
            match: "Anti-Yo antibodies",
          },
          {
            id: 7,
            term: "Limbic encephalitis → SCLC",
            match: "Anti-Hu antibodies; memory loss + personality change",
          },
          {
            id: 8,
            term: "Dermatomyositis → GI/lung/ovarian Ca",
            match: "Occult malignancy in > 50% adults with dermatomyositis",
          },
          {
            id: 9,
            term: "Acanthosis nigricans → gastric Ca",
            match:
              "Also insulin resistance; symmetric velvety hyperpigmentation",
          },
          {
            id: 10,
            term: "Trousseau's sign (thrombophlebitis)",
            match: "Migratory thrombophlebitis; pancreatic adenocarcinoma",
          },
          {
            id: 11,
            term: "Hypertrophic pulmonary osteoarthropathy",
            match: "NSCLC; periosteal reaction of long bones; finger clubbing",
          },
          {
            id: 12,
            term: "Opsoclonus-myoclonus",
            match: "Neuroblastoma (children) or SCLC (adults)",
          },
          {
            id: 13,
            term: "Nephrotic syndrome (membranous GN)",
            match: "Solid tumors: colon, lung, gastric; anti-PLA2R",
          },
          {
            id: 14,
            term: "Carcinoid syndrome",
            match:
              "Serotonin + other mediators; flushing + diarrhea + right heart lesions",
          },
        ],
        10: [
          {
            id: 1,
            term: "Down syndrome (Trisomy 21)",
            match: "AV septal defect + Alzheimer risk + leukemia; maternal age",
          },
          {
            id: 2,
            term: "Edwards syndrome (Trisomy 18)",
            match: "Rocker-bottom feet + VSD + overlapping fingers; lethal",
          },
          {
            id: 3,
            term: "Patau syndrome (Trisomy 13)",
            match: "Holoprosencephaly + cleft palate + polydactyly; lethal",
          },
          {
            id: 4,
            term: "Turner syndrome (45,X)",
            match: "Coarctation of aorta + webbed neck + primary amenorrhea",
          },
          {
            id: 5,
            term: "Klinefelter (47,XXY)",
            match:
              "Hypogonadism + gynecomastia + tall + infertile; Barr body present",
          },
          {
            id: 6,
            term: "DiGeorge syndrome (22q11)",
            match: "Absent thymus + hypocalcemia + conotruncal cardiac defects",
          },
          {
            id: 7,
            term: "Prader-Willi (paternal 15q11)",
            match:
              "Hyperphagia + obesity + hypogonadism; maternal 15 expressed",
          },
          {
            id: 8,
            term: "Angelman (maternal 15q11)",
            match:
              "'Happy puppet'; seizures + absent speech; paternal 15 expressed",
          },
          {
            id: 9,
            term: "Williams syndrome (7q11)",
            match: "Elfin facies + supravalvular aortic stenosis + loquacious",
          },
          {
            id: 10,
            term: "Fragile X syndrome (FMR1 CGG)",
            match:
              "Most common inherited intellectual disability; macroorchidism + large ears",
          },
          {
            id: 11,
            term: "Beckwith-Wiedemann (IGF2 overexpression)",
            match: "Macrosomia + omphalocele + Wilms tumor risk",
          },
          {
            id: 12,
            term: "VACTERL association",
            match:
              "Vertebral + Anal atresia + Cardiac + TE fistula + Esophageal + Renal + Limb",
          },
          {
            id: 13,
            term: "Myotonic dystrophy (CTG repeat)",
            match:
              "Grip myotonia + cardiac conduction + cataracts; autosomal dominant",
          },
          {
            id: 14,
            term: "Neurofibromatosis type 1 (NF1)",
            match:
              "Café-au-lait > 6 + neurofibromas + Lisch nodules + optic glioma",
          },
        ],
      },
    },
    physiology: {
      1: {
        // Level 1 - 6 pairs each
        1: [
          { id: 1, term: "Thyroid", match: "T3 & T4" },
          { id: 2, term: "Pancreas", match: "Insulin" },
          { id: 3, term: "Adrenal cortex", match: "Cortisol" },
          { id: 4, term: "Pituitary", match: "Growth hormone" },
          { id: 5, term: "Parathyroid", match: "PTH" },
          { id: 6, term: "Pineal", match: "Melatonin" },
        ],
        2: [
          { id: 1, term: "SA node", match: "Pacemaker" },
          { id: 2, term: "AV node", match: "Delays impulse" },
          { id: 3, term: "Cardiac output", match: "HR x SV" },
          { id: 4, term: "Preload", match: "EDV" },
          { id: 5, term: "Afterload", match: "Vascular resistance" },
          { id: 6, term: "Contractility", match: "Force of squeeze" },
        ],
        3: [
          { id: 1, term: "Tidal volume", match: "Normal breath" },
          { id: 2, term: "Vital capacity", match: "Max exhale" },
          { id: 3, term: "FEV1", match: "1 sec exhale" },
          { id: 4, term: "Residual volume", match: "Air remaining" },
          { id: 5, term: "Surfactant", match: "Reduces tension" },
          { id: 6, term: "Compliance", match: "Lung stretchiness" },
        ],
        4: [
          { id: 1, term: "GFR", match: "Filtration rate" },
          { id: 2, term: "Creatinine", match: "GFR marker" },
          { id: 3, term: "Aldosterone", match: "Na retention" },
          { id: 4, term: "ADH", match: "Water retention" },
          { id: 5, term: "Renin", match: "BP regulation" },
          { id: 6, term: "Angiotensin II", match: "Vasoconstriction" },
        ],
        5: [
          { id: 1, term: "Pepsin", match: "Protein digestion" },
          { id: 2, term: "Amylase", match: "Starch digestion" },
          { id: 3, term: "Lipase", match: "Fat digestion" },
          { id: 4, term: "Bile", match: "Fat emulsification" },
          { id: 5, term: "Intrinsic factor", match: "B12 absorption" },
          { id: 6, term: "Gastrin", match: "Acid secretion" },
        ],
        6: [
          { id: 1, term: "Insulin", match: "Lowers glucose" },
          { id: 2, term: "Glucagon", match: "Raises glucose" },
          { id: 3, term: "ADH", match: "Water retention" },
          { id: 4, term: "Aldosterone", match: "Na retention" },
          { id: 5, term: "Erythropoietin", match: "RBC production" },
          { id: 6, term: "Renin", match: "RAAS activation" },
        ],
        7: [
          { id: 1, term: "Erythropoietin", match: "RBC production" },
          { id: 2, term: "Renin", match: "Angiotensin activation" },
          { id: 3, term: "ANP", match: "Natriuresis" },
          { id: 4, term: "BNP", match: "Ventricular stretch marker" },
          { id: 5, term: "Leptin", match: "Satiety signal" },
          { id: 6, term: "Ghrelin", match: "Hunger hormone" },
        ],
        8: [
          { id: 1, term: "Stretch reflex", match: "Monosynaptic arc" },
          { id: 2, term: "Golgi tendon reflex", match: "Inhibits contraction" },
          { id: 3, term: "Withdrawal reflex", match: "Pain response" },
          { id: 4, term: "Crossed extensor", match: "Contralateral support" },
          { id: 5, term: "Pupillary light reflex", match: "CN II & III" },
          { id: 6, term: "Gag reflex", match: "CN IX & X" },
        ],
        9: [
          { id: 1, term: "Intracellular fluid", match: "67% of body water" },
          { id: 2, term: "Extracellular fluid", match: "33% of body water" },
          { id: 3, term: "Plasma volume", match: "25% of ECF" },
          { id: 4, term: "Interstitial fluid", match: "75% of ECF" },
          { id: 5, term: "Isotonic fluid loss", match: "Diarrhea / vomiting" },
          {
            id: 6,
            term: "Hypertonic fluid loss",
            match: "Insensible perspiration",
          },
        ],
        10: [
          {
            id: 1,
            term: "High cortisol",
            match: "Suppresses CRH + ACTH (negative feedback)",
          },
          { id: 2, term: "High T3/T4", match: "Suppresses TSH + TRH" },
          {
            id: 3,
            term: "Late-follicular estrogen peak",
            match: "Positive feedback → LH surge",
          },
          {
            id: 4,
            term: "High PTH",
            match: "Raises Ca2+ → negative feedback on PTH",
          },
          {
            id: 5,
            term: "High aldosterone",
            match: "Volume expansion → suppresses renin",
          },
          {
            id: 6,
            term: "High glucose",
            match: "Stimulates insulin → lowers glucose",
          },
        ],
        11: [
          {
            id: 1,
            term: "Insulin receptor",
            match: "Tyrosine kinase receptor",
          },
          {
            id: 2,
            term: "Growth hormone receptor",
            match: "JAK/STAT signaling pathway",
          },
          {
            id: 3,
            term: "Cortisol receptor",
            match: "Nuclear (intracellular) receptor",
          },
          {
            id: 4,
            term: "Epinephrine receptor",
            match: "G protein-coupled receptor (GPCR)",
          },
          {
            id: 5,
            term: "Acetylcholine (NMJ)",
            match: "Ligand-gated ion channel (nicotinic)",
          },
          { id: 6, term: "TSH receptor", match: "Gs-coupled GPCR → cAMP" },
        ],
        12: [
          {
            id: 1,
            term: "Intestinal glucose absorption",
            match: "SGLT1 — Na-glucose cotransport",
          },
          {
            id: 2,
            term: "Cellular glucose uptake",
            match: "GLUT transporters (facilitated diffusion)",
          },
          {
            id: 3,
            term: "Na+ reabsorption (basolateral)",
            match: "Na/K-ATPase (primary active transport)",
          },
          {
            id: 4,
            term: "HCO3⁻ across RBC membrane",
            match: "Cl⁻/HCO3⁻ exchanger (band 3)",
          },
          {
            id: 5,
            term: "O2 across alveolar membrane",
            match: "Simple diffusion (down gradient)",
          },
          {
            id: 6,
            term: "Vitamin B12 absorption",
            match: "Requires intrinsic factor (receptor-mediated)",
          },
        ],
        13: [
          {
            id: 1,
            term: "GnRH (pulsatile)",
            match: "Stimulates LH + FSH from anterior pituitary",
          },
          {
            id: 2,
            term: "LH surge",
            match: "Triggered by high estradiol; causes ovulation",
          },
          {
            id: 3,
            term: "FSH function",
            match:
              "Follicle development + granulosa cell aromatase stimulation",
          },
          {
            id: 4,
            term: "Progesterone (luteal phase)",
            match: "Maintains endometrium; negative feedback on LH + FSH",
          },
          {
            id: 5,
            term: "PTH effects",
            match:
              "↑bone resorption + ↑renal Ca2+ reabsorption + ↑1-alpha hydroxylase",
          },
          {
            id: 6,
            term: "Calcitonin",
            match: "Opposes PTH; ↓bone resorption; from parafollicular C cells",
          },
        ],
        14: [
          {
            id: 1,
            term: "Action potential (neuron)",
            match: "Rapid Na+ influx → depolarization (phase 0)",
          },
          {
            id: 2,
            term: "Repolarization (neuron)",
            match: "K+ efflux restores negative membrane potential",
          },
          {
            id: 3,
            term: "Resting membrane potential",
            match: "-70 mV; maintained by Na/K-ATPase",
          },
          {
            id: 4,
            term: "Threshold potential",
            match: "-55 mV; if reached, AP fires (all-or-none)",
          },
          {
            id: 5,
            term: "Saltatory conduction",
            match: "AP jumps between nodes of Ranvier; faster",
          },
          {
            id: 6,
            term: "Refractory period (absolute)",
            match:
              "Na+ channels inactivated; no AP possible regardless of stimulus",
          },
        ],
        15: [
          {
            id: 1,
            term: "Frank-Starling mechanism",
            match: "↑preload → ↑stretch → ↑stroke volume (up to a point)",
          },
          {
            id: 2,
            term: "Preload determinants",
            match: "Venous return + blood volume + venous tone",
          },
          {
            id: 3,
            term: "Afterload determinants",
            match: "Systemic vascular resistance + aortic valve resistance",
          },
          {
            id: 4,
            term: "Cardiac output equation",
            match: "CO = stroke volume × heart rate",
          },
          {
            id: 5,
            term: "Mean arterial pressure",
            match: "MAP = CO × SVR; or (SBP + 2×DBP) / 3",
          },
          {
            id: 6,
            term: "Pulse pressure",
            match: "SBP − DBP; ↑in aortic regurgitation + complete heart block",
          },
        ],
        16: [
          { id: 1, term: "Tidal volume", match: "Normal breathing; ~500 mL" },
          {
            id: 2,
            term: "Vital capacity",
            match: "TLC − RV; maximum exhale after maximum inhale; ~4800 mL",
          },
          {
            id: 3,
            term: "FEV1/FVC < 0.7",
            match: "Obstructive pattern (COPD, asthma)",
          },
          {
            id: 4,
            term: "FEV1/FVC > 0.7 with reduced FVC",
            match: "Restrictive pattern (fibrosis, obesity)",
          },
          {
            id: 5,
            term: "Residual volume",
            match:
              "Air remaining after maximal expiration; unmeasurable by spirometry",
          },
          {
            id: 6,
            term: "Compliance ↑ (emphysema)",
            match: "Loss of elastic recoil; easier to inflate; air trapping",
          },
        ],
        17: [
          {
            id: 1,
            term: "Gastrin (G cells)",
            match:
              "↑HCl + pepsinogen + motility; stimulated by protein + vagus",
          },
          {
            id: 2,
            term: "Secretin (S cells)",
            match: "↑pancreatic HCO3-; triggered by acidic chyme in duodenum",
          },
          {
            id: 3,
            term: "CCK (I cells)",
            match:
              "↑pancreatic enzymes + gallbladder contraction; triggered by fat/protein",
          },
          {
            id: 4,
            term: "GIP (K cells)",
            match: "Glucose-dependent insulinotropic peptide; incretin effect",
          },
          {
            id: 5,
            term: "GLP-1 (L cells)",
            match:
              "Major incretin; ↑insulin, ↓glucagon; delays gastric emptying",
          },
          {
            id: 6,
            term: "Somatostatin (D cells)",
            match: "Inhibits gastrin, secretin, CCK, insulin, glucagon, GH",
          },
        ],
        18: [
          {
            id: 1,
            term: "Iron absorption",
            match:
              "Duodenum; ferrous (Fe2+); enhanced by vitamin C; decreased by Ca2+ + tannins",
          },
          {
            id: 2,
            term: "Calcium absorption",
            match: "Duodenum; calcitriol (1,25-OH2D) stimulates TRPV6 channel",
          },
          {
            id: 3,
            term: "B12 (cobalamin) absorption",
            match:
              "Terminal ileum; requires intrinsic factor from parietal cells",
          },
          {
            id: 4,
            term: "Fat-soluble vitamins (ADEK)",
            match:
              "Require bile salts for micelle formation; absorbed in jejunum",
          },
          {
            id: 5,
            term: "Folate absorption",
            match:
              "Proximal small intestine; reduced by methotrexate + trimethoprim",
          },
          {
            id: 6,
            term: "Bile salt recycling (enterohepatic)",
            match:
              "Absorbed in terminal ileum; 95% recycled; disrupted by ileal resection",
          },
        ],
      },
      2: {
        // Level 2 - 8 pairs each
        1: [
          { id: 1, term: "Thyroid", match: "T3 & T4" },
          { id: 2, term: "Pancreas", match: "Insulin" },
          { id: 3, term: "Adrenal cortex", match: "Cortisol" },
          { id: 4, term: "Pituitary", match: "Growth hormone" },
          { id: 5, term: "Parathyroid", match: "PTH" },
          { id: 6, term: "Pineal", match: "Melatonin" },
          { id: 7, term: "Ovaries", match: "Estrogen" },
          { id: 8, term: "Testes", match: "Testosterone" },
        ],
        2: [
          { id: 1, term: "Sympathetic", match: "Fight or flight" },
          { id: 2, term: "Parasympathetic", match: "Rest and digest" },
          { id: 3, term: "Norepinephrine", match: "Sympathetic NT" },
          { id: 4, term: "Acetylcholine", match: "Parasympathetic NT" },
          { id: 5, term: "Dopamine", match: "Reward pathway" },
          { id: 6, term: "Serotonin", match: "Mood regulation" },
          { id: 7, term: "GABA", match: "Inhibitory NT" },
          { id: 8, term: "Glutamate", match: "Excitatory NT" },
        ],
        3: [
          { id: 1, term: "Frank-Starling", match: "Length-tension" },
          { id: 2, term: "Mean arterial pressure", match: "CO x SVR" },
          { id: 3, term: "Baroreceptors", match: "BP sensors" },
          { id: 4, term: "Chemoreceptors", match: "O2/CO2 sensors" },
          { id: 5, term: "Stroke volume", match: "EDV - ESV" },
          { id: 6, term: "Ejection fraction", match: "SV/EDV" },
          { id: 7, term: "Pulse pressure", match: "Systolic - diastolic" },
          { id: 8, term: "Venous return", match: "Blood to heart" },
        ],
        4: [
          { id: 1, term: "Hemoglobin", match: "O2 carrier" },
          { id: 2, term: "Oxyhemoglobin", match: "O2 bound Hb" },
          { id: 3, term: "Carbaminohemoglobin", match: "CO2 bound Hb" },
          { id: 4, term: "Bohr effect", match: "pH affects O2 binding" },
          { id: 5, term: "2,3-DPG", match: "Right shift" },
          { id: 6, term: "Methemoglobin", match: "Oxidized Fe" },
          { id: 7, term: "CO poisoning", match: "Left shift" },
          { id: 8, term: "P50", match: "50% saturation" },
        ],
        5: [
          { id: 1, term: "Gluconeogenesis", match: "New glucose" },
          { id: 2, term: "Glycogenolysis", match: "Glycogen breakdown" },
          { id: 3, term: "Glycolysis", match: "Glucose to pyruvate" },
          { id: 4, term: "Krebs cycle", match: "ATP production" },
          { id: 5, term: "Beta oxidation", match: "Fat metabolism" },
          { id: 6, term: "Ketogenesis", match: "Ketone formation" },
          { id: 7, term: "Glucagon", match: "Raises glucose" },
          { id: 8, term: "Insulin", match: "Lowers glucose" },
        ],
        6: [
          { id: 1, term: "Tidal volume", match: "Normal breath" },
          { id: 2, term: "Vital capacity", match: "Max exhale" },
          { id: 3, term: "FEV1", match: "1 sec exhale" },
          { id: 4, term: "Residual volume", match: "Air remaining" },
          { id: 5, term: "Surfactant", match: "Reduces tension" },
          { id: 6, term: "Compliance", match: "Lung stretchiness" },
          { id: 7, term: "Dead space", match: "No gas exchange" },
          { id: 8, term: "Minute ventilation", match: "RR x TV" },
        ],
        7: [
          { id: 1, term: "Starling's law", match: "Preload increases CO" },
          { id: 2, term: "Laplace's law", match: "Wall tension = Pr/2h" },
          { id: 3, term: "Fick's principle", match: "O2 consumption via fick" },
          { id: 4, term: "Poiseuille's law", match: "Flow ∝ r⁴" },
          {
            id: 5,
            term: "Henderson-Hasselbalch",
            match: "pH = pKa + log [A-/HA]",
          },
          { id: 6, term: "Bohr effect", match: "CO2 shifts O2 curve right" },
          { id: 7, term: "Haldane effect", match: "O2 displaces CO2" },
          { id: 8, term: "Ohm's law (cardiac)", match: "MAP = CO × SVR" },
        ],
        8: [
          { id: 1, term: "P wave", match: "Atrial depolarization" },
          { id: 2, term: "QRS complex", match: "Ventricular depolarization" },
          { id: 3, term: "T wave", match: "Ventricular repolarization" },
          { id: 4, term: "PR interval", match: "AV conduction time" },
          { id: 5, term: "QT interval", match: "Total ventricular activity" },
          { id: 6, term: "ST elevation", match: "STEMI pattern" },
          { id: 7, term: "U wave", match: "Hypokalemia sign" },
          { id: 8, term: "Delta wave", match: "WPW syndrome" },
        ],
        9: [
          { id: 1, term: "Erythrocytes", match: "Oxygen transport" },
          { id: 2, term: "Neutrophils", match: "Bacterial phagocytosis" },
          { id: 3, term: "Eosinophils", match: "Parasite defense" },
          { id: 4, term: "Basophils", match: "IgE receptor cells" },
          { id: 5, term: "Monocytes", match: "Become macrophages" },
          { id: 6, term: "Lymphocytes", match: "Adaptive immunity" },
          { id: 7, term: "Platelets", match: "Hemostasis" },
          { id: 8, term: "Plasma cells", match: "Antibody secretion" },
        ],
        10: [
          {
            id: 1,
            term: "G6PD deficiency",
            match: "Heinz bodies + hemolytic anemia",
          },
          {
            id: 2,
            term: "Pyruvate kinase deficiency",
            match: "Hemolytic anemia (splenomegaly)",
          },
          {
            id: 3,
            term: "Adenosine deaminase deficiency",
            match: "SCID (no purine salvage)",
          },
          {
            id: 4,
            term: "Phenylalanine hydroxylase deficiency",
            match: "PKU (phenylketonuria)",
          },
          {
            id: 5,
            term: "Hexosaminidase A deficiency",
            match: "Tay-Sachs disease",
          },
          {
            id: 6,
            term: "Alpha-1 antitrypsin deficiency",
            match: "Emphysema + cirrhosis",
          },
          {
            id: 7,
            term: "Homogentisate oxidase deficiency",
            match: "Alkaptonuria (dark urine)",
          },
          {
            id: 8,
            term: "OTC deficiency (urea cycle)",
            match: "X-linked hyperammonemia",
          },
        ],
        11: [
          { id: 1, term: "Low pH + high CO2", match: "Respiratory acidosis" },
          { id: 2, term: "High pH + low CO2", match: "Respiratory alkalosis" },
          { id: 3, term: "Low pH + low HCO3", match: "Metabolic acidosis" },
          { id: 4, term: "High pH + high HCO3", match: "Metabolic alkalosis" },
          {
            id: 5,
            term: "High anion gap metabolic acidosis",
            match: "MUDPILES mnemonic",
          },
          { id: 6, term: "Vomiting", match: "Metabolic alkalosis — lose H+" },
          { id: 7, term: "Diarrhea", match: "Metabolic acidosis — lose HCO3⁻" },
          {
            id: 8,
            term: "Hyperventilation",
            match: "Respiratory alkalosis — blow off CO2",
          },
        ],
        12: [
          {
            id: 1,
            term: "Increased CO2",
            match: "Right shift (decreased O2 affinity — Bohr)",
          },
          { id: 2, term: "Decreased pH", match: "Right shift (Bohr effect)" },
          { id: 3, term: "Increased temperature", match: "Right shift" },
          {
            id: 4,
            term: "Increased 2,3-DPG",
            match: "Right shift (high altitude, chronic anemia)",
          },
          {
            id: 5,
            term: "Carbon monoxide",
            match: "Left shift + decreased O2 capacity",
          },
          {
            id: 6,
            term: "Fetal hemoglobin (HbF)",
            match: "Left shift — high O2 affinity",
          },
          { id: 7, term: "Methemoglobin", match: "Left shift + oxidized Fe3+" },
          {
            id: 8,
            term: "Alkalosis",
            match: "Left shift (increased O2 affinity)",
          },
        ],
        13: [
          {
            id: 1,
            term: "Proximal tubule reabsorption",
            match: "67% of Na+, water; 100% glucose + amino acids; 90% HCO3-",
          },
          {
            id: 2,
            term: "Thick ascending loop (NKCC2)",
            match:
              "Furosemide target; impermeable to water; dilutes tubular fluid",
          },
          {
            id: 3,
            term: "DCT (NCC cotransporter)",
            match: "Thiazide target; fine Na+ reabsorption; Mg2+ reabsorption",
          },
          {
            id: 4,
            term: "Collecting duct principal cells",
            match: "ENaC + ROMK; aldosterone-sensitive; aquaporin-2 (ADH)",
          },
          {
            id: 5,
            term: "Intercalated cells (type A)",
            match: "H+ secretion + HCO3- reabsorption; distal acidification",
          },
          {
            id: 6,
            term: "Tubuloglomerular feedback",
            match:
              "Macula densa ↑NaCl → ↓GFR (afferent arteriole constriction)",
          },
          {
            id: 7,
            term: "Countercurrent multiplier",
            match: "Loop of Henle creates medullary osmotic gradient",
          },
          {
            id: 8,
            term: "FeNa < 1%",
            match: "Pre-renal azotemia (avid Na+ reabsorption)",
          },
        ],
        14: [
          {
            id: 1,
            term: "Hemorrhagic shock physiology",
            match:
              "↑SVR + tachycardia via baroreceptor reflex + ADH/RAAS activation",
          },
          {
            id: 2,
            term: "Septic shock (early/warm)",
            match: "↓SVR + ↑CO; vasodilation from cytokines; warm extremities",
          },
          {
            id: 3,
            term: "Cardiogenic shock",
            match: "↓CO + ↑PCWP + ↑SVR; poor perfusion despite volume",
          },
          {
            id: 4,
            term: "Tension pneumothorax physiology",
            match:
              "↑intrathoracic pressure → ↓venous return → obstructive shock",
          },
          {
            id: 5,
            term: "Aortic stenosis physiology",
            match: "Pressure overload → concentric LVH; fixed cardiac output",
          },
          {
            id: 6,
            term: "Mitral regurgitation physiology",
            match:
              "Volume overload → eccentric LVH; ↑LA pressure → pulmonary HTN",
          },
          {
            id: 7,
            term: "Systolic heart failure (HFrEF)",
            match:
              "Low EF + ↑LVEDP; compensated by RAAS + sympathetic + hypertrophy",
          },
          {
            id: 8,
            term: "Diastolic heart failure (HFpEF)",
            match:
              "Normal EF + ↑LVEDP + stiff ventricle; hypertension most common cause",
          },
        ],
        15: [
          {
            id: 1,
            term: "Metabolic acidosis (anion gap)",
            match:
              "MUDPILES: methanol, uremia, DKA, paraldehyde/propylene glycol, INH, lactic acidosis, ethylene glycol, salicylates",
          },
          {
            id: 2,
            term: "Metabolic acidosis (non-anion gap)",
            match:
              "HARDUP: hyperalimentation, Addison's, RTA, diarrhea, ureterosigmoidostomy, pancreatic fistula",
          },
          {
            id: 3,
            term: "Metabolic alkalosis causes",
            match:
              "Vomiting (Cl- loss), thiazide/loop diuretics, hyperaldosteronism, milk-alkali",
          },
          {
            id: 4,
            term: "Respiratory acidosis",
            match:
              "Hypoventilation; ↑pCO2; acute compensation: ↑HCO3- 1 mEq/10 mmHg",
          },
          {
            id: 5,
            term: "Respiratory alkalosis",
            match:
              "Hyperventilation; ↓pCO2; anxiety, altitude, pregnancy, liver failure",
          },
          {
            id: 6,
            term: "Winter's formula (metabolic acidosis)",
            match: "Expected pCO2 = 1.5(HCO3-) + 8 ± 2",
          },
          {
            id: 7,
            term: "RTA type 1 (distal)",
            match:
              "H+ secretion failure; urine pH > 5.5; nephrolithiasis; hypokalemia",
          },
          {
            id: 8,
            term: "RTA type 4 (hypoaldosteronism)",
            match:
              "Hyperkalemia + non-anion gap acidosis; most common RTA; DM nephropathy",
          },
        ],
        16: [
          {
            id: 1,
            term: "Insulin actions",
            match:
              "↑glucose uptake (GLUT4) + glycogen synthesis + lipogenesis; ↓gluconeogenesis + lipolysis",
          },
          {
            id: 2,
            term: "Glucagon actions",
            match:
              "↑glycogenolysis + gluconeogenesis + lipolysis; ↑ketogenesis",
          },
          {
            id: 3,
            term: "Cortisol actions",
            match:
              "↑gluconeogenesis + lipolysis + proteolysis; anti-inflammatory; ↑blood glucose",
          },
          {
            id: 4,
            term: "GH actions",
            match:
              "↑lipolysis + protein synthesis; IGF-1 mediated; anti-insulin",
          },
          {
            id: 5,
            term: "Aldosterone actions",
            match:
              "Na+ retention + K+ excretion + H+ excretion; principal cells",
          },
          {
            id: 6,
            term: "ADH/vasopressin actions",
            match:
              "V2 receptor → aquaporin-2 insertion; water reabsorption in collecting duct",
          },
          {
            id: 7,
            term: "ANP/BNP actions",
            match:
              "↑GFR + natriuresis; ↓renin + aldosterone; vasodilation; released by atrial stretch",
          },
          {
            id: 8,
            term: "Thyroid hormone actions",
            match:
              "↑BMR + thermogenesis + O2 consumption; ↑cardiac output; ↑drug metabolism",
          },
        ],
        17: [
          {
            id: 1,
            term: "IL-1 and TNF-alpha",
            match:
              "Fever + acute phase response; from macrophages; promote inflammation",
          },
          {
            id: 2,
            term: "IL-2",
            match:
              "T-cell proliferation and differentiation ('T-cell growth factor')",
          },
          {
            id: 3,
            term: "IL-4",
            match:
              "IgE class switching + Th2 differentiation; allergy + parasite response",
          },
          {
            id: 4,
            term: "IL-5",
            match:
              "Eosinophil production + differentiation; asthma + parasitic infection",
          },
          {
            id: 5,
            term: "IL-10",
            match: "Anti-inflammatory; inhibits Th1 + macrophage activation",
          },
          {
            id: 6,
            term: "IL-12",
            match:
              "NK cell activation + Th1 differentiation; bridges innate and adaptive",
          },
          {
            id: 7,
            term: "IFN-gamma (type II)",
            match:
              "Macrophage activation; upregulates MHC I + II; Th1 cytokine",
          },
          {
            id: 8,
            term: "IFN-alpha/beta (type I)",
            match:
              "Antiviral defense; innate response; upregulates PKR + RNase L",
          },
        ],
        18: [
          {
            id: 1,
            term: "MHC class I (HLA-A, B, C)",
            match:
              "Present intracellular peptides to CD8+ T cells; all nucleated cells",
          },
          {
            id: 2,
            term: "MHC class II (HLA-DR, DP, DQ)",
            match: "Present extracellular peptides to CD4+ T cells; APCs only",
          },
          {
            id: 3,
            term: "CD4+ Th1 response",
            match:
              "IFN-gamma; cell-mediated; fights intracellular pathogens + fungi",
          },
          {
            id: 4,
            term: "CD4+ Th2 response",
            match: "IL-4/5/13; antibody-mediated; allergy + parasites",
          },
          {
            id: 5,
            term: "Complement C3b",
            match: "Opsonin; coats bacteria for phagocytosis",
          },
          {
            id: 6,
            term: "Complement C5a",
            match: "Potent anaphylatoxin + neutrophil chemotactic factor",
          },
          {
            id: 7,
            term: "Complement MAC (C5b-9)",
            match: "Membrane attack complex; gram-negative bacteriolysis",
          },
          {
            id: 8,
            term: "IgA (secretory)",
            match:
              "Mucosal immunity; tears, saliva, colostrum, gut; dimer with J chain",
          },
        ],
      },
      3: {
        // Level 3 - 10 pairs each
        1: [
          { id: 1, term: "Thyroid", match: "T3 & T4" },
          { id: 2, term: "Pancreas", match: "Insulin" },
          { id: 3, term: "Adrenal cortex", match: "Cortisol" },
          { id: 4, term: "Pituitary", match: "Growth hormone" },
          { id: 5, term: "Parathyroid", match: "PTH" },
          { id: 6, term: "Pineal", match: "Melatonin" },
          { id: 7, term: "Ovaries", match: "Estrogen" },
          { id: 8, term: "Testes", match: "Testosterone" },
          { id: 9, term: "Hypothalamus", match: "ADH release" },
          { id: 10, term: "Adrenal medulla", match: "Epinephrine" },
        ],
        2: [
          { id: 1, term: "Action potential", match: "Na+ influx" },
          { id: 2, term: "Repolarization", match: "K+ efflux" },
          { id: 3, term: "Resting potential", match: "-70 mV" },
          { id: 4, term: "Threshold", match: "-55 mV" },
          { id: 5, term: "Refractory period", match: "No new AP" },
          { id: 6, term: "Saltatory conduction", match: "Node to node" },
          { id: 7, term: "Myelin", match: "Insulation" },
          { id: 8, term: "Synapse", match: "Neural junction" },
          { id: 9, term: "EPSP", match: "Excitatory" },
          { id: 10, term: "IPSP", match: "Inhibitory" },
        ],
        3: [
          { id: 1, term: "pH", match: "7.35-7.45" },
          { id: 2, term: "pCO2", match: "35-45 mmHg" },
          { id: 3, term: "HCO3", match: "22-26 mEq/L" },
          { id: 4, term: "Respiratory acidosis", match: "High CO2" },
          { id: 5, term: "Respiratory alkalosis", match: "Low CO2" },
          { id: 6, term: "Metabolic acidosis", match: "Low HCO3" },
          { id: 7, term: "Metabolic alkalosis", match: "High HCO3" },
          { id: 8, term: "Winter's formula", match: "Expected pCO2" },
          { id: 9, term: "Anion gap", match: "Na - Cl - HCO3" },
          { id: 10, term: "Compensation", match: "Restore pH" },
        ],
        4: [
          { id: 1, term: "Actin", match: "Thin filament" },
          { id: 2, term: "Myosin", match: "Thick filament" },
          { id: 3, term: "Troponin", match: "Ca2+ binding" },
          { id: 4, term: "Tropomyosin", match: "Blocks binding" },
          { id: 5, term: "Sarcomere", match: "Contractile unit" },
          { id: 6, term: "T-tubules", match: "AP propagation" },
          { id: 7, term: "Sarcoplasmic reticulum", match: "Ca2+ storage" },
          { id: 8, term: "Cross-bridge cycle", match: "Power stroke" },
          { id: 9, term: "ATP", match: "Energy currency" },
          { id: 10, term: "Creatine phosphate", match: "Quick energy" },
        ],
        5: [
          { id: 1, term: "FSH", match: "Follicle development" },
          { id: 2, term: "LH", match: "Ovulation trigger" },
          { id: 3, term: "Estrogen", match: "Endometrial growth" },
          { id: 4, term: "Progesterone", match: "Maintains pregnancy" },
          { id: 5, term: "hCG", match: "Pregnancy hormone" },
          { id: 6, term: "Prolactin", match: "Milk production" },
          { id: 7, term: "Oxytocin", match: "Uterine contractions" },
          { id: 8, term: "Inhibin", match: "FSH inhibition" },
          { id: 9, term: "GnRH", match: "LH/FSH release" },
          { id: 10, term: "Testosterone", match: "Spermatogenesis" },
        ],
        6: [
          { id: 1, term: "GFR", match: "Filtration rate" },
          { id: 2, term: "Creatinine", match: "GFR marker" },
          { id: 3, term: "Aldosterone", match: "Na retention" },
          { id: 4, term: "ADH", match: "Water retention" },
          { id: 5, term: "Renin", match: "BP regulation" },
          { id: 6, term: "Angiotensin II", match: "Vasoconstriction" },
          { id: 7, term: "PTH", match: "Raises calcium" },
          { id: 8, term: "Calcitonin", match: "Lowers calcium" },
          { id: 9, term: "Erythropoietin", match: "RBC production" },
          { id: 10, term: "Bicarbonate", match: "Buffer" },
        ],
        7: [
          { id: 1, term: "Tidal volume", match: "500 mL at rest" },
          { id: 2, term: "Anatomical dead space", match: "150 mL" },
          { id: 3, term: "FRC", match: "ERV + RV" },
          { id: 4, term: "Total lung capacity", match: "All lung volumes" },
          { id: 5, term: "FEV1/FVC < 0.7", match: "Obstructive pattern" },
          { id: 6, term: "Reduced TLC", match: "Restrictive pattern" },
          { id: 7, term: "A-a gradient", match: "Shunt or diffusion defect" },
          { id: 8, term: "Lung compliance", match: "ΔV / ΔP" },
          { id: 9, term: "Surfactant", match: "Reduce surface tension" },
          { id: 10, term: "Hypoxic vasoconstriction", match: "V/Q matching" },
        ],
        8: [
          { id: 1, term: "Gastrin", match: "Stimulates gastric acid" },
          { id: 2, term: "Secretin", match: "Stimulates pancreatic HCO3" },
          { id: 3, term: "CCK", match: "Gallbladder contraction" },
          { id: 4, term: "GIP", match: "Inhibits acid / stimulates insulin" },
          { id: 5, term: "Motilin", match: "Migrating motor complex" },
          { id: 6, term: "Somatostatin", match: "Inhibits all GI secretion" },
          { id: 7, term: "VIP", match: "Watery diarrhea" },
          { id: 8, term: "GLP-1", match: "Insulin release after meals" },
          { id: 9, term: "Peptide YY", match: "Reduces appetite" },
          { id: 10, term: "Ghrelin", match: "Stimulates hunger" },
        ],
        9: [
          { id: 1, term: "Proximal tubule", match: "Reabsorbs 67% of Na+" },
          { id: 2, term: "Thick ascending limb", match: "NKCC2 cotransporter" },
          {
            id: 3,
            term: "Distal convoluted tubule",
            match: "NCC cotransporter",
          },
          { id: 4, term: "Collecting duct", match: "ADH target" },
          { id: 5, term: "Macula densa", match: "Detects tubular NaCl" },
          { id: 6, term: "JGA", match: "Renin secretion site" },
          { id: 7, term: "Aquaporin-2", match: "ADH-regulated channel" },
          { id: 8, term: "Carbonic anhydrase", match: "HCO3 reabsorption" },
          { id: 9, term: "Podocytes", match: "Glomerular filtration barrier" },
          { id: 10, term: "Mesangial cells", match: "Regulate GFR" },
        ],
        10: [
          {
            id: 1,
            term: "Beta-1 adrenergic",
            match: "Gs → cAMP → PKA → ↑HR + contractility",
          },
          {
            id: 2,
            term: "Alpha-1 adrenergic",
            match: "Gq → IP3/DAG → vasoconstriction",
          },
          {
            id: 3,
            term: "Alpha-2 adrenergic",
            match: "Gi → ↓cAMP → ↓NE release",
          },
          {
            id: 4,
            term: "M1/M3 muscarinic",
            match: "Gq → IP3 → smooth muscle contraction",
          },
          { id: 5, term: "M2 muscarinic", match: "Gi → ↓cAMP → ↓HR" },
          { id: 6, term: "D1 dopamine receptor", match: "Gs → ↑cAMP" },
          { id: 7, term: "D2 dopamine receptor", match: "Gi → ↓cAMP" },
          {
            id: 8,
            term: "H1 histamine receptor",
            match: "Gq → allergy / itch / vasodilation",
          },
          {
            id: 9,
            term: "H2 histamine receptor",
            match: "Gs → ↑cAMP → gastric acid secretion",
          },
          {
            id: 10,
            term: "Beta-2 adrenergic",
            match: "Gs → ↑cAMP → bronchodilation",
          },
        ],
        11: [
          {
            id: 1,
            term: "Proximal tubule",
            match: "Reabsorbs 67% Na, 100% glucose, 90% HCO3⁻",
          },
          {
            id: 2,
            term: "Thin descending loop of Henle",
            match: "Water permeable only — concentrates tubule",
          },
          {
            id: 3,
            term: "Thin ascending loop of Henle",
            match: "Solute permeable, water impermeable",
          },
          {
            id: 4,
            term: "Thick ascending loop of Henle",
            match: "NKCC2 — furosemide target",
          },
          {
            id: 5,
            term: "Distal convoluted tubule",
            match: "NCC cotransporter — thiazide target",
          },
          {
            id: 6,
            term: "Principal cells",
            match: "ENaC (Na in) + ROMK (K out) + aquaporin-2",
          },
          {
            id: 7,
            term: "Alpha-intercalated cells",
            match: "H+ secretion + HCO3⁻ reabsorption",
          },
          {
            id: 8,
            term: "Beta-intercalated cells",
            match: "HCO3⁻ secretion (metabolic alkalosis)",
          },
          {
            id: 9,
            term: "Collecting duct",
            match: "ADH → aquaporin-2 insertion → water reabsorption",
          },
          {
            id: 10,
            term: "Juxtaglomerular apparatus",
            match: "Macula densa → tubuloglomerular feedback",
          },
        ],
        12: [
          {
            id: 1,
            term: "Hemorrhagic shock",
            match: "↑SVR + tachycardia (baroreceptor reflex)",
          },
          {
            id: 2,
            term: "Septic shock",
            match: "↓SVR + ↑CO (hyperdynamic phase)",
          },
          { id: 3, term: "Cardiogenic shock", match: "↓CO + ↑PCWP + ↑SVR" },
          {
            id: 4,
            term: "Pericardial tamponade",
            match: "Beck's triad + pulsus paradoxus",
          },
          {
            id: 5,
            term: "Tension pneumothorax",
            match: "Tracheal deviation away + JVD + absent breath sounds",
          },
          {
            id: 6,
            term: "Aortic stenosis (physiology)",
            match: "Pressure overload → concentric LVH",
          },
          {
            id: 7,
            term: "Mitral regurgitation (physiology)",
            match: "Volume overload → eccentric LVH",
          },
          {
            id: 8,
            term: "Atrial fibrillation (physiology)",
            match: "Loss of atrial kick → 10-20% ↓CO",
          },
          {
            id: 9,
            term: "Systolic heart failure",
            match: "Low EF + ↑LVEDP + neurohormonal activation",
          },
          {
            id: 10,
            term: "Exercise physiology",
            match: "↑CO + ↓SVR + ↑venous return",
          },
        ],
        13: [
          {
            id: 1,
            term: "Alveolar gas equation",
            match: "PAO2 = FiO2(Patm − PH2O) − PaCO2/RQ",
          },
          {
            id: 2,
            term: "A-a gradient elevated",
            match: "V/Q mismatch, diffusion defect, or intrapulmonary shunt",
          },
          {
            id: 3,
            term: "Hypoxic pulmonary vasoconstriction",
            match:
              "Pulmonary vessels constrict in hypoxia (opposite of systemic)",
          },
          {
            id: 4,
            term: "Bohr effect",
            match: "↑CO2 / ↑H+ / ↑temp / ↑2,3-DPG → right shift Hb-O2 curve",
          },
          {
            id: 5,
            term: "Haldane effect",
            match: "O2 binding decreases Hb-CO2 affinity; CO2 release in lungs",
          },
          {
            id: 6,
            term: "Surfactant function",
            match:
              "Reduces surface tension; prevents alveolar collapse (atelectasis)",
          },
          {
            id: 7,
            term: "V/Q = 0 (shunt)",
            match:
              "Perfusion without ventilation; O2 does not correct hypoxemia",
          },
          {
            id: 8,
            term: "V/Q = infinity (dead space)",
            match: "Ventilation without perfusion; ↑dead space; ↑pCO2",
          },
          {
            id: 9,
            term: "Hypercapnic respiratory failure (type 2)",
            match: "↑pCO2 + hypoxemia; neuromuscular disease + COPD",
          },
          {
            id: 10,
            term: "FRC (functional residual capacity)",
            match:
              "ERV + RV; equilibrium of chest wall spring out vs lung recoil in",
          },
        ],
        14: [
          {
            id: 1,
            term: "Filtration fraction (FF)",
            match:
              "GFR / RPF; normally ~20%; ↑in efferent arteriole constriction",
          },
          {
            id: 2,
            term: "GFR determinants",
            match:
              "Kf × (net filtration pressure) = Kf × [(Pgc − Pbs) − (πgc − πbs)]",
          },
          {
            id: 3,
            term: "Renin release triggers",
            match:
              "↓renal perfusion + ↓Na+ at macula densa + sympathetic activation",
          },
          {
            id: 4,
            term: "RAAS cascade",
            match: "Renin → angiotensinogen → AT-I → ACE → AT-II → aldosterone",
          },
          {
            id: 5,
            term: "Angiotensin II effects",
            match:
              "↑BP + aldosterone + ADH + thirst; efferent arteriole constriction",
          },
          {
            id: 6,
            term: "Aldosterone effect on tubule",
            match: "↑ENaC + Na/K-ATPase; ↑Na+ in, ↑K+ and H+ out",
          },
          {
            id: 7,
            term: "Free water clearance",
            match:
              "CH2O = V − Cosm; negative in concentrated urine (ADH present)",
          },
          {
            id: 8,
            term: "Urine osmolality in SIADH",
            match:
              "> 100 mOsm/L (inappropriately concentrated despite hyponatremia)",
          },
          {
            id: 9,
            term: "Diabetes insipidus (central)",
            match: "↓ADH; dilute urine; urine concentrates after desmopressin",
          },
          {
            id: 10,
            term: "Diabetes insipidus (nephrogenic)",
            match:
              "↓V2 receptor response; dilute urine; no response to desmopressin",
          },
        ],
        15: [
          {
            id: 1,
            term: "HPA axis (stress response)",
            match: "Stress → CRH → ACTH → cortisol → negative feedback",
          },
          {
            id: 2,
            term: "HPT axis",
            match: "TRH → TSH → T3/T4 → negative feedback on TRH + TSH",
          },
          {
            id: 3,
            term: "Primary hypothyroidism",
            match:
              "↑TSH + ↓T4; Hashimoto's most common; treat with levothyroxine",
          },
          {
            id: 4,
            term: "Primary hyperthyroidism",
            match:
              "↓TSH + ↑T4; Graves' most common; treat with methimazole or RAI",
          },
          {
            id: 5,
            term: "Primary hyperaldosteronism (Conn's)",
            match: "↑aldosterone + ↑Na+ + ↓K+ + ↓renin; HTN + muscle weakness",
          },
          {
            id: 6,
            term: "Prolactin regulation",
            match:
              "Tonic inhibition by dopamine; TRH stimulates; hypothyroidism → ↑prolactin",
          },
          {
            id: 7,
            term: "Acromegaly physiology",
            match:
              "↑GH + ↑IGF-1; failure to suppress GH with glucose; pituitary adenoma",
          },
          {
            id: 8,
            term: "SIADH physiology",
            match:
              "↑ADH → ↑water retention → dilutional hyponatremia + ↑urine osmolality",
          },
          {
            id: 9,
            term: "Pheochromocytoma physiology",
            match:
              "↑catecholamines → episodic HTN + tachycardia + diaphoresis; ↑VMA/metanephrines",
          },
          {
            id: 10,
            term: "Carcinoid syndrome physiology",
            match: "Serotonin + bradykinin; flushing + diarrhea; ↑urine 5-HIAA",
          },
        ],
        16: [
          {
            id: 1,
            term: "Spermatogenesis stages",
            match:
              "Spermatogonia → primary spermatocyte (meiosis I) → secondary → spermatids → spermatozoa",
          },
          {
            id: 2,
            term: "Testosterone synthesis",
            match:
              "LH → Leydig cells → testosterone; FSH → Sertoli → sperm maturation",
          },
          {
            id: 3,
            term: "Inhibin B (male)",
            match: "From Sertoli cells; negative feedback on FSH selectively",
          },
          {
            id: 4,
            term: "Follicular phase physiology",
            match: "Days 1-14; ↑FSH → follicle growth + estrogen → LH surge",
          },
          {
            id: 5,
            term: "Luteal phase physiology",
            match:
              "Days 14-28; corpus luteum → progesterone; fixed 14-day duration",
          },
          {
            id: 6,
            term: "hCG function in early pregnancy",
            match:
              "Maintains corpus luteum progesterone until placenta takes over (~10 weeks)",
          },
          {
            id: 7,
            term: "Aromatase function",
            match:
              "Converts androgens → estrogens; granulosa cells + adipose + placenta",
          },
          {
            id: 8,
            term: "Menopause physiology",
            match:
              "↓estrogen + inhibin → ↑FSH + LH; hot flashes from thermoregulatory disruption",
          },
          {
            id: 9,
            term: "Placental progesterone",
            match:
              "Replaces corpus luteum after 8-10 weeks (luteal-placental shift)",
          },
          {
            id: 10,
            term: "Prolactin + lactation",
            match:
              "↑prolactin postpartum; suckling maintains lactation; inhibits GnRH → anovulation",
          },
        ],
        17: [
          {
            id: 1,
            term: "Primary hemostasis",
            match: "Platelet plug; vWF bridges platelets to exposed collagen",
          },
          {
            id: 2,
            term: "Secondary hemostasis",
            match:
              "Coagulation cascade; fibrin clot; intrinsic + extrinsic pathways",
          },
          {
            id: 3,
            term: "PT monitors",
            match: "Extrinsic + common pathway; factor VII; warfarin effect",
          },
          {
            id: 4,
            term: "aPTT monitors",
            match:
              "Intrinsic + common pathway; factors VIII, IX, XI; heparin effect",
          },
          {
            id: 5,
            term: "vWF function",
            match:
              "Bridges platelet GPIb to subendothelial collagen; carries factor VIII",
          },
          {
            id: 6,
            term: "Thrombin functions",
            match:
              "Cleaves fibrinogen to fibrin; activates factors V, VIII, XIII; activates platelets",
          },
          {
            id: 7,
            term: "Protein C pathway",
            match:
              "Thrombomodulin + thrombin → protein C → inactivates Va + VIIIa",
          },
          {
            id: 8,
            term: "Antithrombin III",
            match: "Inactivates thrombin + Xa; potentiated by heparin (1000×)",
          },
          {
            id: 9,
            term: "tPA (tissue plasminogen activator)",
            match:
              "Activates plasminogen → plasmin → fibrinolysis; used in ischemic stroke",
          },
          {
            id: 10,
            term: "D-dimer",
            match: "Fibrin degradation product; elevated in PE, DVT, DIC, MI",
          },
        ],
        18: [
          {
            id: 1,
            term: "Osmolarity calculation",
            match: "2×Na+ + glucose/18 + BUN/2.8; normal 285-295 mOsm/L",
          },
          {
            id: 2,
            term: "Hyperkalemia ECG changes",
            match:
              "Peaked T → widened QRS → sine wave → ventricular fibrillation",
          },
          {
            id: 3,
            term: "Hypokalemia ECG changes",
            match:
              "Flattened T waves + U waves; increased ectopy + digoxin toxicity risk",
          },
          {
            id: 4,
            term: "Hypercalcemia ECG",
            match:
              "Shortened QT; 'bones, stones, moans, groans'; suppressed PTH",
          },
          {
            id: 5,
            term: "Hypocalcemia signs",
            match: "Chvostek + Trousseau; prolonged QT; tetany + seizures",
          },
          {
            id: 6,
            term: "Hypomagnesemia effects",
            match:
              "Refractory hypokalemia + hypocalcemia; arrhythmias; must replete Mg2+ first",
          },
          {
            id: 7,
            term: "Starling forces (capillary)",
            match: "Filtration = Kf[(Pc − Pi) − σ(πc − πi)]",
          },
          {
            id: 8,
            term: "Edema formation",
            match:
              "↑hydrostatic pressure (CHF) or ↓oncotic pressure (hypoalbuminemia) or ↑permeability",
          },
          {
            id: 9,
            term: "Third spacing",
            match:
              "Fluid sequestration into non-functional compartment; burns, pancreatitis",
          },
          {
            id: 10,
            term: "Refeeding syndrome",
            match:
              "Hypophosphatemia after refeeding; insulin drives PO4 into cells; arrhythmias + respiratory failure",
          },
        ],
      },
      4: {
        // Level 4 - 12 pairs each
        1: [
          { id: 1, term: "Thyroid", match: "T3 & T4" },
          { id: 2, term: "Pancreas", match: "Insulin" },
          { id: 3, term: "Adrenal cortex", match: "Cortisol" },
          { id: 4, term: "Pituitary", match: "Growth hormone" },
          { id: 5, term: "Parathyroid", match: "PTH" },
          { id: 6, term: "Pineal", match: "Melatonin" },
          { id: 7, term: "Ovaries", match: "Estrogen" },
          { id: 8, term: "Testes", match: "Testosterone" },
          { id: 9, term: "Hypothalamus", match: "ADH release" },
          { id: 10, term: "Adrenal medulla", match: "Epinephrine" },
          { id: 11, term: "Kidneys", match: "Renin" },
          { id: 12, term: "Heart", match: "ANP & BNP" },
        ],
        2: [
          { id: 1, term: "Type I muscle", match: "Slow oxidative" },
          { id: 2, term: "Type IIa muscle", match: "Fast oxidative" },
          { id: 3, term: "Type IIb muscle", match: "Fast glycolytic" },
          { id: 4, term: "Motor unit", match: "Neuron + fibers" },
          { id: 5, term: "Recruitment", match: "More motor units" },
          { id: 6, term: "Tetanus", match: "Sustained contraction" },
          { id: 7, term: "Muscle spindle", match: "Stretch receptor" },
          { id: 8, term: "Golgi tendon", match: "Tension receptor" },
          { id: 9, term: "Stretch reflex", match: "Monosynaptic" },
          {
            id: 10,
            term: "Reciprocal inhibition",
            match: "Relaxes antagonist",
          },
          { id: 11, term: "Length-tension", match: "Optimal length" },
          { id: 12, term: "Force-velocity", match: "Speed vs force" },
        ],
        3: [
          { id: 1, term: "Starling forces", match: "Fluid movement" },
          { id: 2, term: "Hydrostatic pressure", match: "Pushes out" },
          { id: 3, term: "Oncotic pressure", match: "Pulls in" },
          { id: 4, term: "Edema", match: "Fluid accumulation" },
          { id: 5, term: "Lymphatics", match: "Fluid return" },
          { id: 6, term: "Capillary exchange", match: "Diffusion" },
          { id: 7, term: "Filtration", match: "At arteriolar end" },
          { id: 8, term: "Reabsorption", match: "At venular end" },
          { id: 9, term: "Albumin", match: "Main oncotic protein" },
          { id: 10, term: "Interstitial fluid", match: "Between cells" },
          { id: 11, term: "Plasma", match: "Blood fluid" },
          { id: 12, term: "Intracellular fluid", match: "Inside cells" },
        ],
        4: [
          { id: 1, term: "Aquaporins", match: "Water channels" },
          { id: 2, term: "Na/K ATPase", match: "3 Na out, 2 K in" },
          { id: 3, term: "Facilitated diffusion", match: "Carrier-mediated" },
          { id: 4, term: "Simple diffusion", match: "Down gradient" },
          { id: 5, term: "Active transport", match: "Against gradient" },
          { id: 6, term: "Endocytosis", match: "Cell intake" },
          { id: 7, term: "Exocytosis", match: "Cell release" },
          { id: 8, term: "Cotransport", match: "Same direction" },
          { id: 9, term: "Countertransport", match: "Opposite direction" },
          { id: 10, term: "Osmosis", match: "Water movement" },
          { id: 11, term: "Osmolarity", match: "Solute concentration" },
          { id: 12, term: "Tonicity", match: "Cell response" },
        ],
        5: [
          { id: 1, term: "Innate immunity", match: "Non-specific" },
          { id: 2, term: "Adaptive immunity", match: "Specific response" },
          { id: 3, term: "T helper cells", match: "CD4+" },
          { id: 4, term: "Cytotoxic T cells", match: "CD8+" },
          { id: 5, term: "B cells", match: "Antibody production" },
          { id: 6, term: "Plasma cells", match: "Mature B cells" },
          { id: 7, term: "Memory cells", match: "Long-term immunity" },
          { id: 8, term: "MHC I", match: "All nucleated cells" },
          { id: 9, term: "MHC II", match: "APCs only" },
          { id: 10, term: "Complement", match: "MAC formation" },
          { id: 11, term: "Interferons", match: "Antiviral" },
          { id: 12, term: "Cytokines", match: "Cell signaling" },
        ],
        6: [
          { id: 1, term: "Stroke volume", match: "EDV - ESV" },
          { id: 2, term: "Cardiac output", match: "HR x SV" },
          { id: 3, term: "Mean arterial pressure", match: "CO x SVR" },
          { id: 4, term: "Baroreceptors", match: "BP sensors" },
          { id: 5, term: "Chemoreceptors", match: "O2/CO2 sensors" },
          { id: 6, term: "Pulse pressure", match: "Systolic - diastolic" },
          { id: 7, term: "Preload", match: "EDV" },
          { id: 8, term: "Afterload", match: "Vascular resistance" },
          { id: 9, term: "Contractility", match: "Force of squeeze" },
          { id: 10, term: "Venous return", match: "Blood to heart" },
          { id: 11, term: "Ejection fraction", match: "SV/EDV" },
          { id: 12, term: "Frank-Starling", match: "Length-tension" },
        ],
        7: [
          { id: 1, term: "Renin", match: "JGA low pressure response" },
          {
            id: 2,
            term: "Angiotensin II",
            match: "Vasoconstriction + aldosterone",
          },
          { id: 3, term: "Aldosterone", match: "Na+ reabsorption in DCT" },
          {
            id: 4,
            term: "ADH / vasopressin",
            match: "Water reabsorption in CD",
          },
          { id: 5, term: "ANP", match: "↓ Na reabsorption, ↓ renin" },
          { id: 6, term: "PTH", match: "↑ Ca2+, ↓ PO4" },
          {
            id: 7,
            term: "Calcitonin",
            match: "↓ Ca2+ — osteoclast inhibition",
          },
          { id: 8, term: "1,25(OH)2 Vitamin D", match: "GI Ca2+ absorption" },
          { id: 9, term: "FGF-23", match: "↓ Phosphate reabsorption" },
          { id: 10, term: "Osteoprotegerin", match: "↓ Osteoclast activity" },
          { id: 11, term: "RANKL", match: "Osteoclast activator" },
          { id: 12, term: "Klotho", match: "FGF-23 co-receptor" },
        ],
        8: [
          {
            id: 1,
            term: "Proximal tubule (efficiency)",
            match: "67% Na, 100% glucose, 90% HCO3⁻ reabsorbed",
          },
          {
            id: 2,
            term: "Thick ascending LOH (function)",
            match: "Generates medullary concentration gradient",
          },
          {
            id: 3,
            term: "DCT (hormonal control)",
            match: "Aldosterone → ↑Na reabsorption, ↑K secretion",
          },
          {
            id: 4,
            term: "FENa <1%",
            match: "Prerenal azotemia (avid Na retention)",
          },
          {
            id: 5,
            term: "FENa >2%",
            match: "Intrinsic renal failure (tubular damage)",
          },
          {
            id: 6,
            term: "BUN:Cr >20:1",
            match: "Prerenal — low flow, concentrated urine",
          },
          {
            id: 7,
            term: "Non-anion gap acidosis",
            match: "Hyperchloremic — diarrhea or RTA",
          },
          {
            id: 8,
            term: "Urinary anion gap positive",
            match: "Renal tubular acidosis (↓NH4+)",
          },
          {
            id: 9,
            term: "Urinary anion gap negative",
            match: "Diarrhea (↑NH4+ excretion)",
          },
          {
            id: 10,
            term: "Nephrogenic DI",
            match: "ADH present but collecting duct unresponsive",
          },
          {
            id: 11,
            term: "Central DI",
            match: "ADH deficiency — responds to desmopressin",
          },
          {
            id: 12,
            term: "Clearance formula",
            match: "Cl = U × V / P (mL/min)",
          },
        ],
        9: [
          {
            id: 1,
            term: "IL-1",
            match: "Fever + acute phase + T cell activation",
          },
          {
            id: 2,
            term: "IL-2",
            match: "T cell proliferation ('T cell growth factor')",
          },
          {
            id: 3,
            term: "IL-4",
            match: "IgE class switching + Th2 differentiation",
          },
          {
            id: 4,
            term: "IL-5",
            match: "Eosinophil production + differentiation",
          },
          {
            id: 5,
            term: "IL-6",
            match: "Fever + acute phase reactants (CRP, fibrinogen)",
          },
          { id: 6, term: "IL-8", match: "Neutrophil chemotaxis" },
          {
            id: 7,
            term: "IL-10",
            match: "Anti-inflammatory — inhibits Th1 + macrophages",
          },
          { id: 8, term: "IL-12", match: "NK cell + Th1 differentiation" },
          {
            id: 9,
            term: "TNF-alpha",
            match: "Fever + cachexia + septic shock",
          },
          {
            id: 10,
            term: "TGF-beta",
            match: "Fibrosis + Treg differentiation + immunosuppression",
          },
          {
            id: 11,
            term: "IFN-alpha / IFN-beta",
            match: "Antiviral (innate — type I interferons)",
          },
          {
            id: 12,
            term: "IFN-gamma",
            match: "Macrophage activation (type II — adaptive)",
          },
        ],
        10: [
          {
            id: 1,
            term: "Cushing's triad",
            match: "HTN + bradycardia + irregular breathing (raised ICP)",
          },
          {
            id: 2,
            term: "Kussmaul breathing",
            match: "Deep labored breaths — metabolic acidosis (DKA)",
          },
          {
            id: 3,
            term: "Cheyne-Stokes respiration",
            match: "Bilateral cerebral hemisphere damage",
          },
          {
            id: 4,
            term: "Obstructive sleep apnea",
            match: "Hypoxia → pulmonary HTN → cor pulmonale",
          },
          {
            id: 5,
            term: "Left heart failure (physiology)",
            match: "Pulmonary edema + orthopnea + PND",
          },
          {
            id: 6,
            term: "Right heart failure (physiology)",
            match: "JVD + peripheral edema + hepatomegaly",
          },
          {
            id: 7,
            term: "High output cardiac failure",
            match: "Beriberi, severe anemia, AV fistula, Paget's",
          },
          {
            id: 8,
            term: "Type 1 respiratory failure",
            match: "Hypoxemia + normal/low CO2",
          },
          {
            id: 9,
            term: "Type 2 respiratory failure",
            match: "Hypoxemia + hypercapnia (ventilatory failure)",
          },
          {
            id: 10,
            term: "Intrapulmonary shunt",
            match: "Hypoxemia not corrected by supplemental O2",
          },
          {
            id: 11,
            term: "Dead space physiology",
            match: "Ventilation without perfusion → ↑CO2",
          },
          {
            id: 12,
            term: "Auto-PEEP (intrinsic PEEP)",
            match: "Air trapping in severe COPD / asthma",
          },
        ],
        11: [
          {
            id: 1,
            term: "Glutamate",
            match:
              "Main excitatory CNS neurotransmitter; NMDA + AMPA + kainate receptors",
          },
          {
            id: 2,
            term: "GABA",
            match:
              "Main inhibitory CNS neurotransmitter; GABA-A (Cl- channel) + GABA-B (K+ / ↓cAMP)",
          },
          {
            id: 3,
            term: "Dopamine mesolimbic pathway",
            match: "VTA → nucleus accumbens; reward + addiction",
          },
          {
            id: 4,
            term: "Dopamine nigrostriatal pathway",
            match: "Substantia nigra → striatum; movement; lost in Parkinson's",
          },
          {
            id: 5,
            term: "Dopamine tuberoinfundibular",
            match: "Hypothalamus → pituitary; inhibits prolactin",
          },
          {
            id: 6,
            term: "Serotonin (5-HT)",
            match:
              "Raphe nuclei; mood + sleep + appetite; SSRIs increase synaptic 5-HT",
          },
          {
            id: 7,
            term: "Norepinephrine (NE)",
            match: "Locus coeruleus; alertness + arousal + fight-or-flight",
          },
          {
            id: 8,
            term: "ACh (NMJ)",
            match:
              "Nicotinic receptor (ligand-gated Na+ channel); depolarizes end plate",
          },
          {
            id: 9,
            term: "Long-term potentiation (LTP)",
            match:
              "NMDA receptor-dependent; Mg2+ removed → Ca2+ influx → AMPA upregulation",
          },
          {
            id: 10,
            term: "Stretch reflex (monosynaptic)",
            match:
              "Ia afferent (muscle spindle) → alpha motor neuron → muscle contraction",
          },
          {
            id: 11,
            term: "Golgi tendon organ",
            match:
              "Ib afferent; responds to tension; disynaptic inhibition (protects from overload)",
          },
          {
            id: 12,
            term: "Descending pain modulation",
            match:
              "PAG → raphe nuclei → dorsal horn; serotonin + NE release; opioid-sensitive",
          },
        ],
        12: [
          {
            id: 1,
            term: "Hepatic glucose output",
            match:
              "Glucagon + catecholamines → glycogenolysis + gluconeogenesis",
          },
          {
            id: 2,
            term: "Pyruvate dehydrogenase complex",
            match:
              "Pyruvate → acetyl-CoA; requires B1 (thiamine); inhibited by high NADH",
          },
          {
            id: 3,
            term: "Citric acid (Krebs) cycle",
            match: "Acetyl-CoA → 3 NADH + 1 FADH2 + 1 GTP + 2 CO2 per turn",
          },
          {
            id: 4,
            term: "Oxidative phosphorylation",
            match: "ETC + ATP synthase; 32-34 ATP per glucose; requires O2",
          },
          {
            id: 5,
            term: "Fatty acid oxidation (beta-oxidation)",
            match:
              "Mitochondria; generates acetyl-CoA + NADH + FADH2; carnitine shuttle",
          },
          {
            id: 6,
            term: "Ketogenesis",
            match:
              "Liver mitochondria; fasting + DKA; beta-hydroxybutyrate + acetoacetate",
          },
          {
            id: 7,
            term: "Urea cycle",
            match:
              "Liver; converts NH3 to urea; OTC deficiency = X-linked hyperammonemia",
          },
          {
            id: 8,
            term: "Heme synthesis",
            match:
              "ALA synthase (rate-limiting; needs B6); in liver + erythroid cells",
          },
          {
            id: 9,
            term: "Bilirubin metabolism",
            match:
              "Heme → unconjugated → liver conjugation → bile → urobilinogen",
          },
          {
            id: 10,
            term: "Purine synthesis (de novo)",
            match:
              "PRPP → IMP → AMP / GMP; rate-limited by PRPP synthetase + amidophosphoribosyltransferase",
          },
          {
            id: 11,
            term: "Purine salvage",
            match:
              "Hypoxanthine → IMP via HGPRT; deficiency = Lesch-Nyhan syndrome",
          },
          {
            id: 12,
            term: "CYP450 reactions (phase I)",
            match:
              "Oxidation/reduction/hydrolysis; require O2 + NADPH; in smooth ER",
          },
        ],
      },
      5: {
        // Level 5 - 14 pairs each
        1: [
          { id: 1, term: "Thyroid", match: "T3 & T4" },
          { id: 2, term: "Pancreas", match: "Insulin" },
          { id: 3, term: "Adrenal cortex", match: "Cortisol" },
          { id: 4, term: "Pituitary", match: "Growth hormone" },
          { id: 5, term: "Parathyroid", match: "PTH" },
          { id: 6, term: "Pineal", match: "Melatonin" },
          { id: 7, term: "Ovaries", match: "Estrogen" },
          { id: 8, term: "Testes", match: "Testosterone" },
          { id: 9, term: "Hypothalamus", match: "ADH release" },
          { id: 10, term: "Adrenal medulla", match: "Epinephrine" },
          { id: 11, term: "Kidneys", match: "Renin" },
          { id: 12, term: "Heart", match: "ANP & BNP" },
          { id: 13, term: "Stomach", match: "Gastrin" },
          { id: 14, term: "Small intestine", match: "Secretin" },
        ],
        2: [
          { id: 1, term: "Dead space", match: "No gas exchange" },
          { id: 2, term: "V/Q ratio", match: "Ventilation/perfusion" },
          { id: 3, term: "Shunt", match: "Low V/Q" },
          { id: 4, term: "Hypoxic vasoconstriction", match: "Match V/Q" },
          { id: 5, term: "Minute ventilation", match: "RR x TV" },
          { id: 6, term: "Alveolar ventilation", match: "Effective exchange" },
          { id: 7, term: "A-a gradient", match: "O2 difference" },
          { id: 8, term: "Diffusion capacity", match: "DLCO" },
          {
            id: 9,
            term: "Work of breathing",
            match: "Compliance + resistance",
          },
          { id: 10, term: "Airway resistance", match: "Flow obstruction" },
          { id: 11, term: "Closing volume", match: "Airway collapse" },
          { id: 12, term: "Functional residual", match: "FRC" },
          { id: 13, term: "Total lung capacity", match: "TLC" },
          { id: 14, term: "Inspiratory reserve", match: "Extra inhale" },
        ],
        3: [
          { id: 1, term: "Macula densa", match: "GFR sensor" },
          { id: 2, term: "JG cells", match: "Renin release" },
          { id: 3, term: "PCT", match: "Bulk reabsorption" },
          { id: 4, term: "DCT", match: "Fine tuning" },
          { id: 5, term: "Countercurrent", match: "Concentration gradient" },
          { id: 6, term: "Vasa recta", match: "Maintains gradient" },
          { id: 7, term: "Clearance", match: "Volume cleared/time" },
          { id: 8, term: "Filtration fraction", match: "GFR/RPF" },
          { id: 9, term: "PAH", match: "RPF marker" },
          { id: 10, term: "Inulin", match: "GFR marker" },
          { id: 11, term: "Free water clearance", match: "Dilution ability" },
          { id: 12, term: "Titratable acid", match: "Buffered H+" },
          { id: 13, term: "Ammonia", match: "H+ carrier" },
          { id: 14, term: "Bicarbonate", match: "Buffer regeneration" },
        ],
        4: [
          { id: 1, term: "QRS complex", match: "Ventricular depol" },
          { id: 2, term: "P wave", match: "Atrial depol" },
          { id: 3, term: "T wave", match: "Ventricular repol" },
          { id: 4, term: "PR interval", match: "AV delay" },
          { id: 5, term: "QT interval", match: "Total ventricular" },
          { id: 6, term: "ST segment", match: "Ventricular plateau" },
          { id: 7, term: "U wave", match: "Purkinje repol" },
          { id: 8, term: "Axis deviation", match: "Heart position" },
          { id: 9, term: "Bundle branch block", match: "Wide QRS" },
          { id: 10, term: "Heart block", match: "Conduction delay" },
          { id: 11, term: "Atrial flutter", match: "Sawtooth" },
          { id: 12, term: "Ventricular tach", match: "Wide complex tachy" },
          { id: 13, term: "Ventricular fib", match: "No organized rhythm" },
          { id: 14, term: "Asystole", match: "Flat line" },
        ],
        5: [
          { id: 1, term: "Bilirubin", match: "Heme breakdown" },
          { id: 2, term: "Conjugation", match: "Glucuronidation" },
          { id: 3, term: "Urobilinogen", match: "Intestinal conversion" },
          { id: 4, term: "CYP450", match: "Drug metabolism" },
          { id: 5, term: "Phase I", match: "Oxidation/reduction" },
          { id: 6, term: "Phase II", match: "Conjugation" },
          { id: 7, term: "First pass", match: "Hepatic extraction" },
          { id: 8, term: "Albumin synthesis", match: "Liver function" },
          { id: 9, term: "Clotting factors", match: "Liver production" },
          { id: 10, term: "Cholesterol", match: "Bile salt precursor" },
          { id: 11, term: "Enterohepatic", match: "Bile recycling" },
          { id: 12, term: "Portal hypertension", match: "Collateral flow" },
          { id: 13, term: "Ammonia", match: "Urea cycle" },
          { id: 14, term: "Hepatic encephalopathy", match: "High ammonia" },
        ],
        6: [
          { id: 1, term: "Action potential", match: "Na+ influx" },
          { id: 2, term: "Repolarization", match: "K+ efflux" },
          { id: 3, term: "Resting potential", match: "-70 mV" },
          { id: 4, term: "Threshold", match: "-55 mV" },
          { id: 5, term: "Saltatory conduction", match: "Node to node" },
          { id: 6, term: "Myelin", match: "Insulation" },
          { id: 7, term: "Synapse", match: "Neural junction" },
          { id: 8, term: "EPSP", match: "Excitatory" },
          { id: 9, term: "IPSP", match: "Inhibitory" },
          { id: 10, term: "Neuromuscular junction", match: "Motor end plate" },
          { id: 11, term: "ACh", match: "NMJ transmitter" },
          { id: 12, term: "Refractory period", match: "No new AP" },
          { id: 13, term: "Summation", match: "Additive signals" },
          { id: 14, term: "Temporal summation", match: "Repeated signals" },
        ],
        7: [
          { id: 1, term: "Long QT syndrome", match: "Torsades risk" },
          {
            id: 2,
            term: "Wolff-Parkinson-White",
            match: "Delta wave + short PR",
          },
          {
            id: 3,
            term: "Right bundle branch block",
            match: "RSR' pattern in V1",
          },
          {
            id: 4,
            term: "Left bundle branch block",
            match: "Broad notched R in V6",
          },
          { id: 5, term: "First degree AV block", match: "PR > 200 ms" },
          { id: 6, term: "Wenckebach", match: "Progressive PR then drop" },
          { id: 7, term: "Mobitz type II", match: "Sudden dropped beat" },
          { id: 8, term: "Complete heart block", match: "AV dissociation" },
          { id: 9, term: "SVT", match: "Narrow complex tachycardia" },
          { id: 10, term: "VT", match: "Wide complex tachycardia" },
          { id: 11, term: "Brugada syndrome", match: "Coved ST in V1-V3" },
          {
            id: 12,
            term: "Pulseless electrical activity",
            match: "No pulse despite rhythm",
          },
          {
            id: 13,
            term: "Ventricular fibrillation",
            match: "Shockable + no pulse",
          },
          { id: 14, term: "Asystole", match: "Flat line — non-shockable" },
        ],
        8: [
          {
            id: 1,
            term: "GFR estimation (CKD-EPI)",
            match: "Uses serum creatinine + age + sex; more accurate than MDRD",
          },
          {
            id: 2,
            term: "Creatinine clearance",
            match:
              "Estimates GFR; slightly overestimates due to tubular secretion",
          },
          {
            id: 3,
            term: "Cystatin C",
            match:
              "Better GFR marker than creatinine; not affected by muscle mass",
          },
          {
            id: 4,
            term: "BUN:creatinine ratio > 20",
            match: "Pre-renal azotemia (dehydration, GI bleed, heart failure)",
          },
          {
            id: 5,
            term: "BUN:creatinine ratio < 10",
            match:
              "Intrinsic renal failure or liver disease (low urea production)",
          },
          {
            id: 6,
            term: "Urinary casts (hyaline)",
            match: "Normal or concentrated urine; non-specific",
          },
          {
            id: 7,
            term: "RBC casts in urine",
            match: "Glomerulonephritis (nephritic syndrome); pathognomonic",
          },
          {
            id: 8,
            term: "WBC casts in urine",
            match: "Pyelonephritis or interstitial nephritis",
          },
          {
            id: 9,
            term: "Granular ('muddy brown') casts",
            match: "Acute tubular necrosis (ATN)",
          },
          {
            id: 10,
            term: "Waxy casts",
            match: "Chronic renal failure; severe tubular atrophy",
          },
          {
            id: 11,
            term: "Fatty casts ('oval fat bodies')",
            match: "Nephrotic syndrome; Maltese cross under polarized light",
          },
          {
            id: 12,
            term: "Urine anion gap",
            match:
              "Na+ + K+ − Cl- (urine); positive = impaired NH4+ excretion (RTA)",
          },
          {
            id: 13,
            term: "Nephrotic syndrome physiology",
            match:
              "Protein loss → ↓oncotic pressure → edema + hyperlipidemia + thrombosis risk",
          },
          {
            id: 14,
            term: "Nephritic syndrome physiology",
            match:
              "Immune complex + complement → GBM damage → hematuria + RBC casts + ↑BP",
          },
        ],
        9: [
          {
            id: 1,
            term: "Ventricular pressure-volume loop",
            match:
              "ABCD: filling → isovolumetric contraction → ejection → isovolumetric relaxation",
          },
          {
            id: 2,
            term: "↑Preload effect on PV loop",
            match:
              "Wider loop; ↑EDV → ↑SV; no change in end-systolic pressure-volume",
          },
          {
            id: 3,
            term: "↑Afterload effect on PV loop",
            match: "Narrower loop; ↑ESV; ↓SV; loop shifts right",
          },
          {
            id: 4,
            term: "↑Contractility effect on PV loop",
            match: "↑SV; ESPVR slopes steeper; ↓ESV without changing EDV",
          },
          {
            id: 5,
            term: "Coronary blood flow regulation",
            match:
              "Autoregulation (myogenic + metabolic); adenosine + NO key vasodilators",
          },
          {
            id: 6,
            term: "Coronary blood flow (diastole)",
            match:
              "Left coronary fills mainly in diastole; right fills in both",
          },
          {
            id: 7,
            term: "Oxygen delivery (DO2)",
            match: "CO × CaO2; CaO2 = (Hb × 1.34 × SaO2) + (0.003 × PaO2)",
          },
          {
            id: 8,
            term: "Fick principle (cardiac output)",
            match: "CO = VO2 / (CaO2 − CvO2); gold standard for CO measurement",
          },
          {
            id: 9,
            term: "Pulmonary capillary wedge pressure",
            match:
              "Approximates left atrial pressure; elevated in left heart failure",
          },
          {
            id: 10,
            term: "Central venous pressure",
            match:
              "Approximates right atrial pressure; elevated in right heart failure + tamponade",
          },
          {
            id: 11,
            term: "QTc interval",
            match:
              "QT / √RR; prolonged > 440 ms (men) / 460 ms (women); TdP risk",
          },
          {
            id: 12,
            term: "Cardiac cycle timing",
            match:
              "S1 = MV + TV closure (start systole); S2 = AV + PV closure (start diastole)",
          },
          {
            id: 13,
            term: "S3 heart sound",
            match:
              "Early diastole; ventricular filling; normal in children; HF in adults",
          },
          {
            id: 14,
            term: "S4 heart sound",
            match:
              "Late diastole (atrial kick); stiff ventricle; HTN + HCM + aortic stenosis",
          },
        ],
        10: [
          {
            id: 1,
            term: "Obstructive vs restrictive spirometry",
            match:
              "Obstructive: ↓FEV1/FVC; Restrictive: ↓FVC + ↑or normal FEV1/FVC",
          },
          {
            id: 2,
            term: "DLCO (diffusing capacity)",
            match:
              "↓in emphysema + fibrosis + anemia; ↑in polycythemia + pulmonary hemorrhage",
          },
          {
            id: 3,
            term: "CO2 transport in blood",
            match: "70% as HCO3- + 23% bound to Hb + 7% dissolved",
          },
          {
            id: 4,
            term: "Pulmonary vascular resistance",
            match:
              "↑in hypoxia (unique to pulmonary circulation); low normally (< 2 Wood units)",
          },
          {
            id: 5,
            term: "V/Q ratio (apex vs base)",
            match:
              "Apex: high V/Q (better ventilation, less perfusion); base: low V/Q",
          },
          {
            id: 6,
            term: "Zone I lung (West)",
            match: "PA > Ppa > Ppv; no flow; apex; only in hemorrhage",
          },
          {
            id: 7,
            term: "Zone II lung (West)",
            match: "Ppa > PA > Ppv; flow proportional to Ppa − PA; mid-lung",
          },
          {
            id: 8,
            term: "Zone III lung (West)",
            match: "Ppa > Ppv > PA; highest blood flow; base; constant flow",
          },
          {
            id: 9,
            term: "Respiratory muscle failure",
            match:
              "Reduced MIP/MEP; NIF < −20 cmH2O suggests need for intubation",
          },
          {
            id: 10,
            term: "Hypoxic drive (COPD)",
            match:
              "Chronic CO2 retention → renal compensation → central chemoreceptors reset; hypoxia drives breathing",
          },
          {
            id: 11,
            term: "Central chemoreceptors",
            match:
              "Medulla; respond to ↑CO2 / ↓pH of CSF; most powerful respiratory drive",
          },
          {
            id: 12,
            term: "Peripheral chemoreceptors",
            match:
              "Carotid + aortic bodies; respond to ↓PaO2 (< 60), ↑PaCO2, ↓pH",
          },
          {
            id: 13,
            term: "Hering-Breuer reflex",
            match:
              "Pulmonary stretch receptors → inhibit inspiration when lungs fully inflated",
          },
          {
            id: 14,
            term: "Pneumotaxic center (pons)",
            match: "Limits inspiration duration; fine-tunes respiratory rhythm",
          },
        ],
        11: [
          {
            id: 1,
            term: "Phase I drug metabolism",
            match:
              "CYP450 oxidation/reduction/hydrolysis; smooth ER; may activate or inactivate drugs",
          },
          {
            id: 2,
            term: "Phase II drug metabolism",
            match:
              "Conjugation (glucuronidation, sulfation, acetylation); usually inactivates",
          },
          {
            id: 3,
            term: "Enterohepatic circulation",
            match:
              "Drug secreted in bile → deconjugated in gut → reabsorbed; prolongs drug action",
          },
          {
            id: 4,
            term: "Plasma protein binding",
            match: "Bound drug is inactive + not filtered; only free drug acts",
          },
          {
            id: 5,
            term: "Hepatic clearance (high extraction)",
            match:
              "Limited by liver blood flow; first-pass effect high; e.g., propranolol, morphine",
          },
          {
            id: 6,
            term: "Hepatic clearance (low extraction)",
            match:
              "Limited by enzyme activity; protein binding important; e.g., warfarin, diazepam",
          },
          {
            id: 7,
            term: "Renal drug clearance",
            match: "GFR filtration + tubular secretion − reabsorption",
          },
          {
            id: 8,
            term: "pH trapping",
            match:
              "Weak acid in alkaline urine → ionized → trapped → ↑excretion (aspirin OD treatment)",
          },
          {
            id: 9,
            term: "Blood-brain barrier (BBB)",
            match:
              "Tight junctions + astrocyte end-feet; lipid-soluble drugs cross better",
          },
          {
            id: 10,
            term: "Tolerance (pharmacodynamic)",
            match:
              "Receptor downregulation or desensitization; reduced response to same dose",
          },
          {
            id: 11,
            term: "Tachyphylaxis",
            match:
              "Rapid tolerance after repeated doses; receptor desensitization (e.g., beta-agonists)",
          },
          {
            id: 12,
            term: "Drug-receptor affinity (Kd)",
            match:
              "Concentration for 50% receptor occupancy; lower Kd = higher affinity",
          },
          {
            id: 13,
            term: "Competitive antagonist",
            match:
              "Shifts dose-response curve right; ↑EC50; same Emax achievable with more drug",
          },
          {
            id: 14,
            term: "Non-competitive antagonist",
            match:
              "Reduces Emax; cannot be overcome by ↑agonist; allosteric or irreversible",
          },
        ],
        12: [
          {
            id: 1,
            term: "Pain (fast, sharp) A-delta fibers",
            match:
              "Myelinated; fast conduction; mechanical + thermal; sharp localized pain",
          },
          {
            id: 2,
            term: "Pain (slow, dull) C fibers",
            match:
              "Unmyelinated; slow; polymodal; burning + aching; wide dynamic range neurons",
          },
          {
            id: 3,
            term: "Referred pain mechanism",
            match:
              "Convergence of visceral + somatic afferents at same dorsal horn level",
          },
          {
            id: 4,
            term: "Gate control theory",
            match:
              "Large Aβ fibers activate interneurons → inhibit C-fiber pain signals at dorsal horn",
          },
          {
            id: 5,
            term: "Nociception (central sensitization)",
            match:
              "Wind-up; repeated C-fiber input → NMDA activation → hyperalgesia + allodynia",
          },
          {
            id: 6,
            term: "Endorphins/enkephalins",
            match:
              "Endogenous opioids; bind mu, delta, kappa receptors; PAG + dorsal horn",
          },
          {
            id: 7,
            term: "Proprioception pathway",
            match:
              "Dorsal column → nucleus gracilis/cuneatus → medial lemniscus → VPL thalamus → cortex",
          },
          {
            id: 8,
            term: "Pain/temp pathway",
            match:
              "Spinothalamic → decussates 1-2 levels above entry → VPL thalamus → S1 cortex",
          },
          {
            id: 9,
            term: "Motor cortex (homunculus)",
            match: "Leg medial (ACA); arm + face lateral (MCA); inverted map",
          },
          {
            id: 10,
            term: "Upper vs lower motor neuron lesion",
            match:
              "UMN: spasticity + hyperreflexia + Babinski; LMN: flaccidity + atrophy + fasciculations",
          },
          {
            id: 11,
            term: "Cerebellar lesion signs",
            match:
              "Ipsilateral: ataxia + intention tremor + dysdiadochokinesia + nystagmus",
          },
          {
            id: 12,
            term: "Basal ganglia circuit (direct pathway)",
            match:
              "Striatum → ↓GPi → ↓thalamic inhibition → ↑movement (dopamine facilitates)",
          },
          {
            id: 13,
            term: "Basal ganglia circuit (indirect pathway)",
            match:
              "Striatum → ↑GPe → ↓STN → ↑GPi → ↑thalamic inhibition → ↓movement",
          },
          {
            id: 14,
            term: "Parkinson's physiology",
            match:
              "↓dopamine in nigrostriatal pathway → ↑indirect / ↓direct → akinesia + rigidity + tremor",
          },
        ],
      },
    },
  };

  const handleThemeSelect = (themeId) => {
    setSelectedTheme(themeId);
    setGamePhase("level");
  };

  const loadSavedProgress = () => {
    if (typeof window === "undefined") return null;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  };

  const saveProgress = (nextProgress) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(nextProgress));
    } catch (error) {
      // Ignore storage failures
    }
  };

  useEffect(() => {
    const saved = loadSavedProgress();
    if (saved?.completedGames) {
      setCompletedGames(saved.completedGames);
    }
    setHasLoadedProgress(true);
  }, []);

  useEffect(() => {
    if (!hasLoadedProgress) return;
    saveProgress({ completedGames });
  }, [completedGames, hasLoadedProgress]);

  const getGameOrder = (themeId, levelId) => {
    const levelData = matchData[themeId]?.[levelId];
    if (Array.isArray(levelData)) {
      return [1];
    }
    if (levelData && typeof levelData === "object") {
      return Object.keys(levelData)
        .map((key) => Number(key))
        .sort((a, b) => a - b);
    }
    return [];
  };

  const getLevelProgress = (themeId, levelId) => {
    const levelData = matchData[themeId]?.[levelId];
    const totalGames =
      levelData && typeof levelData === "object"
        ? Object.keys(levelData).length
        : 0;
    const completedCount = completedGames?.[themeId]?.[levelId]
      ? Object.keys(completedGames[themeId][levelId]).length
      : 0;
    return { completedCount, totalGames };
  };

  const handleLevelSelect = (levelId) => {
    setSelectedLevel(levelId);
    const order = getGameOrder(selectedTheme, levelId);

    // Resume from the first uncompleted game; if all done, restart from game 1
    const completedForLevel = completedGames?.[selectedTheme]?.[levelId] || {};
    const firstUncompletedIndex = order.findIndex(
      (gameId) => !completedForLevel[gameId],
    );
    const startIndex = firstUncompletedIndex >= 0 ? firstUncompletedIndex : 0;
    const firstGameId = order[startIndex] || 1;

    setGameOrder(order);
    setSelectedGame(firstGameId);
    initializeGame(selectedTheme, levelId, firstGameId);
  };

  const initializeGame = (themeId, levelId, gameId) => {
    const level = levels.find((l) => l.id === levelId);
    const levelData = matchData[themeId]?.[levelId];
    let pairsSource = [];

    if (Array.isArray(levelData)) {
      pairsSource = levelData;
    } else if (levelData && typeof levelData === "object") {
      const fallbackOrder = Object.keys(levelData)
        .map((key) => Number(key))
        .sort((a, b) => a - b);
      const selectedGameId = gameId || fallbackOrder[0];
      pairsSource = levelData[selectedGameId] || [];
      setSelectedGame(selectedGameId || null);
    }

    const pairs = pairsSource.slice(0, level.pairs);

    // Create cards array with both terms and matches
    const cardData = [];
    pairs.forEach((pair) => {
      cardData.push({
        id: `${pair.id}-term`,
        pairId: pair.id,
        content: pair.term,
        type: "term",
      });
      cardData.push({
        id: `${pair.id}-match`,
        pairId: pair.id,
        content: pair.match,
        type: "match",
      });
    });

    // Shuffle cards
    const shuffled = cardData.sort(() => Math.random() - 0.5);

    setCards(shuffled);
    setFlippedCards([]);
    setMatchedPairs([]);
    setMoves(0);
    setStartTime(Date.now());
    setEndTime(null);
    setGamePhase("playing");
  };

  const handleCardClick = (cardId) => {
    // Don't allow clicking if 2 cards are already flipped or card is already matched
    if (flippedCards.length === 2) return;
    if (matchedPairs.includes(cards.find((c) => c.id === cardId).pairId))
      return;
    if (flippedCards.includes(cardId)) return;

    const newFlipped = [...flippedCards, cardId];
    setFlippedCards(newFlipped);

    // Check for match when 2 cards are flipped
    if (newFlipped.length === 2) {
      setMoves(moves + 1);

      const card1 = cards.find((c) => c.id === newFlipped[0]);
      const card2 = cards.find((c) => c.id === newFlipped[1]);

      if (card1.pairId === card2.pairId) {
        // Match found!
        setTimeout(() => {
          setMatchedPairs([...matchedPairs, card1.pairId]);
          setFlippedCards([]);

          // Check if game is complete
          const level = levels.find((l) => l.id === selectedLevel);
          if (matchedPairs.length + 1 === level.pairs) {
            if (selectedTheme && selectedLevel && selectedGame) {
              setCompletedGames((prev) => {
                const updated = { ...prev };
                if (!updated[selectedTheme]) updated[selectedTheme] = {};
                if (!updated[selectedTheme][selectedLevel]) {
                  updated[selectedTheme][selectedLevel] = {};
                }
                updated[selectedTheme][selectedLevel][selectedGame] = true;
                return updated;
              });
            }
            setEndTime(Date.now());
            setGamePhase("complete");

            // Award coins and XP based on level
            const newUserData = {
              ...userData,
              coins: userData.coins + level.coins,
              xp: userData.xp + level.xp,
              avatarStats: applyBoost(userData.avatarStats, GAME_BOOST),
            };
            saveUserData(newUserData);
          }
        }, 500);
      } else {
        // Not a match - flip back after delay
        setTimeout(() => {
          setFlippedCards([]);
        }, 1000);
      }
    }
  };

  const handleRestart = () => {
    initializeGame(selectedTheme, selectedLevel, selectedGame);
  };

  const handleNextGame = () => {
    if (gameOrder.length === 0) {
      initializeGame(selectedTheme, selectedLevel, selectedGame);
      return;
    }

    // Check if all games in this level are now complete
    const completedForLevel =
      completedGames?.[selectedTheme]?.[selectedLevel] || {};
    const allDone = gameOrder.every((id) => completedForLevel[id]);
    if (allDone) {
      handleBackToLevels();
      return;
    }

    // Advance to the next uncompleted game
    const nextUncompletedIndex = gameOrder.findIndex(
      (id) => !completedForLevel[id],
    );
    const nextGameId = gameOrder[nextUncompletedIndex];
    setSelectedGame(nextGameId);
    initializeGame(selectedTheme, selectedLevel, nextGameId);
  };

  const handleBackToLevels = () => {
    setGamePhase("level");
    setSelectedLevel(null);
    setSelectedGame(null);
    setGameOrder([]);
    setCards([]);
    setFlippedCards([]);
    setMatchedPairs([]);
    setMoves(0);
    setStartTime(null);
    setEndTime(null);
  };

  const handleBackToThemes = () => {
    setGamePhase("theme");
    setSelectedTheme(null);
    setSelectedLevel(null);
    setSelectedGame(null);
    setGameOrder([]);
    setCards([]);
    setFlippedCards([]);
    setMatchedPairs([]);
    setMoves(0);
    setStartTime(null);
    setEndTime(null);
  };

  const handleResetProgress = () => {
    if (typeof window !== "undefined") {
      const confirmed = window.confirm("Reset all Medical Match progress?");
      if (!confirmed) return;
    }
    setCompletedGames({});
    saveProgress({ completedGames: {} });
  };

  // Theme Selection Screen
  if (gamePhase === "theme") {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-cardgame.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-teal-900/80 to-cyan-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <button
              onClick={onBack}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Games
            </button>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Medical Match Game
              </h1>
              <p className="text-gray-600">
                Test your memory and medical knowledge by matching pairs
              </p>
            </div>

            <h2 className="text-2xl font-bold text-white mb-6">Select Theme</h2>
            <button
              onClick={handleResetProgress}
              className="mb-6 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Reset progress
            </button>

            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
              {themes.map((theme) => {
                const ThemeIcon = theme.icon;
                return (
                  <button
                    key={theme.id}
                    onClick={() => handleThemeSelect(theme.id)}
                    className={
                      "rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br " +
                      theme.color +
                      " " +
                      theme.borderColor
                    }
                  >
                    <div className="flex justify-center mb-4">
                      <div className="bg-white bg-opacity-80 rounded-full p-4 shadow-md">
                        <ThemeIcon className={"w-8 h-8 " + theme.iconColor} />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
                      {theme.name}
                    </h3>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      {theme.description}
                    </p>
                    <div className="flex items-center justify-center gap-4 text-sm">
                      <span className="font-semibold text-teal-600">
                        {theme.coins} coins
                      </span>
                      <span className="font-semibold text-purple-600">
                        {theme.xp} XP
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Level Selection Screen
  if (gamePhase === "level") {
    const theme = themes.find((t) => t.id === selectedTheme);
    const ThemeIcon = theme.icon;

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-cardgame.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-teal-900/80 to-cyan-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <button
              onClick={handleBackToThemes}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Themes
            </button>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-6">
              <div className="flex items-center gap-4">
                <div
                  className={
                    "bg-gradient-to-br rounded-full p-4 " + theme.color
                  }
                >
                  <ThemeIcon className={"w-8 h-8 " + theme.iconColor} />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-800">
                    {theme.name}
                  </h1>
                  <p className="text-gray-600">{theme.description}</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white mb-6">Select Level</h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {levels.map((level) => {
                const { completedCount, totalGames } = getLevelProgress(
                  selectedTheme,
                  level.id,
                );
                const progressPercent =
                  totalGames > 0 ? (completedCount / totalGames) * 100 : 0;
                return (
                  <button
                    key={level.id}
                    onClick={() => handleLevelSelect(level.id)}
                    className={
                      "rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br " +
                      level.color +
                      " " +
                      level.borderColor
                    }
                  >
                    <div className="flex justify-center mb-4">
                      <div className="bg-white bg-opacity-80 rounded-full w-16 h-16 flex items-center justify-center shadow-md">
                        <span className="text-2xl font-bold text-gray-800">
                          {level.id}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
                      {level.name}
                    </h3>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      {level.pairs} pairs to match
                    </p>
                    <div className="flex items-center justify-center gap-4 text-sm">
                      <span className="font-semibold text-teal-600">
                        {level.coins} coins
                      </span>
                      <span className="font-semibold text-purple-600">
                        {level.xp} XP
                      </span>
                    </div>
                    <div className="mt-4">
                      <div className="h-2 bg-white/70 rounded-full">
                        <div
                          className="h-full bg-teal-600 rounded-full transition-all duration-300"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <div className="mt-1 text-xs text-gray-600 text-center">
                        {completedCount} / {totalGames} games completed
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Game Complete Screen
  if (gamePhase === "complete") {
    const theme = themes.find((t) => t.id === selectedTheme);
    const level = levels.find((l) => l.id === selectedLevel);
    const timeInSeconds = Math.floor((endTime - startTime) / 1000);
    const completedForLevel =
      completedGames?.[selectedTheme]?.[selectedLevel] || {};
    const { totalGames } = getLevelProgress(selectedTheme, selectedLevel);
    const isLevelComplete = Object.keys(completedForLevel).length >= totalGames;
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-cardgame.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-teal-900/80 to-cyan-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <button
              onClick={onBack}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Games
            </button>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center">
              <Award className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {isLevelComplete ? "Level Complete! 🏆" : "Congratulations!"}
              </h2>
              <p className="text-gray-600 mb-6">
                {isLevelComplete
                  ? `You've finished all games in ${level.name}!`
                  : `You completed ${theme.name} - ${level.name}!`}
              </p>

              <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-3xl font-bold text-teal-600">
                      {moves}
                    </div>
                    <div className="text-gray-700 text-sm">Moves</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-purple-600">
                      {minutes}:{seconds.toString().padStart(2, "0")}
                    </div>
                    <div className="text-gray-700 text-sm">Time</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-orange-600">
                      {level.pairs}
                    </div>
                    <div className="text-gray-700 text-sm">Pairs</div>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  You earned {level.coins} coins and {level.xp} XP!
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleRestart}
                  className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-5 h-5" />
                  Replay Same Game
                </button>
                <button
                  onClick={handleNextGame}
                  className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                >
                  {isLevelComplete ? "Back to Levels" : "Next Game"}
                </button>
                <button
                  onClick={handleBackToLevels}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Try Different Level
                </button>
                <button
                  onClick={handleBackToThemes}
                  className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Try Different Theme
                </button>
                <button
                  onClick={onBack}
                  className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Back to Games
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Playing Phase
  const theme = themes.find((t) => t.id === selectedTheme);
  const level = levels.find((l) => l.id === selectedLevel);

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: "url('/avatar-cardgame.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="min-h-screen bg-gradient-to-br from-teal-900/80 to-cyan-900/80">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <button
            onClick={handleBackToLevels}
            className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
          >
            ← Change Level
          </button>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {theme.name} - {level.name}
                </h1>
                <p className="text-sm text-gray-600">{theme.description}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Moves</div>
                <div className="text-3xl font-bold text-teal-600">{moves}</div>
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600">Progress</span>
                <span className="font-semibold text-teal-600">
                  {matchedPairs.length}/{level.pairs} pairs
                </span>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-teal-400 to-teal-600 transition-all duration-500"
                  style={{
                    width: `${(matchedPairs.length / level.pairs) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            {cards.map((card) => {
              const isFlipped = flippedCards.includes(card.id);
              const isMatched = matchedPairs.includes(card.pairId);

              return (
                <button
                  key={card.id}
                  onClick={() => handleCardClick(card.id)}
                  disabled={isMatched}
                  className={
                    "relative h-32 rounded-xl border-2 transition-all transform hover:scale-105 " +
                    (isMatched
                      ? "bg-green-100 border-green-400 opacity-60"
                      : isFlipped
                        ? "bg-white border-teal-400"
                        : "bg-gradient-to-br from-gray-100 to-gray-200 border-gray-300 hover:border-gray-400")
                  }
                >
                  <div className="absolute inset-0 flex items-center justify-center p-3">
                    {isFlipped || isMatched ? (
                      <div className="text-center">
                        <div className="font-semibold text-gray-800 text-sm leading-tight">
                          {card.content}
                        </div>
                        {isMatched && (
                          <Check className="w-5 h-5 text-green-600 mx-auto mt-2" />
                        )}
                      </div>
                    ) : (
                      <div className="text-4xl text-gray-400">?</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-4">
            <h3 className="text-sm font-bold text-gray-800 mb-2">
              How to Play
            </h3>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>• Click on cards to flip them and reveal their content</li>
              <li>• Find matching pairs (e.g., organ with its function)</li>
              <li>• Complete all pairs to win coins and XP!</li>
              <li>• Try to finish in as few moves as possible</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalMatch;
